import { supabaseStorageCompat as supabase } from "./fileStorage";

import { db } from "./dbClient";

// ─── KYCOnboardingPortal.jsx ──────────────────────────────────────────────────
// Public-facing KYC onboarding portal.
// Share a link like: https://yourapp.com?kyc=TOKEN
//
// Two exports:
//   default KYCOnboardingPortal  — the client-facing form (public link)
//   KYCReviewDashboard           — your team's review interface (import separately)
//
// Storage: submissions saved to Supabase `kyc_submissions` table.
// Requires: setup_kyc.sql to be run first.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useAuditLog } from "./useAuditLog";

// ─── VERCEL PROTECTION BYPASS ─────────────────────────────────────────────────
const VERCEL_BYPASS_SECRET = import.meta.env.VITE_VERCEL_BYPASS_SECRET || import.meta.env.VERCEL_BYPASS_SECRET;

function getApiHeaders(baseHeaders = {}) {
  const headers = { ...baseHeaders };
  if (VERCEL_BYPASS_SECRET) {
    headers['x-vercel-protection-bypass'] = VERCEL_BYPASS_SECRET;
  }
  return headers;
}

// ─── DILISENSE SANCTIONS SCREENING (via server proxy — E-037 / F-024) ────────
const PROXY_BASE = import.meta.env.VITE_PROXY_URL || "http://localhost:3001";
async function dilisenseCheck(name, type) {
  // API key is managed server-side
  const endpoint = (type === "Business" || type === "business") ? "entity" : "individual";
  const resp = await fetch(`${PROXY_BASE}/api/dilisense/${endpoint}?names=${encodeURIComponent(name)}&fuzzy_search=1`, {
    headers: getApiHeaders({ "Content-Type": "application/json" })
  });
  if (resp.status === 401) throw new Error("Dilisense API key not configured on server");
  if (!resp.ok) throw new Error(`Dilisense error: ${resp.status}`);
  return resp.json();
}
function parseSanctionsResults(data) {
  // Handle null/undefined responses from API
  if (!data || typeof data !== 'object') {
    return {
      totalHits: 0,
      sanctions: [],
      pep: [],
      criminal: [],
      allRecords: [],
      sanctionsStatus: "pending",
      watchlistStatus: "pending",
      criminalStatus: "pending",
      error: "No data returned from sanctions check"
    };
  }

  const records = data.found_records || [];
  const sanctions = records.filter(r => r.source_type === "SANCTION");
  const pep       = records.filter(r => r.source_type === "PEP");
  const criminal  = records.filter(r => r.source_type === "CRIMINAL");
  return {
    totalHits: data.total_hits || 0,
    sanctions,
    pep,
    criminal,
    allRecords: records,
    sanctionsStatus: sanctions.length > 0 ? "failed" : "passed",
    watchlistStatus: pep.length > 0 ? "failed" : "passed",
    criminalStatus: criminal.length > 0 ? "failed" : "passed"
  };
}

// ─── Design tokens (matches CrossRegGuard) ────────────────────────────────────
import { C, useTheme } from "./theme";
import ExternalResearch from "./ExternalResearch";

// ─── Utility: base64 file encode ──────────────────────────────────────────────
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload  = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// ─── Utility: compress image for upload ────────────────────────────────────────
function compressImage(dataUrl, maxWidth = 1024, maxHeight = 1024, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// ─── Utility: safely parse JSONB arrays from database ─────────────────────────
function ensureArray(value) {
  if (!value) return [];
  if (typeof value === "string") {
    try { return JSON.parse(value); } catch { return []; }
  }
  if (Array.isArray(value)) return value;
  return [];
}

// ─── Shared UI ────────────────────────────────────────────────────────────────
function Input({ label, value, onChange, placeholder, type = "text", required, hint }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.12em", fontFamily: "'DM Mono', monospace" }}>
        {label.toUpperCase()}{required && <span style={{ color: C.danger, marginLeft: 3 }}>*</span>}
      </label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ background: C.bg, border: `1px solid ${focused ? C.accent : C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: "10px 13px", fontFamily: "'DM Mono', monospace", outline: "none", width: "100%", boxSizing: "border-box", transition: "border-color 0.15s" }} />
      {hint && <div style={{ color: C.textDim, fontSize: 11 }}>{hint}</div>}
    </div>
  );
}

function Select({ label, value, onChange, options, required }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.12em", fontFamily: "'DM Mono', monospace" }}>
        {label.toUpperCase()}{required && <span style={{ color: C.danger, marginLeft: 3 }}>*</span>}
      </label>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: value ? C.text : C.textDim, fontSize: 13, padding: "10px 13px", fontFamily: "'DM Mono', monospace", outline: "none", width: "100%" }}>
        <option value="">Select…</option>
        {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
    </div>
  );
}

function RadioGroup({ label, value, onChange, options }) {
  return (
    <div>
      <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.12em", fontFamily: "'DM Mono', monospace", marginBottom: 8 }}>{label.toUpperCase()}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {options.map(o => (
          <button key={o.value} onClick={() => onChange(o.value)} style={{
            padding: "8px 16px", borderRadius: 7, cursor: "pointer", fontSize: 12,
            background: value === o.value ? C.accentGlow : C.bg,
            border: `1px solid ${value === o.value ? C.accent : C.border}`,
            color: value === o.value ? C.accent : C.textMid,
            fontFamily: "'DM Mono', monospace", transition: "all 0.13s",
          }}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function StepIndicator({ steps, current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 36 }}>
      {steps.map((s, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : 0 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, flexShrink: 0 }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%",
              background: i < current ? C.success : i === current ? C.accent : C.border,
              border: `2px solid ${i < current ? C.success : i === current ? C.accent : C.borderLight}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700, fontFamily: "'DM Mono', monospace",
              color: i <= current ? C.bg : C.textDim,
              transition: "all 0.3s",
              boxShadow: i === current ? `0 0 16px ${C.accent}50` : "none",
            }}>
              {i < current ? "✓" : i + 1}
            </div>
            <div style={{ color: i === current ? C.accent : i < current ? C.success : C.textDim, fontSize: 9, letterSpacing: "0.1em", fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>
              {s}
            </div>
          </div>
          {i < steps.length - 1 && (
            <div style={{ flex: 1, height: 1, background: i < current ? C.success : C.border, margin: "0 6px", marginBottom: 18, transition: "background 0.3s" }} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── STEP 1: Applicant type ────────────────────────────────────────────────────
function StepType({ data, onChange, onNext }) {
  const { t } = useTranslation();
  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>{t('onboarding.step1.title')}</h2>
        <p style={{ color: C.textMid, fontSize: 13, margin: 0 }}>{t('onboarding.step1.subtitle')}</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 28 }}>
        {[
          { value: "individual", icon: "👤", title: t('onboarding.step1.individual'), desc: t('onboarding.step1.individualDesc') },
          { value: "business",   icon: "🏢", title: t('onboarding.step1.business'),   desc: t('onboarding.step1.businessDesc') },
        ].map(opt => (
          <button key={opt.value} onClick={() => onChange("type", opt.value)} style={{
            padding: "24px 20px", borderRadius: 12, cursor: "pointer", textAlign: "left",
            background: data.type === opt.value ? `${C.accent}0A` : C.surfaceAlt,
            border: `1.5px solid ${data.type === opt.value ? C.accent : C.border}`,
            transition: "all 0.2s",
          }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>{opt.icon}</div>
            <div style={{ color: data.type === opt.value ? C.accent : C.text, fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{opt.title}</div>
            <div style={{ color: C.textDim, fontSize: 12, lineHeight: 1.5 }}>{opt.desc}</div>
          </button>
        ))}
      </div>
      <button onClick={onNext} disabled={!data.type} style={{ width: "100%", padding: "13px", background: data.type ? C.accent : C.border, border: "none", borderRadius: 9, color: C.bg, fontWeight: 700, cursor: data.type ? "pointer" : "not-allowed", fontFamily: "'DM Mono', monospace", fontSize: 14 }}>
        {t('onboarding.continue')}
      </button>
    </div>
  );
}

// ─── STEP 2: Personal info ─────────────────────────────────────────────────────
function StepPersonal({ data, onChange, onNext, onBack, hideNav = false }) {
  const { t } = useTranslation();
  const isInd = data.type === "individual";
  const canNext = data.firstName && data.lastName && data.dob && data.nationality && data.email && data.phone && data.taxId && data.jobTitle && data.employerName && data.purposeOfAccount && data.addressLine1 && data.city && data.postCode && data.country;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>{t('onboarding.step2.title')}</h2>
        <p style={{ color: C.textMid, fontSize: 13, margin: 0 }}>{t('onboarding.step2.subtitle')}</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label={t('onboarding.step2.firstName')} value={data.firstName || ""} onChange={v => onChange("firstName", v)} placeholder="Jane" required />
          <Input label={t('onboarding.step2.lastName')}  value={data.lastName  || ""} onChange={v => onChange("lastName",  v)} placeholder="Smith" required />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label={t('onboarding.step2.dob')} value={data.dob || ""} onChange={v => onChange("dob", v)} type="date" required />
          <Input label={t('onboarding.step2.nationality')} value={data.nationality || ""} onChange={v => onChange("nationality", v)} placeholder={t('onboarding.step2.nationalityPlaceholder')} required />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label={t('onboarding.step2.email')} value={data.email || ""} onChange={v => onChange("email", v)} type="email" placeholder="jane@example.com" required />
          <Input label={t('onboarding.step2.phone')} value={data.phone || ""} onChange={v => onChange("phone", v)} placeholder="+44 7700 900000" required />
        </div>
        <Input label={t('onboarding.step2.taxId')} value={data.taxId || ""} onChange={v => onChange("taxId", v)} placeholder="e.g. UTR 1234567890" hint={t('onboarding.step2.taxIdHint')} required />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label={t('onboarding.step2.jobTitle')} value={data.jobTitle || ""} onChange={v => onChange("jobTitle", v)} placeholder={t('onboarding.step2.jobTitlePlaceholder')} required />
          <Input label={t('onboarding.step2.employerName')} value={data.employerName || ""} onChange={v => onChange("employerName", v)} placeholder={t('onboarding.step2.employerNamePlaceholder')} required />
        </div>
        <Select label={t('onboarding.step2.purposeOfAccount')} value={data.purposeOfAccount || ""} onChange={v => onChange("purposeOfAccount", v)} required
          options={["Personal Savings / Banking", "Business Transactions", "Investment & Trading", "Cryptocurrency", "International Transfers", "Payroll / Salary Receipt", "Property Purchase", "Other"]} />
        <Input label={t('onboarding.step2.walletId')} value={data.walletId || ""} onChange={v => onChange("walletId", v)} placeholder={t('onboarding.step2.walletIdPlaceholder')} hint={t('onboarding.step2.walletIdHint')} />

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, marginTop: 4 }}>
          <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 12, fontFamily: "'Syne', sans-serif" }}>{t('onboarding.step2.residentialAddress')}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Input label={t('onboarding.step2.addressLine1')} value={data.addressLine1 || ""} onChange={v => onChange("addressLine1", v)} placeholder="123 High Street" required />
            <Input label={t('onboarding.step2.addressLine2')} value={data.addressLine2 || ""} onChange={v => onChange("addressLine2", v)} placeholder={t('onboarding.step2.addressLine2Placeholder')} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Input label={t('onboarding.step2.city')}     value={data.city     || ""} onChange={v => onChange("city",     v)} placeholder="London"  required />
              <Input label={t('onboarding.step2.postCode')} value={data.postCode || ""} onChange={v => onChange("postCode", v)} placeholder="SW1A 1AA" required />
              <Input label={t('onboarding.step2.country')}  value={data.country  || ""} onChange={v => onChange("country",  v)} placeholder="UK"       required />
            </div>
          </div>
        </div>
      </div>
      {!hideNav && (
      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <button onClick={onBack} style={{ flex: 1, padding: "12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 9, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t('onboarding.back')}</button>
        <button onClick={onNext} disabled={!canNext} style={{ flex: 3, padding: "12px", background: canNext ? C.accent : C.border, border: "none", borderRadius: 9, color: C.bg, fontWeight: 700, cursor: canNext ? "pointer" : "not-allowed", fontFamily: "'DM Mono', monospace", fontSize: 14 }}>{t('onboarding.continue')}</button>
      </div>
      )}
    </div>
  );
}

// ─── STEP 3: Business info (only for business type) ───────────────────────────
function StepBusiness({ data, onChange, onNext, onBack }) {
  const { t } = useTranslation();

  // Item 8 — Beneficial Ownership Attestation state
  const [boAttested,    setBoAttested]    = useState(data.boAttested || false);
  const [boSigName,     setBoSigName]     = useState(data.boAttestedBy || "");
  const [boTitle,       setBoTitle]       = useState(data.boAttestedTitle || "");
  const today = new Date().toISOString().slice(0, 10);

  // Sync attestation fields back into parent data whenever they change
  function handleAttestation(attested, sigName, title) {
    onChange("boAttested",      attested);
    onChange("boAttestedBy",    sigName);
    onChange("boAttestedTitle", title);
    onChange("boAttestedAt",    attested ? new Date().toISOString() : null);
  }

  const boAttestationValid = boAttested && boSigName.trim().length > 0;

  const canNext = data.businessName && data.regNumber && data.businessType && data.incorporationCountry && boAttestationValid;

  const [uboName,       setUboName]       = useState("");
  const [uboRole,       setUboRole]       = useState("Director");
  const [uboOwnership,  setUboOwnership]  = useState("");
  const [uboError,      setUboError]      = useState("");

  const ubos = data.ubos || [];
  const totalOwnership = ubos.reduce((sum, u) => sum + (parseFloat(u.ownership) || 0), 0);

  function addUbo() {
    setUboError("");
    if (!uboName.trim()) { setUboError("Name is required."); return; }
    const pct = parseFloat(uboOwnership);
    if (uboOwnership && (isNaN(pct) || pct < 0 || pct > 100)) { setUboError("Ownership must be 0–100%."); return; }
    if (uboOwnership && totalOwnership + pct > 100) { setUboError(`Total ownership cannot exceed 100%. Currently ${totalOwnership}% allocated.`); return; }
    onChange("ubos", [...ubos, { name: uboName.trim(), role: uboRole, ownership: uboOwnership || "" }]);
    // also keep legacy directors field in sync
    onChange("directors", [...ubos.map(u => u.name), uboName.trim()]);
    setUboName(""); setUboOwnership(""); setUboRole("Director");
  }

  function removeUbo(i) {
    const updated = ubos.filter((_, idx) => idx !== i);
    onChange("ubos", updated);
    onChange("directors", updated.map(u => u.name));
  }

  const remainingPct = 100 - totalOwnership;
  const ownershipBarColor = totalOwnership > 100 ? C.danger : totalOwnership === 100 ? C.success : C.accent;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>{t('onboarding.step3.title')}</h2>
        <p style={{ color: C.textMid, fontSize: 13, margin: 0 }}>{t('onboarding.step3.subtitle')}</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Input label={t('onboarding.step3.legalName')}   value={data.businessName  || ""} onChange={v => onChange("businessName",  v)} placeholder="Acme Corp Ltd" required />
        <Input label={t('onboarding.step3.tradingName')} value={data.tradingName   || ""} onChange={v => onChange("tradingName",   v)} placeholder="Acme Corp" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label={t('onboarding.step3.regNumber')}  value={data.regNumber    || ""} onChange={v => onChange("regNumber",    v)} placeholder="12345678" required />
          <Input label={t('onboarding.step3.taxId')}      value={data.businessTaxId|| ""} onChange={v => onChange("businessTaxId",v)} placeholder="GB123456789" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Select label={t('onboarding.step3.businessType')} value={data.businessType || ""} onChange={v => onChange("businessType", v)} required
            options={["Limited Company", "Partnership", "Sole Trader", "LLC", "Trust", "Foundation", "Other"]} />
          <Input label={t('onboarding.step3.incorporationCountry')} value={data.incorporationCountry || ""} onChange={v => onChange("incorporationCountry", v)} placeholder="United Kingdom" required />
        </div>
        <Input label={t('onboarding.step3.registeredAddress')} value={data.registeredAddress || ""} onChange={v => onChange("registeredAddress", v)} placeholder="123 Business Park, London, EC1A 1BB" />
        <Input label={t('onboarding.step3.website')}           value={data.website           || ""} onChange={v => onChange("website",           v)} placeholder="https://acmecorp.com" />

        {/* ── UBO / Directors section ── */}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 18, marginTop: 4 }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 600, fontFamily: "'Syne', sans-serif", marginBottom: 4 }}>
              {t('onboarding.step3.directorsTitle')}
            </div>
            <div style={{ color: C.textDim, fontSize: 12, lineHeight: 1.6 }}>
              {t('onboarding.step3.directorsDesc')}
            </div>
          </div>

          {/* Ownership progress bar */}
          {ubos.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.1em" }}>OWNERSHIP ALLOCATED</span>
                <span style={{ color: ownershipBarColor, fontSize: 11, fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>
                  {totalOwnership.toFixed(1)}% / 100%
                  {totalOwnership < 100 && <span style={{ color: C.textDim, fontWeight: 400 }}> · {t('onboarding.step3.unallocated', { pct: remainingPct.toFixed(1) })}</span>}
                  {totalOwnership === 100 && <span style={{ color: C.success }}> · {t('onboarding.step3.fullyAccounted')}</span>}
                </span>
              </div>
              <div style={{ height: 6, background: C.border, borderRadius: 4, overflow: "hidden" }}>
                <div style={{ width: `${Math.min(totalOwnership, 100)}%`, height: "100%", background: ownershipBarColor, borderRadius: 4, transition: "width 0.3s, background 0.3s" }} />
              </div>
              {/* Per-person breakdown */}
              <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                {ubos.filter(u => u.ownership).map((u, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 5, padding: "3px 8px" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: `hsl(${(i * 67 + 180) % 360}, 60%, 55%)`, flexShrink: 0 }} />
                    <span style={{ color: C.textMid, fontSize: 10 }}>{u.name.split(" ")[0]}</span>
                    <span style={{ color: C.accent, fontSize: 10, fontWeight: 700 }}>{u.ownership}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add row */}
          <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
            <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 10 }}>{t('onboarding.step3.addPerson').toUpperCase()}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, marginBottom: 8 }}>
              <input
                value={uboName} onChange={e => setUboName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addUbo()}
                placeholder={t('onboarding.step3.fullLegalName')}
                style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "9px 12px", fontFamily: "'DM Mono', monospace", outline: "none" }}
              />
              <select value={uboRole} onChange={e => setUboRole(e.target.value)}
                style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 12, padding: "9px 10px", fontFamily: "'DM Mono', monospace", outline: "none", cursor: "pointer" }}>
                {["Director", "UBO", "Director & UBO", "Secretary", "Trustee", "Nominee"].map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <div style={{ display: "flex", alignItems: "center", gap: 6, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "0 10px" }}>
                <input
                  value={uboOwnership} onChange={e => setUboOwnership(e.target.value.replace(/[^0-9.]/g, ""))}
                  onKeyDown={e => e.key === "Enter" && addUbo()}
                  placeholder="0"
                  style={{ background: "transparent", border: "none", color: C.text, fontSize: 13, width: 40, fontFamily: "'DM Mono', monospace", outline: "none", textAlign: "right" }}
                />
                <span style={{ color: C.textDim, fontSize: 12 }}>%</span>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {uboError
                ? <span style={{ color: C.danger, fontSize: 11 }}>⚠ {uboError}</span>
                : <span style={{ color: C.textDim, fontSize: 11 }}>{t('onboarding.step3.ownershipHint')}</span>
              }
              <button onClick={addUbo} style={{ padding: "8px 18px", background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 7, color: C.accent, cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>
                + {t('onboarding.add')}
              </button>
            </div>
          </div>

          {/* UBO table */}
          {ubos.length > 0 && (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
                    {[t('onboarding.step3.colName'), t('onboarding.step3.colRole'), t('onboarding.step3.colOwnership'), ""].map(h => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: C.textDim, fontSize: 10, letterSpacing: "0.1em", fontFamily: "'DM Mono', monospace" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ubos.map((u, i) => (
                    <tr key={i} style={{ borderBottom: i < ubos.length - 1 ? `1px solid ${C.border}` : "none" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 28, height: 28, borderRadius: "50%", background: `hsl(${(i * 67 + 180) % 360}, 50%, 20%)`, border: `1px solid hsl(${(i * 67 + 180) % 360}, 50%, 40%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: `hsl(${(i * 67 + 180) % 360}, 70%, 70%)`, fontWeight: 700, flexShrink: 0 }}>
                            {u.name.charAt(0)}
                          </div>
                          <span style={{ color: C.text, fontSize: 13 }}>{u.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ color: u.role.includes("UBO") ? C.gold : C.textMid, fontSize: 11, background: u.role.includes("UBO") ? `${C.gold}15` : C.surfaceAlt, border: `1px solid ${u.role.includes("UBO") ? C.gold + "40" : C.border}`, padding: "2px 8px", borderRadius: 4 }}>
                          {u.role}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        {u.ownership
                          ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 60, height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                                <div style={{ width: `${Math.min(parseFloat(u.ownership), 100)}%`, height: "100%", background: parseFloat(u.ownership) >= 25 ? C.gold : C.accent, borderRadius: 2 }} />
                              </div>
                              <span style={{ color: parseFloat(u.ownership) >= 25 ? C.gold : C.text, fontSize: 12, fontWeight: parseFloat(u.ownership) >= 25 ? 700 : 400, fontFamily: "'DM Mono', monospace" }}>
                                {u.ownership}%
                                {parseFloat(u.ownership) >= 25 && <span style={{ color: C.gold, fontSize: 10, marginLeft: 4 }}>UBO threshold</span>}
                              </span>
                            </div>
                          )
                          : <span style={{ color: C.textDim, fontSize: 12 }}>{t('onboarding.step3.notSpecified')}</span>
                        }
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>
                        <button onClick={() => removeUbo(i)} style={{ background: "transparent", border: "none", color: C.danger, cursor: "pointer", fontSize: 14, padding: "2px 6px", opacity: 0.7 }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {ubos.length === 0 && (
            <div style={{ textAlign: "center", padding: "20px", color: C.textDim, fontSize: 12, background: C.surfaceAlt, borderRadius: 8, border: `1px dashed ${C.border}` }}>
              {t('onboarding.step3.noDirectors')}
            </div>
          )}
        </div>

        {/* ── Item 8: Beneficial Ownership Attestation ── */}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, marginTop: 8 }}>
          <div style={{ color: C.text, fontSize: 13, fontWeight: 600, fontFamily: "'Syne', sans-serif", marginBottom: 4 }}>
            Beneficial Ownership Attestation
          </div>
          <div style={{ color: C.textDim, fontSize: 12, marginBottom: 14, lineHeight: 1.6 }}>
            Before continuing, you must confirm that the beneficial ownership information above is accurate and complete.
          </div>

          {/* Checkbox */}
          <label style={{ display: "flex", gap: 12, cursor: "pointer", alignItems: "flex-start", background: boAttested ? `${C.success}08` : `${C.warn}08`, border: `1px solid ${boAttested ? C.success + "40" : C.warn + "30"}`, borderRadius: 9, padding: "14px 16px", marginBottom: 14 }}>
            <input
              type="checkbox"
              checked={boAttested}
              onChange={e => {
                const checked = e.target.checked;
                setBoAttested(checked);
                handleAttestation(checked, boSigName, boTitle);
              }}
              style={{ marginTop: 2, accentColor: C.accent, flexShrink: 0 }}
            />
            <span style={{ color: C.textMid, fontSize: 12, lineHeight: 1.7 }}>
              I confirm that the beneficial ownership information provided above is accurate and complete to the best of my knowledge.
              <span style={{ color: C.danger, marginLeft: 3 }}>*</span>
            </span>
          </label>

          {/* Signature fields */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.12em", fontFamily: "'DM Mono', monospace" }}>
                FULL NAME (DIGITAL SIGNATURE)<span style={{ color: C.danger, marginLeft: 3 }}>*</span>
              </label>
              <input
                type="text"
                value={boSigName}
                onChange={e => {
                  setBoSigName(e.target.value);
                  handleAttestation(boAttested, e.target.value, boTitle);
                }}
                placeholder={t('onboarding.step3.fullLegalName')}
                style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: "10px 13px", fontFamily: "'DM Mono', monospace", outline: "none", width: "100%", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.12em", fontFamily: "'DM Mono', monospace" }}>
                TITLE / ROLE
              </label>
              <input
                type="text"
                value={boTitle}
                onChange={e => {
                  setBoTitle(e.target.value);
                  handleAttestation(boAttested, boSigName, e.target.value);
                }}
                placeholder={t('onboarding.step3.rolePlaceholder')}
                style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: "10px 13px", fontFamily: "'DM Mono', monospace", outline: "none", width: "100%", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.12em", fontFamily: "'DM Mono', monospace" }}>
                DATE
              </label>
              <input
                type="date"
                value={today}
                readOnly
                style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textDim, fontSize: 13, padding: "10px 13px", fontFamily: "'DM Mono', monospace", outline: "none", width: "100%", boxSizing: "border-box", cursor: "not-allowed" }}
              />
            </div>
          </div>

          {/* Attested badge */}
          {boAttestationValid && (
            <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 20, background: `${C.success}18`, border: `1px solid ${C.success}`, color: C.success, fontSize: 11, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>
              ✓ Beneficial Ownership Attested — {boSigName}{boTitle ? ` (${boTitle})` : ""}
            </div>
          )}

          {!boAttestationValid && (
            <div style={{ marginTop: 10, color: C.warn, fontSize: 11 }}>
              ⚠ You must check the attestation checkbox and provide your full name to continue.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <button onClick={onBack} style={{ flex: 1, padding: "12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 9, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t('onboarding.back')}</button>
        <button onClick={onNext} disabled={!canNext} style={{ flex: 3, padding: "12px", background: canNext ? C.accent : C.border, border: "none", borderRadius: 9, color: C.bg, fontWeight: 700, cursor: canNext ? "pointer" : "not-allowed", fontFamily: "'DM Mono', monospace", fontSize: 14 }}>{t('onboarding.continue')}</button>
      </div>
    </div>
  );
}

// ─── STEP 4: Source of funds & PEP ────────────────────────────────────────────
function StepCompliance({ data, onChange, onNext, onBack }) {
  const { t } = useTranslation();
  const canNext = data.sourceOfFunds && data.pepDeclaration !== undefined && data.sanctionsDeclaration !== undefined;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>{t('onboarding.step4.title')}</h2>
        <p style={{ color: C.textMid, fontSize: 13, margin: 0 }}>{t('onboarding.step4.subtitle')}</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <Select label={t('onboarding.step4.sourceOfFunds')} value={data.sourceOfFunds || ""} onChange={v => onChange("sourceOfFunds", v)} required
          options={["Employment / Salary", "Business Income", "Investment Returns", "Inheritance / Gift", "Property Sale", "Savings", "Pension / Retirement", "Other"]} />

        {data.sourceOfFunds === "Other" && (
          <Input label={t('onboarding.step4.pleaseDescribe')} value={data.sourceOfFundsOther || ""} onChange={v => onChange("sourceOfFundsOther", v)} placeholder={t('onboarding.step4.describeSourcePlaceholder')} />
        )}

        <Input label={t('onboarding.step4.annualIncome')} value={data.annualIncome || ""} onChange={v => onChange("annualIncome", v)} placeholder="e.g. 100,000" type="text" />

        <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
          <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 6, fontFamily: "'Syne', sans-serif" }}>{t('onboarding.step4.pepTitle')}</div>
          <div style={{ color: C.textDim, fontSize: 12, marginBottom: 14, lineHeight: 1.6 }}>
            <strong>PEP (Politically Exposed Person):</strong> A PEP is an individual who holds or has held a prominent public position (e.g., government official, senior military officer, major political party figure, senior judicial officer, or executive of a major international organization). Family members and close associates of PEPs are also considered PEPs.
            <br/><br/>
            <em>If you are or were a PEP, you must declare it. Your application will require enhanced due diligence and may be subject to additional review or restrictions.</em>
          </div>
          <RadioGroup label={t('onboarding.step4.pepQuestion')} value={data.pepDeclaration}
            onChange={v => onChange("pepDeclaration", v)}
            options={[{ value: "no", label: t('onboarding.step4.pepNo') }, { value: "yes", label: t('onboarding.step4.pepYes') }, { value: "associate", label: t('onboarding.step4.pepAssociate') }]} />
          {(data.pepDeclaration === "yes" || data.pepDeclaration === "associate") && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
              <Input label={t('onboarding.step4.provideDetails')} value={data.pepDetails || ""} onChange={v => onChange("pepDetails", v)} placeholder={t('onboarding.step4.pepDetailsPlaceholder')} />
              {/* #7: PEP Family / Close Associate Screening fields */}
              {data.pepDeclaration === "associate" && (
                <>
                  <Input label={t('onboarding.step4.pepAssociateName')} value={data.pepAssociateName || ""} onChange={v => onChange("pepAssociateName", v)} placeholder={t('onboarding.step4.pepAssociateNamePlaceholder')} />
                  <Select label={t('onboarding.step4.pepRelationship')} value={data.pepRelationship || ""} onChange={v => onChange("pepRelationship", v)}
                    options={t('onboarding.step4.pepRelationshipOptions', { returnObjects: true })} />
                  <Input label={t('onboarding.step4.pepAssociatePosition')} value={data.pepPosition || ""} onChange={v => onChange("pepPosition", v)} placeholder={t('onboarding.step4.pepAssociatePositionPlaceholder')} />
                  <Input label={t('onboarding.step4.pepCountryOfStatus')} value={data.pepCountry || ""} onChange={v => onChange("pepCountry", v)} placeholder={t('onboarding.step4.pepCountryPlaceholder')} />
                </>
              )}
              {data.pepDeclaration === "yes" && (
                <>
                  <Input label={t('onboarding.step4.pepOwnPosition')} value={data.pepPosition || ""} onChange={v => onChange("pepPosition", v)} placeholder={t('onboarding.step4.pepOwnPositionPlaceholder')} />
                  <Input label={t('onboarding.step4.pepCountry')} value={data.pepCountry || ""} onChange={v => onChange("pepCountry", v)} placeholder={t('onboarding.step4.pepCountryPlaceholder')} />
                  <Input label={t('onboarding.step4.pepFamilyMembers')} value={data.pepFamilyMembers || ""} onChange={v => onChange("pepFamilyMembers", v)} placeholder={t('onboarding.step4.pepFamilyMembersPlaceholder')} />
                </>
              )}
              <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "10px 12px", color: "#EF4444", fontSize: 11 }}>
                {t('onboarding.step4.pepEddWarning')}
              </div>
            </div>
          )}
        </div>

        <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
          <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 6, fontFamily: "'Syne', sans-serif" }}>{t('onboarding.step4.sanctionsTitle')}</div>
          <div style={{ color: C.textDim, fontSize: 12, marginBottom: 14, lineHeight: 1.6 }}>
            <strong>Sanctions:</strong> You may be subject to sanctions if you are listed on government sanction lists (e.g., OFAC, EU, UN, HMT), involved in sanctions-related activities, or reside in/are a national of a sanctioned country. This includes being a target of economic sanctions, trade restrictions, or other government-imposed penalties.
            <br/><br/>
            <em>If you are subject to sanctions, you must declare it. Such applications cannot be processed and will be immediately rejected in accordance with regulatory requirements.</em>
          </div>
          <RadioGroup label="" value={data.sanctionsDeclaration}
            onChange={v => onChange("sanctionsDeclaration", v)}
            options={[{ value: "no", label: t('onboarding.step4.sanctionsNo') }, { value: "yes", label: t('onboarding.step4.sanctionsYes') }]} />
          {data.sanctionsDeclaration === true && (
            <div style={{ marginTop: 12, background: "rgba(239,68,68,0.12)", border: "2px solid #EF4444", borderRadius: 8, padding: "12px 14px", color: "#EF4444", fontSize: 11, fontWeight: 600 }}>
              ⚠️ <strong>AUTOMATIC REJECTION:</strong> Applications from individuals subject to sanctions cannot be processed and will be immediately rejected in accordance with regulatory requirements. Your application will not proceed further.
            </div>
          )}
        </div>

        <div style={{ background: `${C.warn}08`, border: `1px solid ${C.warn}25`, borderRadius: 8, padding: "12px 14px" }}>
          <div style={{ color: C.warn, fontSize: 11, lineHeight: 1.6 }}>
            {t('onboarding.step4.falseDeclarationWarning')}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <button onClick={onBack} style={{ flex: 1, padding: "12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 9, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t('onboarding.back')}</button>
        <button onClick={onNext} disabled={!canNext} style={{ flex: 3, padding: "12px", background: canNext ? C.accent : C.border, border: "none", borderRadius: 9, color: C.bg, fontWeight: 700, cursor: canNext ? "pointer" : "not-allowed", fontFamily: "'DM Mono', monospace", fontSize: 14 }}>{t('onboarding.continue')}</button>
      </div>
    </div>
  );
}

// ─── Shared DropZone component ────────────────────────────────────────────────
function DropZone({ value, onFile, onClear, label, hint, accept = "image/*,.pdf", required, previewHeight = 160 }) {
  const { t } = useTranslation();
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  async function handleFile(file) {
    if (!file) return;
    const allowed = ["image/jpeg","image/png","image/webp","application/pdf"];
    if (!allowed.includes(file.type)) { alert("Please upload a JPG, PNG, WebP or PDF."); return; }
    if (file.size > 15 * 1024 * 1024) { alert("File must be under 15MB."); return; }
    const b64 = await fileToBase64(file);
    onFile({ name: file.name, type: file.type, size: file.size, data: b64 });
  }

  const isPdf = value?.type === "application/pdf";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.12em", fontFamily: "'DM Mono', monospace" }}>
          {label.toUpperCase()}{required && <span style={{ color: C.danger, marginLeft: 3 }}>*</span>}
        </div>
      )}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => !value && inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? C.accent : value ? C.success : C.border}`,
          borderRadius: 10, overflow: "hidden", cursor: value ? "default" : "pointer",
          background: dragging ? C.accentGlow : value ? `${C.success}06` : C.surfaceAlt,
          transition: "all 0.15s", position: "relative",
        }}>
        <input ref={inputRef} type="file" accept={accept} style={{ display: "none" }}
          onChange={e => handleFile(e.target.files[0])} />

        {value ? (
          <div>
            {/* Image preview */}
            {!isPdf && (
              <img src={value.data} alt="Preview"
                style={{ width: "100%", maxHeight: previewHeight, objectFit: "cover", display: "block" }} />
            )}
            {/* PDF */}
            {isPdf && (
              <div style={{ padding: "24px", textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📄</div>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{value.name}</div>
              </div>
            )}
            {/* Footer bar */}
            <div style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", padding: "8px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: C.success, fontSize: 12, fontWeight: 700 }}>✓ {value.name}</div>
                <div style={{ color: C.textDim, fontSize: 10, marginTop: 1 }}>{(value.size / 1024).toFixed(0)} KB</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}
                  style={{ background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 5, color: C.accent, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
                  {t('onboarding.dropzone.replace')}
                </button>
                {onClear && (
                  <button onClick={e => { e.stopPropagation(); onClear(); }}
                    style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}40`, borderRadius: 5, color: C.danger, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
                    {t('onboarding.dropzone.remove')}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding: "28px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{dragging ? "📂" : "📁"}</div>
            <div style={{ color: dragging ? C.accent : C.textMid, fontSize: 13, fontWeight: 500 }}>
              {dragging ? t('onboarding.dropzone.dropToUpload') : t('onboarding.dropzone.clickOrDrag')}
            </div>
            {hint && <div style={{ color: C.textDim, fontSize: 11, marginTop: 4 }}>{hint}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── STEP 5: Document upload ───────────────────────────────────────────────────
const ADDITIONAL_DOC_TYPES = [
  { value: "proof_of_address",  label: "Proof of Address",         icon: "🏠", hint: "Utility bill, bank statement (dated within 3 months)" },
  { value: "bank_statement",    label: "Bank Statement",           icon: "🏦", hint: "Last 3 months of primary bank account" },
  { value: "tax_return",        label: "Tax Return",               icon: "📋", hint: "Most recent filed tax return" },
  { value: "company_reg",       label: "Company Registration",     icon: "🏢", hint: "Certificate of incorporation or equivalent" },
  { value: "articles",          label: "Articles of Association",  icon: "📜", hint: "Latest version of constitutional documents" },
  { value: "ownership_chart",   label: "Ownership Structure Chart",icon: "🗂️", hint: "Diagram showing shareholding structure" },
  { value: "source_of_funds",   label: "Source of Funds Evidence", icon: "💰", hint: "Supporting evidence (e.g. sale contract, inheritance docs)" },
  { value: "other",             label: "Other Document",           icon: "📎", hint: "Any other relevant supporting document" },
];

function StepDocuments({ data, onChange, onNext, onBack, isBusiness }) {
  const { t } = useTranslation();
  const canNext = data.idFront;
  const additionalDocs = data.additionalDocs || [];
  const [addingDoc, setAddingDoc] = useState(false);
  const [newDocType, setNewDocType] = useState("");
  const [newDocLabel, setNewDocLabel] = useState("");

  function addDocSlot() {
    if (!newDocType) return;
    const docDef = ADDITIONAL_DOC_TYPES.find(d => d.value === newDocType);
    const label = newDocType === "other" ? (newDocLabel || "Other Document") : docDef?.label || newDocType;
    onChange("additionalDocs", [...additionalDocs, { type: newDocType, label, file: null }]);
    setNewDocType(""); setNewDocLabel(""); setAddingDoc(false);
  }

  function updateDocFile(i, file) {
    const updated = additionalDocs.map((d, idx) => idx === i ? { ...d, file } : d);
    onChange("additionalDocs", updated);
  }

  function removeDoc(i) {
    onChange("additionalDocs", additionalDocs.filter((_, idx) => idx !== i));
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>{t('onboarding.step5.title')}</h2>
        <p style={{ color: C.textMid, fontSize: 13, margin: 0 }}>{t('onboarding.step5.subtitle')}</p>
      </div>

      {/* ── Primary ID ── */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, marginBottom: 16 }}>
        <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.12em", fontFamily: "'DM Mono', monospace", marginBottom: 14 }}>{t('onboarding.step5.primaryIdLabel')} <span style={{ color: C.danger }}>*</span></div>
        <div style={{ marginBottom: 14 }}>
          <Select label={t('onboarding.step5.docType')} value={data.idType || ""} onChange={v => onChange("idType", v)}
            options={["Passport", "National ID Card", "Driver's Licence", "Residence Permit"]} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <DropZone
            value={data.idFront}
            onFile={f => onChange("idFront", f)}
            onClear={() => onChange("idFront", null)}
            label={t('onboarding.step5.frontOfDoc')}
            hint={t('onboarding.step5.fileHint')}
            required
            previewHeight={200}
          />
          <DropZone
            value={data.idBack}
            onFile={f => onChange("idBack", f)}
            onClear={() => onChange("idBack", null)}
            label={t('onboarding.step5.backOfDoc')}
            hint={t('onboarding.step5.backHint')}
            previewHeight={200}
          />
        </div>
        <div style={{ marginTop: 12, color: C.textDim, fontSize: 11, lineHeight: 1.8 }}>
          {t('onboarding.step5.idTips')}
        </div>
      </div>

      {/* ── Additional documents ── */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.12em", fontFamily: "'DM Mono', monospace" }}>{t('onboarding.step5.supportingDocsLabel')}</div>
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 3 }}>{t('onboarding.step5.supportingDocsHint')}</div>
          </div>
          {!addingDoc && (
            <button onClick={() => setAddingDoc(true)}
              style={{ padding: "7px 14px", background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 7, color: C.accent, cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>
              {t('onboarding.step5.addDocument')}
            </button>
          )}
        </div>

        {/* Add doc type picker */}
        {addingDoc && (
          <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 9, padding: 14, marginTop: 12, marginBottom: 14 }}>
            <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 10 }}>{t('onboarding.step5.selectDocType')}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              {ADDITIONAL_DOC_TYPES.map(opt => (
                <button key={opt.value} onClick={() => setNewDocType(opt.value)} style={{
                  padding: "10px 12px", borderRadius: 7, cursor: "pointer", textAlign: "left",
                  background: newDocType === opt.value ? C.accentGlow : C.bg,
                  border: `1px solid ${newDocType === opt.value ? C.accent : C.border}`,
                  transition: "all 0.12s",
                }}>
                  <div style={{ fontSize: 16, marginBottom: 3 }}>{opt.icon}</div>
                  <div style={{ color: newDocType === opt.value ? C.accent : C.text, fontSize: 12, fontWeight: 600 }}>{opt.label}</div>
                  <div style={{ color: C.textDim, fontSize: 10, marginTop: 2, lineHeight: 1.4 }}>{opt.hint}</div>
                </button>
              ))}
            </div>
            {newDocType === "other" && (
              <div style={{ marginBottom: 10 }}>
                <input value={newDocLabel} onChange={e => setNewDocLabel(e.target.value)}
                  placeholder="Document name (e.g. Partnership Agreement)"
                  style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "9px 12px", fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={addDocSlot} disabled={!newDocType} style={{ padding: "8px 18px", background: newDocType ? C.accent : C.border, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: newDocType ? "pointer" : "not-allowed", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                {t('common.add')}
              </button>
              <button onClick={() => { setAddingDoc(false); setNewDocType(""); setNewDocLabel(""); }}
                style={{ padding: "8px 14px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Uploaded additional docs */}
        {additionalDocs.length === 0 && !addingDoc && (
          <div style={{ textAlign: "center", padding: "24px 0", color: C.textDim, fontSize: 12 }}>
            {t('onboarding.step5.noDocsAdded')}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: additionalDocs.length > 0 ? 14 : 0 }}>
          {additionalDocs.map((doc, i) => {
            const docDef = ADDITIONAL_DOC_TYPES.find(d => d.value === doc.type);
            return (
              <div key={i} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 9, padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18 }}>{docDef?.icon || "📎"}</span>
                    <div>
                      <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{doc.label}</div>
                      {docDef?.hint && <div style={{ color: C.textDim, fontSize: 11 }}>{docDef.hint}</div>}
                    </div>
                  </div>
                  <button onClick={() => removeDoc(i)} style={{ background: "transparent", border: "none", color: C.danger, cursor: "pointer", fontSize: 13, opacity: 0.7, padding: "2px 6px" }}>✕</button>
                </div>
                <DropZone
                  value={doc.file}
                  onFile={f => updateDocFile(i, f)}
                  onClear={() => updateDocFile(i, null)}
                  hint="JPG, PNG or PDF · Max 15MB"
                  previewHeight={140}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <button onClick={onBack} style={{ flex: 1, padding: "12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 9, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t('onboarding.back')}</button>
        <button onClick={onNext} disabled={!canNext} style={{ flex: 3, padding: "12px", background: canNext ? C.accent : C.border, border: "none", borderRadius: 9, color: C.bg, fontWeight: 700, cursor: canNext ? "pointer" : "not-allowed", fontFamily: "'DM Mono', monospace", fontSize: 14 }}>{t('onboarding.continue')}</button>
      </div>
    </div>
  );
}

// ─── STEP 6: Selfie capture ────────────────────────────────────────────────────
function StepSelfie({ data, onChange, onNext, onBack, hideNav = false }) {
  const { t } = useTranslation();
  const videoRef   = useRef(null);
  const canvasRef  = useRef(null);
  const streamRef  = useRef(null);
  const fileRef    = useRef();
  const [camState,  setCamState]  = useState("idle");
  const [captured,  setCaptured]  = useState(data.selfie || null);
  const [countdown, setCountdown] = useState(null);
  const [mirror,    setMirror]    = useState(true);

  // Request camera permission and store the stream — called by the button
  function startCamera() {
    setCamState("loading"); // triggers render of <video> element first
  }

  // Once camState is "loading", the <video> element exists in the DOM.
  // Now we can safely attach the stream to the ref.
  useEffect(() => {
    if (camState !== "loading") return;
    let cancelled = false;

    async function attachStream() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          // Some browsers need an explicit play() call; ignore AbortError on fast unmounts
          video.play().catch(() => {});
        }
        setCamState("active");
      } catch (err) {
        if (!cancelled) setCamState("error");
      }
    }

    attachStream();
    return () => { cancelled = true; };
  }, [camState]);

  function stopCamera() {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCamState("idle");
  }

  async function capturePhoto() {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    if (mirror) { ctx.translate(c.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(v, 0, 0);
    const dataUrl = c.toDataURL("image/jpeg", 0.92);
    const compressed = await compressImage(dataUrl, 1024, 1024, 0.85);
    setCaptured(compressed); onChange("selfie", compressed);
    stopCamera();
  }

  function startCountdown() {
    setCountdown(3); let n = 3;
    const t = setInterval(() => {
      n--; if (n <= 0) { clearInterval(t); setCountdown(null); capturePhoto().catch(console.error); } else setCountdown(n);
    }, 1000);
  }

  async function handleUpload(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please upload an image file."); return; }
    if (file.type === "image/heic" || file.type === "image/heif" || file.name?.toLowerCase().endsWith('.heic')) {
      alert("HEIC format is not supported. Please convert to JPG or PNG and try again. iPhone users: Export as JPG before uploading.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) { alert("File must be under 15MB."); return; }
    const b64 = await fileToBase64(file);
    const compressed = await compressImage(b64, 1024, 1024, 0.85);
    setCaptured(compressed); onChange("selfie", compressed);
  }

  useEffect(() => () => stopCamera(), []);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>{t('onboarding.step6.title')}</h2>
        <p style={{ color: C.textMid, fontSize: 13, margin: 0 }}>{t('onboarding.step6.subtitle')}</p>
      </div>

      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
        {captured ? (
          <div style={{ position: "relative" }}>
            <img src={captured} alt="Selfie"
              style={{ width: "100%", maxHeight: 380, objectFit: "cover", display: "block" }} />
            {/* Overlay controls */}
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.85))", pointerEvents: "none" }} />
            <div style={{ position: "absolute", bottom: 14, left: 16, right: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div>
                <div style={{ color: C.success, fontSize: 14, fontWeight: 700 }}>✓ {t('onboarding.step6.selfieCaptured')}</div>
                <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{t('onboarding.step6.lookingGood')}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => fileRef.current?.click()}
                  style={{ padding: "7px 13px", background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 7, color: C.accent, cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
                  {t('onboarding.step6.uploadInstead')}
                </button>
                <button onClick={() => { setCaptured(null); onChange("selfie", null); startCamera(); }}
                  style={{ padding: "7px 13px", background: `${C.danger}18`, border: `1px solid ${C.danger}40`, borderRadius: 7, color: C.danger, cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
                  {t('onboarding.step6.retake')}
                </button>
              </div>
            </div>
          </div>
        ) : camState === "active" || camState === "loading" ? (
          <div style={{ position: "relative" }}>
            <video ref={videoRef} autoPlay playsInline muted
              style={{ width: "100%", maxHeight: 380, objectFit: "cover", display: "block", transform: mirror ? "scaleX(-1)" : "none" }} />
            {/* Loading overlay — shown until stream is live */}
            {camState === "loading" && (
              <div style={{ position: "absolute", inset: 0, background: C.surfaceAlt, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, border: `3px solid ${C.border}`, borderTopColor: C.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <div style={{ color: C.textMid, fontSize: 13 }}>{t('onboarding.step6.startingCamera')}</div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            {/* Oval guide overlay — only when active */}
            {camState === "active" && (<>
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                <div style={{ width: 190, height: 240, border: `3px solid ${countdown !== null ? C.warn : C.accent}`, borderRadius: "50%", boxShadow: `0 0 0 9999px rgba(10,14,26,0.52)`, transition: "border-color 0.2s" }} />
              </div>
              {countdown !== null && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                  <div style={{ fontSize: 88, fontWeight: 800, color: C.warn, fontFamily: "'Syne', sans-serif", textShadow: `0 0 48px ${C.warn}` }}>{countdown}</div>
                </div>
              )}
              <div style={{ position: "absolute", top: 12, right: 12 }}>
                <button onClick={() => setMirror(m => !m)}
                  style={{ background: "rgba(0,0,0,0.5)", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, padding: "5px 10px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
                  {mirror ? "🔄 Mirrored" : "🔄 Normal"}
                </button>
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "14px 20px", background: "linear-gradient(transparent, rgba(0,0,0,0.9))", display: "flex", gap: 10, justifyContent: "center", alignItems: "center" }}>
                <button onClick={startCountdown} disabled={countdown !== null}
                  style={{ padding: "11px 32px", background: countdown ? C.border : C.accent, border: "none", borderRadius: 8, color: C.bg, fontWeight: 700, cursor: countdown ? "default" : "pointer", fontFamily: "'DM Mono', monospace", fontSize: 14, transition: "background 0.2s" }}>
                  {countdown ? `📸 ${countdown}…` : `📸 ${t('onboarding.step6.takePhoto')}`}
                </button>
                <button onClick={stopCamera}
                  style={{ padding: "11px 16px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                  {t('common.cancel')}
                </button>
              </div>
            </>)}
          </div>
        ) : (
          <div style={{ padding: "44px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>🤳</div>
            <div style={{ color: C.text, fontSize: 16, fontWeight: 700, marginBottom: 6, fontFamily: "'Syne', sans-serif" }}>{t('onboarding.step6.takeSelfie')}</div>
            {camState === "error" && (
              <div style={{ color: C.warn, fontSize: 12, marginBottom: 16, background: `${C.warn}10`, border: `1px solid ${C.warn}30`, borderRadius: 7, padding: "8px 14px", display: "inline-block" }}>
                ⚠ {t('onboarding.step6.cameraAccessDenied')}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              {camState !== "error" && (
                <button onClick={startCamera}
                  style={{ padding: "11px 24px", background: C.accent, border: "none", borderRadius: 8, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                  📷 {t('onboarding.step6.useCamera')}
                </button>
              )}
              <button onClick={() => fileRef.current?.click()}
                style={{ padding: "11px 24px", background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 8, color: C.accent, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                ⬆ {t('onboarding.step6.uploadPhoto')}
              </button>
            </div>
          </div>
        )}
      </div>

      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleUpload(e.target.files[0])} />
      <canvas ref={canvasRef} style={{ display: "none" }} />

      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px", marginBottom: 4 }}>
        <div style={{ color: C.textDim, fontSize: 12, lineHeight: 1.9 }}>
          {t('onboarding.step6.tips')}
        </div>
      </div>

      {!hideNav && (
      <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
        <button onClick={onBack} style={{ flex: 1, padding: "12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 9, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t('onboarding.back')}</button>
        <button onClick={onNext} disabled={!captured} style={{ flex: 3, padding: "12px", background: captured ? C.accent : C.border, border: "none", borderRadius: 9, color: C.bg, fontWeight: 700, cursor: captured ? "pointer" : "not-allowed", fontFamily: "'DM Mono', monospace", fontSize: 14 }}>{t('onboarding.continue')}</button>
      </div>
      )}
    </div>
  );
}

// ─── STEP 7: Review & Submit ───────────────────────────────────────────────────
function StepReview({ data, onSubmit, onBack, submitting, narrative = "", setNarrative = () => {}, saveNarrative = () => {}, narrativeSaved = false }) {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const [termsAcceptedAt, setTermsAcceptedAt] = useState(null);
  const [showNarrative, setShowNarrative] = useState(false);

  const sections = [
    { title: "Applicant Type", fields: [["Type", data.type]] },
    { title: "Personal Information", fields: [["Name", `${data.firstName || ""} ${data.lastName || ""}`.trim()], ["DOB", data.dob], ["Nationality", data.nationality], ["Email", data.email], ["Phone", data.phone], ["Tax ID", data.taxId || "—"], ["Job Title", data.jobTitle || "—"], ["Employer", data.employerName || "—"], ["Purpose of Account", data.purposeOfAccount || "—"], ...(data.walletId ? [["Wallet ID", data.walletId]] : [])] },
    ...(data.type === "business" ? [{ title: "Business Details", fields: [["Business", data.businessName], ["Reg No.", data.regNumber], ["Type", data.businessType], ["Country", data.incorporationCountry]] }] : []),
    { title: "Address", fields: [["Address", [data.addressLine1, data.addressLine2, data.city, data.postCode, data.country].filter(Boolean).join(", ")]] },
    { title: "Compliance", fields: [["Source of Funds", data.sourceOfFunds], ["PEP", data.pepDeclaration], ["Sanctions", data.sanctionsDeclaration]] },
    { title: "Documents", fields: [["ID Type", data.idType || "Not specified"], ["Front uploaded", data.idFront ? "✓ Yes" : "✗ No"], ["Back uploaded", data.idBack ? "✓ Yes" : "—"], ["Selfie", data.selfie ? "✓ Yes" : "✗ No"]] },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>{t('onboarding.step7.title')}</h2>
        <p style={{ color: C.textMid, fontSize: 13, margin: 0 }}>{t('onboarding.step7.subtitle')}</p>
      </div>

      {sections.map(s => (
        <div key={s.title} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
          <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.1em", fontFamily: "'DM Mono', monospace", marginBottom: 10 }}>{s.title.toUpperCase()}</div>
          {s.fields.map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
              <span style={{ color: C.textMid }}>{k}</span>
              <span style={{ color: C.text, fontFamily: "'DM Mono', monospace", maxWidth: "65%", textAlign: "right", textTransform: v === "individual" || v === "business" ? "capitalize" : "none" }}>{v || "—"}</span>
            </div>
          ))}
        </div>
      ))}

      {/* Narrative Panel */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 16, marginTop: 16 }}>
        <button onClick={() => setShowNarrative(!showNarrative)} style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", color: C.text, fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: showNarrative ? 12 : 0 }}>
          📝 {narrative.length > 0 ? `Onboarding Notes (${narrative.length} chars)` : "Add Onboarding Notes"}
        </button>
        {showNarrative && (
          <>
            <textarea
              value={narrative}
              onChange={e => setNarrative(e.target.value)}
              placeholder="Add any notes, flags, or observations about this applicant..."
              style={{ width: "100%", minHeight: "100px", padding: 12, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, fontFamily: "'DM Mono', monospace", outline: "none", resize: "vertical", marginBottom: 12 }}
            />
            <button onClick={saveNarrative} style={{ padding: "8px 14px", background: C.success, border: "none", borderRadius: 6, color: C.bg, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>
              {narrativeSaved ? "✓ Saved" : "Save Notes"}
            </button>
          </>
        )}
      </div>

      <div style={{ background: `${C.accent}08`, border: `1px solid ${C.accent}25`, borderRadius: 10, padding: 16, marginTop: 16 }}>
        <label style={{ display: "flex", gap: 12, cursor: "pointer", alignItems: "flex-start" }}>
          <input type="checkbox" checked={agreed} onChange={e => { setAgreed(e.target.checked); setTermsAcceptedAt(e.target.checked ? new Date().toISOString() : null); }} style={{ marginTop: 2, accentColor: C.accent, flexShrink: 0 }} />
          <span style={{ color: C.textMid, fontSize: 12, lineHeight: 1.7 }}>
            {t('onboarding.step7.declaration')}
          </span>
        </label>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
        <button onClick={onBack} disabled={submitting} style={{ flex: 1, padding: "12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 9, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t('onboarding.back')}</button>
        <button onClick={() => agreed && onSubmit(termsAcceptedAt)} disabled={!agreed || submitting} style={{ flex: 3, padding: "12px", background: agreed && !submitting ? C.success : C.border, border: "none", borderRadius: 9, color: C.bg, fontWeight: 700, cursor: agreed && !submitting ? "pointer" : "not-allowed", fontFamily: "'DM Mono', monospace", fontSize: 14 }}>
          {submitting ? t('onboarding.step7.submitting') : `✓ ${t('onboarding.step7.submitApplication')}`}
        </button>
      </div>
    </div>
  );
}

// ─── Complete screen ──────────────────────────────────────────────────────────
function StepComplete({ data }) {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: "center", padding: "20px 0 10px" }}>
      <div style={{ width: 72, height: 72, borderRadius: "50%", background: `${C.success}18`, border: `2px solid ${C.success}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, margin: "0 auto 20px" }}>✓</div>
      <h2 style={{ color: C.success, fontFamily: "'Syne', sans-serif", fontSize: 24, fontWeight: 700, margin: "0 0 10px" }}>{t('onboarding.complete.title')}</h2>
      <p style={{ color: C.textMid, fontSize: 14, lineHeight: 1.7, margin: "0 0 24px" }}>
        {t('onboarding.complete.thankYou', { name: data.firstName })}<br />
        {t('onboarding.complete.reviewTime')}
      </p>
      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, textAlign: "left" }}>
        <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.8 }}>
          📧 {t('onboarding.complete.confirmationSent')} <span style={{ color: C.accent }}>{data.email}</span><br />
          🔍 {t('onboarding.complete.underReview')}<br />
          ⏱ {t('onboarding.complete.expectedResponse')}<br />
          ❓ {t('onboarding.complete.questions')}
        </div>
      </div>
    </div>
  );
}

// ─── Main KYC Portal ─────────────────────────────────────────────────────────
export default function KYCOnboardingPortal({ companyId: propCompanyId = null }) {
  const { t } = useTranslation();
  // Also read company_id from URL ?company=xxx so public links work correctly
  const urlCompanyId    = new URLSearchParams(window.location.search).get("company");
  const companyId       = propCompanyId || urlCompanyId || null;
  const resubmitId      = new URLSearchParams(window.location.search).get("resubmit");
  const reqParam        = new URLSearchParams(window.location.search).get("req");
  const infoRequirements = reqParam ? (() => { try { return JSON.parse(atob(decodeURIComponent(reqParam))); } catch { return null; } })() : null;

  const [step,       setStep]       = useState(0);
  const [data,       setData]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState(null);
  const [ipData,     setIpData]     = useState(null);
  const [narrative,  setNarrative]  = useState("");
  const [narrativeSaved, setNarrativeSaved] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(resubmitId ? true : false);
  const [previousDocPaths, setPreviousDocPaths] = useState([]);

  useEffect(() => {
    fetch("https://ipapi.co/json/")
      .then(r => r.json())
      .then(d => setIpData({ ip: d.ip, city: d.city, region: d.region, country: d.country_name, country_code: d.country_code }))
      .catch(() => {});
  }, []);

  // Load existing submission for resubmission
  useEffect(() => {
    if (!resubmitId) return;

    const loadExisting = async () => {
      try {
        const token = localStorage.getItem('amlguard_token');
        const res = await fetch(`/api/query?resource=kyc_submissions&id=${resubmitId}`, {
          headers: getApiHeaders({ 'Authorization': `Bearer ${token}` }),
        });

        if (!res.ok) {
          console.error('[KYC] Failed to load existing submission');
          setLoadingExisting(false);
          return;
        }

        const submissions = await res.json();
        const existing = submissions.find(s => s.id === resubmitId);
        if (!existing) {
          console.error('[KYC] Submission not found');
          setLoadingExisting(false);
          return;
        }

        // Pre-fill form with existing data
        setData({
          type: existing.type,
          firstName: existing.first_name,
          lastName: existing.last_name,
          email: existing.email,
          phone: existing.phone,
          dob: existing.dob,
          nationality: existing.nationality,
          taxId: existing.tax_id,
          jobTitle: existing.job_title,
          employerName: existing.employer_name,
          purposeOfAccount: existing.purpose_of_account,
          walletId: existing.wallet_id,
          addressLine1: existing.address_line1,
          addressLine2: existing.address_line2,
          city: existing.city,
          postCode: existing.post_code,
          country: existing.country,
          sourceOfFunds: existing.source_of_funds,
          annualIncome: existing.annual_income,
          pepDeclaration: existing.pep_declaration,
          pepDetails: existing.pep_details ? JSON.parse(existing.pep_details) : null,
          sanctionsDeclaration: existing.sanctions_declaration,
          idType: existing.id_type,
          businessName: existing.business_name,
          tradingName: existing.trading_name,
          regNumber: existing.reg_number,
          businessTaxId: existing.business_tax_id,
          businessType: existing.business_type,
          incorporationCountry: existing.incorporation_country,
          registeredAddress: existing.registered_address,
          website: existing.website,
          directors: (() => { try { return existing.directors && typeof existing.directors === 'string' ? JSON.parse(existing.directors) : Array.isArray(existing.directors) ? existing.directors : []; } catch { return []; } })(),
          ubos: (() => { try { return existing.ubos && typeof existing.ubos === 'string' ? JSON.parse(existing.ubos) : Array.isArray(existing.ubos) ? existing.ubos : []; } catch { return []; } })(),
          additionalDocs: (() => { try { return existing.additional_docs && typeof existing.additional_docs === 'string' ? JSON.parse(existing.additional_docs) : Array.isArray(existing.additional_docs) ? existing.additional_docs : []; } catch { return []; } })(),
        });

        // Also load documents
        const docsRes = await fetch(`/api/query?resource=kyc_documents`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const docs = docsRes.ok ? await docsRes.json() : [];
        const subDocs = docs.filter(d => d.submission_id === resubmitId);

        if (subDocs.find(d => d.document_type === 'idFront')) {
          setData(prev => ({ ...prev, idFront: { data: subDocs.find(d => d.document_type === 'idFront').file_data } }));
        }
        if (subDocs.find(d => d.document_type === 'idBack')) {
          setData(prev => ({ ...prev, idBack: { data: subDocs.find(d => d.document_type === 'idBack').file_data } }));
        }
        if (subDocs.find(d => d.document_type === 'selfie')) {
          setData(prev => ({ ...prev, selfie: subDocs.find(d => d.document_type === 'selfie').file_data }));
        }

        // Load previous document paths to carry over on resubmission
        try {
          const prevDocPaths = existing.additional_doc_paths ? JSON.parse(existing.additional_doc_paths) : [];
          if (Array.isArray(prevDocPaths) && prevDocPaths.length > 0) {
            setPreviousDocPaths(prevDocPaths);
            console.log('[KYC] Loaded previous document paths for resubmission:', prevDocPaths.length);
          }
        } catch (e) {
          console.warn('[KYC] Could not parse previous document paths:', e.message);
        }

        setLoadingExisting(false);
      } catch (e) {
        console.error('[KYC] Error loading existing submission:', e);
        setLoadingExisting(false);
      }
    };

    loadExisting();
  }, [resubmitId]);

  // Save narrative to localStorage
  const saveNarrative = () => {
    const customerName = data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : data.businessName || "Unknown";
    const key = `kyc_narrative_${customerName.toLowerCase().replace(/\s+/g, '_')}`;
    localStorage.setItem(key, narrative);
    setNarrativeSaved(true);
    setTimeout(() => setNarrativeSaved(false), 2000);
  };

  // Load narrative from localStorage
  useEffect(() => {
    if (data.firstName || data.businessName) {
      const customerName = data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : data.businessName || "";
      if (customerName) {
        const key = `kyc_narrative_${customerName.toLowerCase().replace(/\s+/g, '_')}`;
        const saved = localStorage.getItem(key) || "";
        setNarrative(saved);
      }
    }
  }, [data.firstName, data.lastName, data.businessName]);

  function updateData(key, value) {
    setData(prev => ({ ...prev, [key]: value }));
  }

  // Steps vary by type
  const isInd = data.type !== "business";
  const STEPS = isInd
    ? ["Type", "Personal", "Compliance", "Documents", "Selfie", "Review"]
    : ["Type", "Personal", "Business",   "Compliance", "Documents", "Selfie", "Review"];

  function next() { setStep(s => Math.min(s + 1, STEPS.length - 1)); window.scrollTo(0, 0); }
  function back() { setStep(s => Math.max(s - 1, 0)); window.scrollTo(0, 0); }

  async function handleSubmit(termsAcceptedAt) {
    setSubmitting(true); setError(null);
    try {
      if (!companyId) {
        throw new Error("Missing company ID — this form link may be invalid. Please contact your onboarding manager.");
      }
      // Strip large binary data from the main record — store metadata only
      // Ensure all values are serializable (strings, numbers, booleans, null, or JSON-stringified objects)
      const record = {
        ...(resubmitId && { id: resubmitId }),
        company_id:            companyId || null,
        type:                  String(data.type || ""),
        first_name:            String(data.firstName || ""),
        last_name:             String(data.lastName || ""),
        email:                 String(data.email || ""),
        phone:                 String(data.phone || ""),
        dob:                   String(data.dob || ""),
        nationality:           String(data.nationality || ""),
        tax_id:                String(data.taxId || ""),
        address_line1:         String(data.addressLine1 || ""),
        address_line2:         String(data.addressLine2 || ""),
        city:                  String(data.city || ""),
        post_code:             String(data.postCode || ""),
        country:               String(data.country || ""),
        source_of_funds:       String(data.sourceOfFunds || ""),
        source_of_funds_other: String(data.sourceOfFundsOther || ""),
        annual_income:         String(data.annualIncome || ""),
        pep_declaration:       data.pepDeclaration === "yes" || data.pepDeclaration === "associate" ? true : data.pepDeclaration === "no" ? false : null,
        pep_details:           data.pepDetails ? JSON.stringify(data.pepDetails) : null,
        pep_associate_name:    String(data.pepAssociateName || ""),
        pep_relationship:      String(data.pepRelationship || ""),
        pep_position:          String(data.pepPosition || ""),
        pep_country:           String(data.pepCountry || ""),
        pep_family_members:    data.pepFamilyMembers ? JSON.stringify(data.pepFamilyMembers) : null,
        sanctions_declaration: data.sanctionsDeclaration === "yes" ? true : data.sanctionsDeclaration === "no" ? false : null,
        id_type:               String(data.idType || ""),
        id_front_name:         data.idFront && typeof data.idFront === 'object' ? String(data.idFront.name || "") : String(data.idFront || ""),
        id_back_name:          data.idBack && typeof data.idBack === 'object' ? String(data.idBack.name || "") : String(data.idBack || ""),
        // Business fields
        job_title:             String(data.jobTitle || ""),
        employer_name:         String(data.employerName || ""),
        purpose_of_account:    String(data.purposeOfAccount || ""),
        wallet_id:             String(data.walletId || ""),
        // Company fields
        business_name:         String(data.businessName || ""),
        trading_name:          String(data.tradingName || ""),
        reg_number:            String(data.regNumber || ""),
        business_tax_id:       String(data.businessTaxId || ""),
        business_type:         String(data.businessType || ""),
        incorporation_country: String(data.incorporationCountry || ""),
        registered_address:    String(data.registeredAddress || ""),
        website:               String(data.website || ""),
        directors:             JSON.stringify(data.directors || []),
        ubos:                  JSON.stringify(data.ubos || []),
        additional_docs:       JSON.stringify((data.additionalDocs || []).map(d => ({ type: d.type, label: d.label, name: (d.file && d.file.name) || String(d.name || "") }))),
        // Auto-reject if applicant declares sanctions
        status:                data.sanctionsDeclaration === true ? "rejected" : (resubmitId ? "pending_review" : "pending"),
        submitted_at:          new Date().toISOString(),
        terms_accepted_at:     typeof termsAcceptedAt === 'string' ? termsAcceptedAt : new Date().toISOString(),
        ip_address:            ipData?.ip || null,
        ip_country:            ipData?.country || null,
        ip_country_code:       ipData?.country_code || null,
        ip_city:               ipData?.city || null,
      };

      console.log("[KYC] Submitting…", { email: record.email, type: record.type, isResubmission: !!resubmitId, fieldsCount: Object.keys(record).length });

      // For resubmission, load existing data first and merge
      let submitRecord = record;
      if (resubmitId) {
        try {
          const token = localStorage.getItem('amlguard_token');
          const existingRes = await fetch(`/api/query?resource=kyc_submissions&id=${resubmitId}`, {
            headers: getApiHeaders({ 'Authorization': `Bearer ${token}` }),
          });
          if (existingRes.ok) {
            const existingSubmissions = await existingRes.json();
            const existingData = existingSubmissions.find(s => s.id === resubmitId);
            if (existingData) {
              // Merge: keep existing values for empty fields
              submitRecord = {
                id: resubmitId,
                type: record.type || existingData.type,
                company_id: record.company_id || existingData.company_id,
                email: record.email || existingData.email,
                first_name: record.first_name || existingData.first_name,
                last_name: record.last_name || existingData.last_name,
                phone: record.phone || existingData.phone,
                dob: record.dob || existingData.dob,
                nationality: record.nationality || existingData.nationality,
                tax_id: record.tax_id || existingData.tax_id,
                address_line1: record.address_line1 || existingData.address_line1,
                address_line2: record.address_line2 || existingData.address_line2,
                city: record.city || existingData.city,
                post_code: record.post_code || existingData.post_code,
                country: record.country || existingData.country,
                source_of_funds: record.source_of_funds || existingData.source_of_funds,
                source_of_funds_other: record.source_of_funds_other || existingData.source_of_funds_other,
                annual_income: record.annual_income || existingData.annual_income,
                pep_declaration: record.pep_declaration !== null ? record.pep_declaration : existingData.pep_declaration,
                pep_details: record.pep_details || existingData.pep_details,
                pep_associate_name: record.pep_associate_name || existingData.pep_associate_name,
                pep_relationship: record.pep_relationship || existingData.pep_relationship,
                pep_position: record.pep_position || existingData.pep_position,
                pep_country: record.pep_country || existingData.pep_country,
                pep_family_members: record.pep_family_members || existingData.pep_family_members,
                sanctions_declaration: record.sanctions_declaration !== null ? record.sanctions_declaration : existingData.sanctions_declaration,
                id_type: record.id_type || existingData.id_type,
                id_front_name: record.id_front_name || existingData.id_front_name,
                id_back_name: record.id_back_name || existingData.id_back_name,
                id_front_data: record.id_front_data || existingData.id_front_data,
                id_back_data: record.id_back_data || existingData.id_back_data,
                selfie_data: record.selfie_data || existingData.selfie_data,
                job_title: record.job_title || existingData.job_title,
                employer_name: record.employer_name || existingData.employer_name,
                purpose_of_account: record.purpose_of_account || existingData.purpose_of_account,
                wallet_id: record.wallet_id || existingData.wallet_id,
                business_name: record.business_name || existingData.business_name,
                trading_name: record.trading_name || existingData.trading_name,
                reg_number: record.reg_number || existingData.reg_number,
                business_tax_id: record.business_tax_id || existingData.business_tax_id,
                business_type: record.business_type || existingData.business_type,
                incorporation_country: record.incorporation_country || existingData.incorporation_country,
                registered_address: record.registered_address || existingData.registered_address,
                website: record.website || existingData.website,
                directors: record.directors || existingData.directors,
                ubos: record.ubos || existingData.ubos,
                additional_docs: record.additional_docs || existingData.additional_docs,
                status: record.status,
                submitted_at: record.submitted_at,
                terms_accepted_at: record.terms_accepted_at,
                ip_address: record.ip_address || existingData.ip_address,
                ip_country: record.ip_country || existingData.ip_country,
                ip_country_code: record.ip_country_code || existingData.ip_country_code,
                ip_city: record.ip_city || existingData.ip_city,
              };
            }
          }
        } catch (e) {
          console.warn('[KYC] Could not merge existing data, submitting as-is:', e.message);
        }
      }

      // Validate record doesn't contain circular references or DOM elements
      try {
        const bodyStr = JSON.stringify(submitRecord);
        console.log("[KYC] Request body size:", bodyStr.length, "bytes");
      } catch (e) {
        // If JSON.stringify fails, try to identify the problematic field
        console.error("[KYC] JSON serialization error:", e.message);
        for (const key in submitRecord) {
          try {
            JSON.stringify({ [key]: submitRecord[key] });
          } catch (fieldError) {
            console.error(`[KYC] Field "${key}" has circular reference, clearing it`);
            submitRecord[key] = null;
          }
        }
      }

      const token = localStorage.getItem('amlguard_token');
      const bodyStr = JSON.stringify(submitRecord);
      console.log("[KYC] Request body size:", bodyStr.length, "bytes");

      // Run Didit identity verification if configured
      let diditVerification = null;
      try {
        console.log('[KYC] Running Didit identity verification...');
        const diditData = data.type === 'company' ? {
          businessName: data.businessName,
          businessType: data.businessType,
          country: data.incorporationCountry,
          registrationNumber: data.regNumber,
          // Also include contact person info
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone
        } : {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          dob: data.dob,
          nationality: data.nationality,
          country: data.country,
          city: data.city,
          address: data.addressLine1,
          postalCode: data.postCode,
          state: data.state
        };

        // Didit verification functions not yet implemented
        // diditVerification = data.type === 'company'
        //   ? await performKYBCheck(diditData)
        //   : await performKYCCheck(diditData);

        console.log('[KYC] Didit verification result:', diditVerification);
        if (diditVerification) {
          submitRecord.didit_verification_id = diditVerification.verificationId || null;
          submitRecord.didit_status = diditVerification.status || null;
          submitRecord.didit_risk_level = diditVerification.riskLevel || null;
        }
      } catch (diditError) {
        console.warn('[KYC] Didit verification error (continuing with submission):', diditError.message);
      }

      // For resubmission, use PUT to update; otherwise POST to create new
      const method = resubmitId ? 'PUT' : 'POST';
      const endpoint = resubmitId ? `/api/query?resource=kyc_submissions&id=${resubmitId}` : '/api/query?resource=kyc_submissions';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: bodyStr,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[KYC] Submit error:", response.status, errorText);
        throw new Error(`Submit failed: ${response.status} — ${errorText}`);
      }

      const result = await response.json();
      const submissionId = resubmitId || result?.id;
      console.log("[KYC] Submitted:", submissionId, { isResubmission: !!resubmitId, mergedFields: Object.keys(submitRecord).length });

      // Auto-reject audit log if sanctions declared
      if (data.sanctionsDeclaration === true && submissionId) {
        try {
          await fetch('/api/query?resource=kyc_audit_logs', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              company_id: companyId,
              submission_id: submissionId,
              action: 'auto_rejected',
              note: 'Application automatically rejected: Applicant declared subject to sanctions',
              reviewer_id: null,
            }),
          });
          console.log('[KYC] Auto-rejection audit log created');
        } catch (e) {
          console.warn('[KYC] Failed to create auto-rejection audit log:', e.message);
        }
      }

      // ── Upload documents after submission ────────────────────────────────────
      // Documents are stored separately and linked to the submission
      if (submissionId) {
        const uploadedFiles = { idFront: false, idBack: false, selfie: false };

        async function uploadDoc(dataUrl, fileType, fileName) {
          if (!dataUrl) return;
          try {
            const uploadRes = await fetch('/api/kyc/upload-document', {
              method: 'POST',
              headers: getApiHeaders({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              }),
              body: JSON.stringify({
                submissionId,
                fileType,
                fileData: dataUrl,
                fileName
              }),
            });

            if (!uploadRes.ok) {
              const errorText = await uploadRes.text();
              console.error(`[KYC] ${fileType} upload failed:`, uploadRes.status, errorText);
              return;
            }

            console.log(`[KYC] ${fileType} uploaded successfully`);
            uploadedFiles[fileType] = true;
          } catch (e) {
            console.warn(`[KYC] ${fileType} upload error:`, e.message);
          }
        }

        // Upload documents in parallel
        await Promise.all([
          data.idFront?.data ? uploadDoc(data.idFront.data, 'idFront', data.idFront.name) : null,
          data.idBack?.data ? uploadDoc(data.idBack.data, 'idBack', data.idBack.name) : null,
          data.selfie ? uploadDoc(data.selfie, 'selfie', `selfie-${Date.now()}`) : null,
        ]);

        console.log("[KYC] Document upload complete:", uploadedFiles);

        // ── Run Socure Document Verification ─────────────────────────────────
        if (uploadedFiles.idFront || uploadedFiles.idBack || uploadedFiles.selfie) {
          try {
            console.log('[KYC] Starting Socure document verification...');
            const socureRes = await fetch('/api/socure/evaluate', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                submission_id: submissionId,
                id_front_base64: data.idFront?.data || null,
                id_back_base64: data.idBack?.data || null,
                selfie_base64: data.selfie || null
              })
            });

            if (!socureRes.ok) {
              const errorText = await socureRes.text();
              console.warn('[KYC] Socure evaluation warning (continuing):', socureRes.status, errorText);
            } else {
              const socureResult = await socureRes.json();
              console.log('[KYC] Socure verification initiated:', socureResult.reference_id, 'Status:', socureResult.status);
            }
          } catch (socureErr) {
            console.warn('[KYC] Socure verification error (continuing):', socureErr.message);
          }
        }
      }

      setSubmitted(true);
    } catch (e) {
      console.error("[KYC] Submit failed:", e);
      setError(e.message);
    }
    setSubmitting(false);
  }

  // Map step index to component
  const stepComponents = isInd
    ? [
        <StepType       data={data} onChange={updateData} onNext={next} />,
        <StepPersonal   data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepCompliance data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepDocuments  data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepSelfie     data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepReview     data={data} onSubmit={handleSubmit} onBack={back} submitting={submitting} narrative={narrative} setNarrative={setNarrative} saveNarrative={saveNarrative} narrativeSaved={narrativeSaved} />,
      ]
    : [
        <StepType       data={data} onChange={updateData} onNext={next} />,
        <StepPersonal   data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepBusiness   data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepCompliance data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepDocuments  data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepSelfie     data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepReview     data={data} onSubmit={handleSubmit} onBack={back} submitting={submitting} narrative={narrative} setNarrative={setNarrative} saveNarrative={saveNarrative} narrativeSaved={narrativeSaved} />,
      ];

  // Show loading state while existing submission is being loaded
  if (loadingExisting && resubmitId) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Mono', monospace" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 14, color: C.textMid, marginBottom: 16 }}>Loading your submission…</div>
          <div style={{ width: 40, height: 40, border: `2px solid ${C.border}`, borderRadius: "50%", borderTopColor: C.accent, animation: "spin 1s linear infinite", margin: "0 auto" }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ── Resubmission mode — show only requested items ───────────────────────────
  if (infoRequirements && !submitted) {
    const req = infoRequirements;
    return (
      <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'DM Mono', monospace", padding: "32px 16px" }}>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&display=swap" rel="stylesheet" />
        <div style={{ position: "fixed", inset: 0, opacity: 0.025, backgroundImage: "linear-gradient(#00D4FF 1px, transparent 1px), linear-gradient(90deg, #00D4FF 1px, transparent 1px)", backgroundSize: "48px 48px", pointerEvents: "none" }} />
        <div style={{ maxWidth: 640, margin: "0 auto", position: "relative" }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
              <div style={{ width: 30, height: 30, background: C.accent, clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }} />
              <span style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 20 }}>CrossRegGuard</span>
            </div>
            <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.2em" }}>{t('onboarding.additionalInfoRequest')}</div>
          </div>

          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: "32px", boxShadow: "0 32px 80px rgba(0,0,0,0.6)" }}>
            {/* Reviewer note */}
            {req.note && (
              <div style={{ background: `${C.accent}0D`, border: `1px solid ${C.accent}33`, borderRadius: 10, padding: "14px 16px", marginBottom: 24 }}>
                <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 6 }}>{t('onboarding.noteFromReviewer')}</div>
                <div style={{ color: C.text, fontSize: 13, lineHeight: 1.6 }}>{req.note}</div>
              </div>
            )}

            <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
              {t('onboarding.additionalInfoRequired')}
            </div>
            <p style={{ color: C.textMid, fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
              {t('onboarding.additionalInfoDesc')}
            </p>

            {/* What's needed */}
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 28 }}>
              <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 10 }}>{t('onboarding.requestedItems')}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {req.id_front           && <span style={{ background: `${C.accent}18`, color: C.accent, fontSize: 11, padding: "4px 10px", borderRadius: 5, fontWeight: 600 }}>🪪 ID (front)</span>}
                {req.id_back            && <span style={{ background: `${C.accent}18`, color: C.accent, fontSize: 11, padding: "4px 10px", borderRadius: 5, fontWeight: 600 }}>🪪 ID (back)</span>}
                {req.selfie             && <span style={{ background: `${C.accent}18`, color: C.accent, fontSize: 11, padding: "4px 10px", borderRadius: 5, fontWeight: 600 }}>🤳 Selfie</span>}
                {req.data               && <span style={{ background: `${C.accent}18`, color: C.accent, fontSize: 11, padding: "4px 10px", borderRadius: 5, fontWeight: 600 }}>📋 Personal Data</span>}
                {req.pep_declaration    && <span style={{ background: `${C.accent}18`, color: C.accent, fontSize: 11, padding: "4px 10px", borderRadius: 5, fontWeight: 600 }}>⚠️ PEP Declaration</span>}
                {req.sanctions_declaration && <span style={{ background: `${C.accent}18`, color: C.accent, fontSize: 11, padding: "4px 10px", borderRadius: 5, fontWeight: 600 }}>🛡️ Sanctions Declaration</span>}
                {(req.additional_docs || []).map(d => <span key={d} style={{ background: `${C.accent}18`, color: C.accent, fontSize: 11, padding: "4px 10px", borderRadius: 5, fontWeight: 600 }}>📎 {d}</span>)}
                {req.custom_doc && <span style={{ background: `${C.accent}18`, color: C.accent, fontSize: 11, padding: "4px 10px", borderRadius: 5, fontWeight: 600 }}>📎 {req.custom_doc}</span>}
              </div>
            </div>

            {error && (
              <div style={{ background: `${C.danger}10`, border: `1px solid ${C.danger}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 18, color: C.danger, fontSize: 12 }}>
                ✗ {error} — {t('onboarding.errorContact')}
              </div>
            )}

            {/* Conditionally show only requested fields */}
            {req.data && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 14 }}>{t('onboarding.personalInformation')}</div>
                <StepPersonal data={data} onChange={updateData} onNext={() => {}} onBack={() => {}} hideNav />
              </div>
            )}

            {(req.id_front || req.id_back) && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 14 }}>
                  {t('onboarding.identityDocuments')} — {[req.id_front && t('onboarding.front'), req.id_back && t('onboarding.back_label')].filter(Boolean).join(" & ")} {t('onboarding.required')}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: req.id_front && req.id_back ? "1fr 1fr" : "1fr", gap: 14 }}>
                  {req.id_front && (
                    <DropZone value={data.idFront} onFile={f => updateData("idFront", f)} onClear={() => updateData("idFront", null)}
                      label="ID Front" hint="JPG, PNG or PDF · Max 15MB" required previewHeight={180} />
                  )}
                  {req.id_back && (
                    <DropZone value={data.idBack} onFile={f => updateData("idBack", f)} onClear={() => updateData("idBack", null)}
                      label="ID Back" hint="JPG, PNG or PDF · Max 15MB" previewHeight={180} />
                  )}
                </div>
              </div>
            )}

            {req.selfie && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 14 }}>{t('onboarding.selfieLabel')}</div>
                <StepSelfie data={data} onChange={updateData} onNext={() => {}} onBack={() => {}} hideNav />
              </div>
            )}

            {(req.additional_docs?.length > 0 || req.custom_doc) && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 14 }}>{t('onboarding.supportingDocuments')}</div>
                {[...(req.additional_docs || []), ...(req.custom_doc ? [req.custom_doc] : [])].map((docType, i) => {
                  const key = `resubDoc_${i}`;
                  const existing = (data.additionalDocs || []).find(d => d.type === docType || d.label === docType);
                  return (
                    <div key={docType} style={{ marginBottom: 14 }}>
                      <div style={{ color: C.textMid, fontSize: 12, marginBottom: 6 }}>{docType}</div>
                      <DropZone
                        value={existing?.file || null}
                        onFile={f => {
                          const docs = [...(data.additionalDocs || [])];
                          const idx = docs.findIndex(d => d.type === docType || d.label === docType);
                          if (idx >= 0) docs[idx] = { ...docs[idx], file: f };
                          else docs.push({ type: docType, label: docType, file: f });
                          updateData("additionalDocs", docs);
                        }}
                        onClear={() => {
                          const docs = (data.additionalDocs || []).filter(d => d.type !== docType && d.label !== docType);
                          updateData("additionalDocs", docs);
                        }}
                        label={docType}
                        hint="JPG, PNG or PDF · Max 15MB"
                        previewHeight={160}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {req.pep_declaration && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 14 }}>PEP DECLARATION</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <label style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "12px 14px", background: data.pepDeclaration === false ? C.accentGlow : C.bg, border: `1px solid ${data.pepDeclaration === false ? C.accent : C.border}`, borderRadius: 8 }}>
                    <input type="radio" name="pep" checked={data.pepDeclaration === false} onChange={() => updateData("pepDeclaration", false)} style={{ cursor: "pointer" }} />
                    <span style={{ color: C.text, fontSize: 13 }}>I AM a PEP</span>
                  </label>
                  <label style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "12px 14px", background: data.pepDeclaration === true ? C.accentGlow : C.bg, border: `1px solid ${data.pepDeclaration === true ? C.accent : C.border}`, borderRadius: 8 }}>
                    <input type="radio" name="pep" checked={data.pepDeclaration === true} onChange={() => updateData("pepDeclaration", true)} style={{ cursor: "pointer" }} />
                    <span style={{ color: C.text, fontSize: 13 }}>I am NOT a PEP</span>
                  </label>
                </div>
                {data.pepDeclaration === false && (
                  <textarea
                    placeholder="Please provide details about your PEP status..."
                    value={data.pepDetails?.notes || ""}
                    onChange={e => updateData("pepDetails", { notes: e.target.value })}
                    style={{ width: "100%", minHeight: 80, padding: 12, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", resize: "vertical", marginTop: 12 }}
                  />
                )}
              </div>
            )}

            {req.sanctions_declaration && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 14 }}>SANCTIONS DECLARATION</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <label style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "12px 14px", background: data.sanctionsDeclaration === false ? C.accentGlow : C.bg, border: `1px solid ${data.sanctionsDeclaration === false ? C.accent : C.border}`, borderRadius: 8 }}>
                    <input type="radio" name="sanctions" checked={data.sanctionsDeclaration === false} onChange={() => updateData("sanctionsDeclaration", false)} style={{ cursor: "pointer" }} />
                    <span style={{ color: C.text, fontSize: 13 }}>I AM on sanctions lists</span>
                  </label>
                  <label style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "12px 14px", background: data.sanctionsDeclaration === true ? C.accentGlow : C.bg, border: `1px solid ${data.sanctionsDeclaration === true ? C.accent : C.border}`, borderRadius: 8 }}>
                    <input type="radio" name="sanctions" checked={data.sanctionsDeclaration === true} onChange={() => updateData("sanctionsDeclaration", true)} style={{ cursor: "pointer" }} />
                    <span style={{ color: C.text, fontSize: 13 }}>I am NOT on sanctions lists</span>
                  </label>
                </div>
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{ width: "100%", padding: "14px", background: C.accent, border: "none", borderRadius: 10, color: C.bg, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "'Syne', sans-serif", fontSize: 15, opacity: submitting ? 0.7 : 1, marginTop: 8 }}>
              {submitting ? t('onboarding.step7.submitting') : t('onboarding.submitUpdatedInfo')}
            </button>
          </div>

          <div style={{ textAlign: "center", marginTop: 20, color: C.textDim, fontSize: 11, lineHeight: 1.8 }}>
            {t('onboarding.footer')}
          </div>
        </div>
      </div>
    );
  }

  // ── Normal full form ─────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'DM Mono', monospace", padding: "32px 16px" }}>
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&display=swap" rel="stylesheet" />
      {/* Background grid */}
      <div style={{ position: "fixed", inset: 0, opacity: 0.025, backgroundImage: "linear-gradient(#00D4FF 1px, transparent 1px), linear-gradient(90deg, #00D4FF 1px, transparent 1px)", backgroundSize: "48px 48px", pointerEvents: "none" }} />

      <div style={{ maxWidth: 640, margin: "0 auto", position: "relative" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <div style={{ width: 30, height: 30, background: C.accent, clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }} />
            <span style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 20 }}>CrossRegGuard</span>
          </div>
          <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.2em" }}>{t('onboarding.identityVerificationPortal')}</div>
        </div>

        {/* Card */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: "32px 32px", boxShadow: "0 32px 80px rgba(0,0,0,0.6)" }}>
          {submitted ? (
            <StepComplete data={data} />
          ) : (
            <>
              <StepIndicator steps={STEPS} current={step} />
              {error && (
                <div style={{ background: `${C.danger}10`, border: `1px solid ${C.danger}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 18, color: C.danger, fontSize: 12 }}>
                  ✗ {error} — {t('onboarding.errorContact')}
                </div>
              )}
              {stepComponents[step]}
            </>
          )}
        </div>

        <div style={{ textAlign: "center", marginTop: 20, color: C.textDim, fontSize: 11, lineHeight: 1.8 }}>
          {t('onboarding.footer')}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// KYC REVIEW DASHBOARD — for your internal compliance team
// Import this into your app: import { KYCReviewDashboard } from "./KYCOnboardingPortal"
// ─────────────────────────────────────────────────────────────────────────────

const RISK_WEIGHTS = {
  pep_yes:        40,
  pep_associate:  25,
  sanctions_yes:  60,
  high_income:    15,
  offshore:       20,
  missing_back:   5,
};

const OFFSHORE_COUNTRIES = ["Cayman Islands","BVI","British Virgin Islands","Panama","Seychelles","Marshall Islands","Vanuatu","Liechtenstein","Monaco","Andorra"];

function computeRiskScore(sub) {
  // If a manual risk_score has been set, use it instead of recalculating
  if (sub.risk_score !== null && sub.risk_score !== undefined && sub.risk_score !== '') {
    const manualScore = parseInt(sub.risk_score);
    if (!isNaN(manualScore)) {
      return { score: Math.min(Math.max(manualScore, 0), 100), flags: [] };
    }
  }

  // Otherwise, calculate based on risk flags
  let score = 10; // baseline
  const flags = [];

  if (sub.pep_declaration === "yes")       { score += RISK_WEIGHTS.pep_yes;       flags.push({ label: "PEP",              color: C.danger }); }
  if (sub.pep_declaration === "associate") { score += RISK_WEIGHTS.pep_associate;  flags.push({ label: "PEP Associate",    color: C.warn   }); }
  if (sub.sanctions_declaration === "yes") { score += RISK_WEIGHTS.sanctions_yes;  flags.push({ label: "Sanctions Flag",   color: C.danger }); }

  const income = parseFloat((sub.annual_income || "").replace(/[^0-9.]/g, ""));
  if (!isNaN(income) && income > 500000) { score += RISK_WEIGHTS.high_income; flags.push({ label: "High Net Worth",  color: C.gold   }); }

  const country = (sub.incorporation_country || sub.country || "").toLowerCase();
  if (OFFSHORE_COUNTRIES.some(c => country.includes(c.toLowerCase()))) {
    score += RISK_WEIGHTS.offshore; flags.push({ label: "Offshore Jurisdiction", color: C.warn });
  }

  if (!sub.id_back_data && sub.id_type !== "Passport") {
    score += RISK_WEIGHTS.missing_back; flags.push({ label: "Missing ID Back", color: C.textDim });
  }

  return { score: Math.min(score, 100), flags };
}

function RiskMeter({ score, onScoreChange, isEditable = false }) {
  const [editMode, setEditMode] = useState(false);
  const [tempScore, setTempScore] = useState(score);

  const color = score >= 70 ? C.danger : score >= 40 ? C.warn : C.success;
  const label = score >= 70 ? "HIGH"   : score >= 40 ? "MEDIUM" : "LOW";

  const handleSave = async () => {
    if (onScoreChange) {
      await onScoreChange(parseInt(tempScore));
      setEditMode(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: isEditable ? 'pointer' : 'default',
          padding: '4px 8px',
          borderRadius: 4,
          background: isEditable && editMode ? `${C.accent}10` : 'transparent'
        }}
        onMouseEnter={(e) => isEditable && !editMode && (e.target.style.background = `${C.border}`)}
        onMouseLeave={(e) => isEditable && !editMode && (e.target.style.background = 'transparent')}
        onClick={() => isEditable && setEditMode(true)}
      >
        <span style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em" }}>RISK SCORE</span>
        {editMode ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="number"
              min="0"
              max="100"
              value={tempScore}
              onChange={(e) => setTempScore(e.target.value)}
              style={{
                width: 50,
                padding: '4px 6px',
                background: C.surface,
                border: `1px solid ${C.accent}`,
                color: C.text,
                borderRadius: 4,
                fontFamily: "'DM Mono', monospace",
                fontSize: 12
              }}
            />
            <button
              onClick={handleSave}
              style={{
                padding: '4px 8px',
                background: C.success,
                color: C.surface,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 10,
                fontWeight: 700
              }}
            >
              Save
            </button>
            <button
              onClick={() => { setTempScore(score); setEditMode(false); }}
              style={{
                padding: '4px 8px',
                background: C.border,
                color: C.text,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 10
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <span style={{ color, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            {score}/100 · {label}
            {isEditable && <span style={{ fontSize: 10 }}>✏️</span>}
          </span>
        )}
      </div>
      <div style={{ height: 6, background: C.border, borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${score}%`, height: "100%", background: `linear-gradient(90deg, ${C.success}, ${color})`, borderRadius: 4, transition: "width 0.4s" }} />
      </div>
    </div>
  );
}

// ─── Mock submissions (shown when Supabase has no data) ───────────────────────
const MOCK_SUBMISSIONS = [
  {
    id: "mock-001", type: "individual",
    first_name: "James", last_name: "Hartwell",
    email: "j.hartwell@gmail.com", phone: "+44 7700 123456",
    dob: "1985-03-14", nationality: "British",
    address_line1: "42 Kensington Gardens", city: "London", post_code: "W2 4BJ", country: "United Kingdom",
    tax_id: "AB 12 34 56 C",
    source_of_funds: "Employment / Salary", annual_income: "95,000",
    pep_declaration: "no", sanctions_declaration: "no",
    id_type: "Passport", id_front_data: null, id_back_data: null, selfie_data: null,
    status: "pending",
    submitted_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    risk_score: null, directors: [],
  },
  {
    id: "mock-002", type: "individual",
    first_name: "Mei-Ling", last_name: "Chen",
    email: "mchen@horizonventures.hk", phone: "+852 9123 4567",
    dob: "1979-08-22", nationality: "Hong Kong SAR",
    address_line1: "18 Des Voeux Road Central", city: "Hong Kong", post_code: "", country: "Hong Kong",
    tax_id: "A123456(7)",
    source_of_funds: "Business Income", annual_income: "420,000",
    pep_declaration: "no", sanctions_declaration: "no",
    id_type: "National ID Card", id_front_data: null, id_back_data: null, selfie_data: null,
    status: "approved",
    reviewed_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    review_note: "All documents verified. Enhanced due diligence completed. Approved.",
    submitted_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    risk_score: null, directors: [],
  },
  {
    id: "mock-003", type: "business",
    first_name: "Oliver", last_name: "Whitmore",
    email: "o.whitmore@falconridge.ky", phone: "+1 345 929 0000",
    dob: "1971-11-05", nationality: "British",
    address_line1: "Harbour Place, 103 South Church St", city: "George Town", post_code: "KY1-1002", country: "Cayman Islands",
    tax_id: "",
    source_of_funds: "Investment Returns", annual_income: "1,800,000",
    pep_declaration: "no", sanctions_declaration: "no",
    id_type: "Passport", id_front_data: null, id_back_data: null, selfie_data: null,
    business_name: "Falcon Ridge Capital Ltd",
    reg_number: "CR-312245", business_tax_id: "",
    business_type: "Limited Company", incorporation_country: "Cayman Islands",
    registered_address: "Harbour Place, 103 South Church St, Grand Cayman KY1-1002",
    website: "https://falconridgecapital.ky",
    directors: ["Oliver Whitmore", "Sophia Reinholt", "James A. Pemberton III"],
    ubos: [
      { name: "Oliver Whitmore",       role: "Director & UBO", ownership: "45" },
      { name: "Sophia Reinholt",        role: "Director & UBO", ownership: "30" },
      { name: "James A. Pemberton III", role: "Director",       ownership: "10" },
    ],
    status: "pending",
    submitted_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    risk_score: null,
  },
  {
    id: "mock-004", type: "individual",
    first_name: "Dmitri", last_name: "Volkov",
    email: "d.volkov@privatbank.ru", phone: "+7 495 000 1234",
    dob: "1968-11-03", nationality: "Russian",
    address_line1: "Ulitsa Arbat 12", city: "Moscow", post_code: "119002", country: "Russia",
    tax_id: "7707123456",
    source_of_funds: "Investment Returns", annual_income: "3,200,000",
    pep_declaration: "yes", pep_details: "Former Deputy Minister of Energy, Russian Federation (2010–2018)",
    sanctions_declaration: "no",
    id_type: "National ID Card", id_front_data: null, id_back_data: null, selfie_data: null,
    status: "rejected",
    reviewed_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    review_note: "High-risk PEP from sanctioned jurisdiction. EDD cannot be completed to required standard. Application declined per policy.",
    submitted_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    risk_score: null, directors: [],
  },
  {
    id: "mock-005", type: "business",
    first_name: "Priya", last_name: "Nair",
    email: "priya@clearbridge.sg", phone: "+65 6123 4567",
    dob: "1983-07-22", nationality: "Singaporean",
    address_line1: "1 Raffles Place #40-02", city: "Singapore", post_code: "048616", country: "Singapore",
    tax_id: "201812345K",
    source_of_funds: "Business Income", annual_income: "650,000",
    pep_declaration: "no", sanctions_declaration: "no",
    id_type: "Passport", id_front_data: null, id_back_data: null, selfie_data: null,
    business_name: "ClearBridge Capital Pte Ltd",
    reg_number: "201812345K", business_tax_id: "201812345K",
    business_type: "Limited Company", incorporation_country: "Singapore",
    registered_address: "1 Raffles Place #40-02, Singapore 048616",
    website: "https://clearbridge.sg",
    directors: ["Priya Nair", "Marcus Tan"],
    ubos: [
      { name: "Priya Nair",  role: "Director & UBO", ownership: "60" },
      { name: "Marcus Tan",  role: "Director & UBO", ownership: "40" },
    ],
    status: "info_requested",
    reviewed_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    review_note: "Please provide certified copies of the Certificate of Incorporation and latest audited accounts.",
    submitted_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    risk_score: null,
  },
];

// ─── Inline portal wrapper (used inside dashboard modal) ─────────────────────
function KYCOnboardingPortalInline({ onComplete, companyId = null }) {
  const C = useTheme();
  return (
    <div style={{ background: C.bg, borderRadius: 12, padding: "24px 16px" }}>
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&display=swap" rel="stylesheet" />
      <KYCFormInner onComplete={onComplete} companyId={companyId} />
    </div>
  );
}

// Inner form logic extracted so it can be used both standalone and inline
function KYCFormInner({ onComplete, companyId = null }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  function updateData(key, value) { setData(prev => ({ ...prev, [key]: value })); }

  const isInd = data.type !== "business";
  const STEPS = isInd
    ? ["Type", "Personal", "Compliance", "Docs", "Selfie", "Review"]
    : ["Type", "Personal", "Business", "Compliance", "Docs", "Selfie", "Review"];

  function next() { setStep(s => Math.min(s + 1, STEPS.length - 1)); }
  function back() { setStep(s => Math.max(s - 1, 0)); }

  async function handleSubmit(termsAcceptedAt) {
    setSubmitting(true); setError(null);
    try {
      const record = {
        company_id: companyId,
        type: data.type, first_name: data.firstName, last_name: data.lastName,
        email: data.email, phone: data.phone, dob: data.dob, nationality: data.nationality,
        tax_id: data.taxId, address_line1: data.addressLine1, address_line2: data.addressLine2,
        city: data.city, post_code: data.postCode, country: data.country,
        source_of_funds: data.sourceOfFunds, source_of_funds_other: data.sourceOfFundsOther,
        annual_income: data.annualIncome, pep_declaration: data.pepDeclaration,
        pep_details: data.pepDetails, sanctions_declaration: data.sanctionsDeclaration,
        id_type: data.idType, id_front_name: data.idFront?.name, id_back_name: data.idBack?.name,
        job_title: data.jobTitle || null, employer_name: data.employerName || null,
        purpose_of_account: data.purposeOfAccount || null, wallet_id: data.walletId || null,
        business_name: data.businessName, trading_name: data.tradingName, reg_number: data.regNumber,
        business_tax_id: data.businessTaxId, business_type: data.businessType,
        incorporation_country: data.incorporationCountry, registered_address: data.registeredAddress,
        website: data.website,
        directors: JSON.stringify(data.directors || []),
        ubos: JSON.stringify(data.ubos || []),
        additional_docs: JSON.stringify((data.additionalDocs || []).map(d => ({ type: d.type, label: d.label, name: d.file?.name || null }))),
        status: "pending", submitted_at: new Date().toISOString(),
        terms_accepted_at: termsAcceptedAt || new Date().toISOString(),
      };
      console.log("[KYC-inline] Submitting…", { email: record.email });
      const { data: inserted, error: dbError } = await db
        .from("kyc_submissions")
        .insert(record)
        .execute();
      if (dbError) {
        console.error("[KYC-inline] Insert error:", dbError);
        throw new Error(dbError.message + (dbError.hint ? " — " + dbError.hint : "") + " (code: " + dbError.code + ")");
      }
      console.log("[KYC-inline] Inserted:", inserted?.id);

      // Store documents asynchronously (non-blocking)
      if (inserted?.id) {
        (async () => {
          try {
            const submissionId = inserted.id;
            const storageBucket = `kyc-submissions/${submissionId}`;
            const token = localStorage.getItem('amlguard_token');
            const docPaths = [];

            // Upload ID front
            if (data.idFront?.data) {
              const blob = new Blob([data.idFront.data], { type: 'image/jpeg' });
              const file = new File([blob], `id_front_${Date.now()}.jpg`, { type: 'image/jpeg' });
              await supabase.from(storageBucket).upload(`id_front.jpg`, file);
              docPaths.push({ name: 'ID Front', path: `${storageBucket}/id_front.jpg`, type: 'image' });
              console.log("[KYC-inline] ID front uploaded");
            }

            // Upload ID back
            if (data.idBack?.data) {
              const blob = new Blob([data.idBack.data], { type: 'image/jpeg' });
              const file = new File([blob], `id_back_${Date.now()}.jpg`, { type: 'image/jpeg' });
              await supabase.from(storageBucket).upload(`id_back.jpg`, file);
              docPaths.push({ name: 'ID Back', path: `${storageBucket}/id_back.jpg`, type: 'image' });
              console.log("[KYC-inline] ID back uploaded");
            }

            // Upload selfie
            if (data.selfie?.data || data.selfie) {
              const dataUrl = data.selfie?.data || data.selfie;
              const blob = new Blob([dataUrl], { type: 'image/jpeg' });
              const file = new File([blob], `selfie_${Date.now()}.jpg`, { type: 'image/jpeg' });
              await supabase.from(storageBucket).upload(`selfie.jpg`, file);
              docPaths.push({ name: 'Selfie', path: `${storageBucket}/selfie.jpg`, type: 'image' });
              console.log("[KYC-inline] Selfie uploaded");
            }

            // Upload additional documents
            if (data.additionalDocs && Array.isArray(data.additionalDocs)) {
              console.log("[KYC-inline] Additional docs to process:", data.additionalDocs.length);
              for (let i = 0; i < data.additionalDocs.length; i++) {
                const doc = data.additionalDocs[i];
                console.log(`[KYC-inline] Doc ${i}:`, { hasFile: !!doc.file, label: doc.label, name: doc.name });
                if (doc.file) {
                  const fileName = doc.file.name || `additional_doc_${i}.pdf`;
                  await supabase.from(storageBucket).upload(`docs/${fileName}`, doc.file);
                  docPaths.push({ name: doc.label || fileName, path: `${storageBucket}/docs/${fileName}`, type: 'document' });
                  console.log("[KYC-inline] Additional document uploaded:", fileName);
                }
              }
            }

            // Preserve previous document paths if no new docs were uploaded
            if (docPaths.length === 0 && previousDocPaths.length > 0) {
              docPaths.push(...previousDocPaths);
              console.log("[KYC-inline] Preserving previous document paths:", previousDocPaths.length);
            }

            // Update submission with document paths
            if (docPaths.length > 0 && token) {
              try {
                const resp = await fetch('/api/query?resource=kyc_submissions', {
                  method: 'PUT',
                  headers: getApiHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  }),
                  body: JSON.stringify({
                    id: submissionId,
                    additional_doc_paths: docPaths  // Send as JSON object, not stringified
                  }),
                });
                if (resp.ok) {
                  console.log("[KYC-inline] Document paths saved to database");
                } else {
                  const errorBody = await resp.json().catch(() => ({}));
                  console.warn("[KYC-inline] Failed to save document paths:", {
                    status: resp.status,
                    error: errorBody.error,
                    detail: errorBody.detail
                  });
                }
              } catch (err) {
                console.warn("[KYC-inline] Failed to update document paths:", err.message);
              }
            }

            console.log("[KYC-inline] All documents uploaded successfully");
          } catch (err) {
            console.warn("[KYC-inline] Document upload warning (non-blocking):", err.message);
            // Don't fail submission if documents can't be stored
          }
        })();
      }

      setSubmitted(true);
      setTimeout(() => onComplete?.(), 2000);
    } catch (e) { console.error("[KYC-inline] failed:", e); setError(e.message); }
    setSubmitting(false);
  }

  if (submitted) return (
    <div style={{ textAlign: "center", padding: "32px 0" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
      <div style={{ color: C.success, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700 }}>{t('onboarding.submittedSuccessfully')}</div>
      <div style={{ color: C.textMid, fontSize: 13, marginTop: 8 }}>{t('onboarding.closingInMoment')}</div>
    </div>
  );

  const stepComponents = isInd
    ? [
        <StepType data={data} onChange={updateData} onNext={next} />,
        <StepPersonal data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepCompliance data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepDocuments data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepSelfie data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepReview data={data} onSubmit={handleSubmit} onBack={back} submitting={submitting} />,
      ]
    : [
        <StepType data={data} onChange={updateData} onNext={next} />,
        <StepPersonal data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepBusiness data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepCompliance data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepDocuments data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepSelfie data={data} onChange={updateData} onNext={next} onBack={back} />,
        <StepReview data={data} onSubmit={handleSubmit} onBack={back} submitting={submitting} />,
      ];

  return (
    <div style={{ maxWidth: 600, margin: "0 auto" }}>
      <StepIndicator steps={STEPS} current={step} />
      {error && (
        <div style={{ background: `${C.danger}10`, border: `1px solid ${C.danger}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 18, color: C.danger, fontSize: 12 }}>
          ✗ {error}
        </div>
      )}
      {stepComponents[step]}

    </div>
  );
}

export function KYCReviewDashboard({ currentUser, company }) {
  const { t } = useTranslation();
  const C = useTheme();
  const [realSubmissions, setRealSubmissions] = useState(null); // null = not loaded yet
  const [dbError,         setDbError]         = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [selected,        setSelected]        = useState(null);
  const [filter,          setFilter]          = useState("all");
  const [actionNote,      setActionNote]      = useState("");
  const [actionRisk,      setActionRisk]      = useState("Medium");
  const [accountNumber,   setAccountNumber]   = useState("");
  const [infoRequest,     setInfoRequest]     = useState({ id_front: false, id_back: false, selfie: false, data: false, pep_declaration: false, sanctions_declaration: false, additional_docs: [], custom_doc: "", note: "" });
  const [sanctionsResults, setSanctionsResults] = useState({});
  const [sanctionsLoading, setSanctionsLoading] = useState({});
  const [serproResults,    setSerproResults]    = useState({});
  const [serproLoading,    setSerproLoading]    = useState({});
  const [serproModal,      setSerproModal]      = useState({ open: false, cpf: "", name: "", dob: "", submissionId: null });
  const [socureResults,    setSocureResults]    = useState({});
  const [socureLoading,    setSocureLoading]    = useState({});
  // API key is stored server-side, not in localStorage — dilisense button will work if backend has key configured
  const [dilisenseKey] = useState(true); // Just a flag to enable the button
  const [showInfoModal,   setShowInfoModal]   = useState(false);
  const [infoLink,        setInfoLink]        = useState(null);
  const [actioning,       setActioning]       = useState(false);
  const [auditLogs,       setAuditLogs]       = useState([]);
  const [selfieTab,       setSelfieTab]       = useState("compare");
  const [showForm,        setShowForm]        = useState(false);
  const [copied,          setCopied]          = useState(false);
  const [showSettings,    setShowSettings]  = useState(false);
  const [teamMembers,     setTeamMembers]   = useState([]);
  const [assignTo,        setAssignTo]      = useState("");
  const [assigning,       setAssigning]     = useState(false);
  const [aiAnalysis,      setAiAnalysis]    = useState({});     // keyed by submission id
  const [aiLoading,       setAiLoading]     = useState({});
  const [dataAnalysis,    setDataAnalysis]  = useState({});   // keyed by submission id
  const [docsAnalysis,    setDocsAnalysis]  = useState({});   // keyed by submission id
  const [apiSettings,     setApiSettings]   = useState(() => {
    try { return JSON.parse(localStorage.getItem("crg_api_settings") || "{}"); } catch { return {}; }
  });
  // External check states
  const [hibpResult,      setHibpResult]    = useState({});   // keyed by submission id
  const [hibpLoading,     setHibpLoading]   = useState({});
  const [phoneResult,     setPhoneResult]   = useState({});   // keyed by submission id
  const [phoneLoading,    setPhoneLoading]  = useState({});
  const [ipResult,        setIpResult]      = useState({});   // keyed by submission id
  const [ipLoading,       setIpLoading]     = useState({});
  const { log: auditLog } = useAuditLog(company, currentUser);
  // E-015 / F-009: Watchlist hits for selected submission
  const [watchlistHits, setWatchlistHits] = useState([]);
  // E-014 / F-003: Beneficial ownership attestation — tracks which UBO indices the reviewer has attested
  const [uboAttestation, setUboAttestation] = useState({});

  // Reset attestation when switching submissions
  useEffect(() => { setUboAttestation({}); }, [selected?.id]);

  // E-015: Check watchlist whenever a submission is selected
  useEffect(() => {
    if (!selected || !company?.id) { setWatchlistHits([]); return; }
    const namesToCheck = [selected.full_name || selected.name];
    // Also check beneficial owners if business
    try {
      const ubos = Array.isArray(selected.ubos) ? selected.ubos : JSON.parse(selected.ubos || "[]");
      ubos.forEach(u => u.name && namesToCheck.push(u.name));
    } catch {}
    try {
      db?.from("internal_watchlist")
        ?.select("full_name, list_type, reason")
        ?.eq("company_id", company.id)
        ?.execute()
        .then(({ data }) => {
          if (!data) return;
          const hits = data.filter(entry =>
            namesToCheck.some(n => n && entry.full_name.toLowerCase() === n.toLowerCase())
        );
          setWatchlistHits(hits);
        })
        .catch(err => console.warn("[KYC] Watchlist check failed:", err));
    } catch (err) {
      console.warn("[KYC] Watchlist error:", err);
    }
  }, [selected?.id, company?.id]);

  // Show real submissions when loaded, otherwise show mocks as placeholders
  const submissions = realSubmissions !== null
    ? [...realSubmissions, ...MOCK_SUBMISSIONS.filter(m => !realSubmissions.find(r => r.id === m.id))]
    : MOCK_SUBMISSIONS;

  function setSubmissions(fn) {
    setRealSubmissions(prev => typeof fn === "function" ? fn(prev ?? []) : fn);
  }

  const portalUrl = company?.id
    ? `${window.location.origin}/app?kyc=1&company=${company.id}`
    : `${window.location.origin}/app?kyc=1`;

  function copyLink() {
    navigator.clipboard.writeText(portalUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  async function loadSubmissions() {
    setLoading(true);
    setDbError(null);
    try {
      const token = localStorage.getItem('amlguard_token');

      // Load submissions from API
      const submissionsRes = await fetch(`/api/query?resource=kyc_submissions`, {
        headers: getApiHeaders({
          'Authorization': `Bearer ${token}`,
        })
      });

      if (!submissionsRes.ok) {
        throw new Error(`Failed to load submissions: ${submissionsRes.status}`);
      }

      const submissions = await submissionsRes.json();

      // Load documents for each submission
      const docsRes = await fetch(`/api/query?resource=kyc_documents`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      const documents = docsRes.ok ? await docsRes.json() : [];

      // Attach documents to submissions
      const submissionsWithDocs = submissions.map(sub => {
        const subDocs = documents.filter(d => d.submission_id === sub.id);
        return {
          ...sub,
          id_front_data: subDocs.find(d => d.document_type === 'idFront')?.file_data,
          id_back_data: subDocs.find(d => d.document_type === 'idBack')?.file_data,
          selfie_data: subDocs.find(d => d.document_type === 'selfie')?.file_data,
        };
      });

      setRealSubmissions(submissionsWithDocs);

      // Restore any previously saved AI analysis results into state
      const ai = {}, dataA = {}, docs = {};
      submissionsWithDocs.forEach(sub => {
        if (sub.ai_analysis)   ai[sub.id]    = sub.ai_analysis;
        if (sub.data_analysis) dataA[sub.id] = sub.data_analysis;
        if (sub.docs_analysis) docs[sub.id]  = sub.docs_analysis;
      });
      if (Object.keys(ai).length)    setAiAnalysis(ai);
      if (Object.keys(dataA).length) setDataAnalysis(dataA);
      if (Object.keys(docs).length)  setDocsAnalysis(docs);
    } catch (e) {
      setDbError(`Connection error: ${e.message}`);
    }
    setLoading(false);
  }

  function selectSubmission(sub) {
    setSelected(sub);
    // Restore analysis from the submission record if not already in state
    if (sub.ai_analysis   && !aiAnalysis[sub.id])   setAiAnalysis(p   => ({...p, [sub.id]: sub.ai_analysis}));
    if (sub.data_analysis && !dataAnalysis[sub.id]) setDataAnalysis(p => ({...p, [sub.id]: sub.data_analysis}));
    if (sub.docs_analysis && !docsAnalysis[sub.id]) setDocsAnalysis(p => ({...p, [sub.id]: sub.docs_analysis}));
    if (sub.sanctions_result && !sanctionsResults[sub.id]) setSanctionsResults(p => ({...p, [sub.id]: sub.sanctions_result}));
  }

  async function loadAuditLogs(submissionId) {
    if (submissionId?.startsWith("mock-")) { setAuditLogs([]); return; }
    try {
      const { data } = await db
        .from("kyc_audit_logs")
        .select("*")
        .eq("submission_id", submissionId)
        .order("created_at", true)
        .execute();
      setAuditLogs(data || []);
    } catch { setAuditLogs([]); }
  }

  function saveApiSettings(updated) {
    setApiSettings(updated);
    try { localStorage.setItem("crg_api_settings", JSON.stringify(updated)); } catch {}
  }

  // ── Shared waterfall helpers ─────────────────────────────────────────────
  async function callAnthropicWithImages(contentBlocks) {
    // Send blocks to AI with vision support
    // The proxy will handle vision-capable models (Claude 3.5 Sonnet, Gemini 2.0 Flash, etc.)
    const { callAI } = await import("./aiClient");
    try {
      return await callAI({
        system: "You are a KYC compliance analyst. Return only valid JSON — no markdown, no preamble.",
        messages: [{ role: "user", content: contentBlocks }],
        maxTokens: 1000
      });
    } catch (e) {
      console.error("[KYC] Vision API failed, falling back to text-only:", e.message);
      // Fallback to text-only if vision fails
      const textBlocks = contentBlocks.filter(b => b.type === "text").map(b => b.text).join("\n\n");
      return callTextWaterfall(textBlocks);
    }
  }

  async function callTextWaterfall(prompt) {
    const { callAI } = await import("./aiClient");
    return callAI({ system: "You are a KYC compliance analyst. Return only valid JSON — no markdown, no preamble.", messages: [{ role: "user", content: prompt }], maxTokens: 1000 });
  }

  // ── External Intelligence Checks ─────────────────────────────────────────
  async function checkHibpEmail(submissionId, email) {
    if (!email) { console.warn("[HIBP] No email provided"); return; }
    setHibpLoading(p => ({ ...p, [submissionId]: true }));
    try {
      const response = await fetch(`/api/hibp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      setHibpResult(p => ({ ...p, [submissionId]: data }));
    } catch (error) {
      console.error("[HIBP] Check failed:", error);
      setHibpResult(p => ({ ...p, [submissionId]: { error: error.message } }));
    } finally {
      setHibpLoading(p => ({ ...p, [submissionId]: false }));
    }
  }

  async function checkPhone(submissionId, phone, country = "US") {
    if (!phone) { console.warn("[Phone] No phone provided"); return; }
    setPhoneLoading(p => ({ ...p, [submissionId]: true }));
    try {
      const response = await fetch(`/api/phonelookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, country })
      });
      const data = await response.json();
      setPhoneResult(p => ({ ...p, [submissionId]: data }));
    } catch (error) {
      console.error("[Phone] Check failed:", error);
      setPhoneResult(p => ({ ...p, [submissionId]: { error: error.message } }));
    } finally {
      setPhoneLoading(p => ({ ...p, [submissionId]: false }));
    }
  }

  async function checkIp(submissionId, ip) {
    if (!ip) { console.warn("[IP] No IP provided"); return; }
    setIpLoading(p => ({ ...p, [submissionId]: true }));
    try {
      const response = await fetch(`/api/checks/ip?ip=${encodeURIComponent(ip)}`);
      const data = await response.json();
      setIpResult(p => ({ ...p, [submissionId]: data }));
    } catch (error) {
      console.error("[IP] Check failed:", error);
      setIpResult(p => ({ ...p, [submissionId]: { error: error.message } }));
    } finally {
      setIpLoading(p => ({ ...p, [submissionId]: false }));
    }
  }

  async function updateRiskScore(submissionId, newScore) {
    try {
      const token = localStorage.getItem('amlguard_token');

      console.log('[RiskScore] Attempting update:', { submissionId, newScore, hasToken: !!token });

      const response = await fetch(`/api/query?resource=kyc_submissions`, {
        method: 'PUT',
        headers: getApiHeaders({
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }),
        body: JSON.stringify({ id: submissionId, risk_score: parseInt(newScore) })
      });

      const responseData = await response.json();

      console.log('[RiskScore] Response:', { status: response.status, data: responseData });

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to update risk score');
      }

      console.log('[RiskScore] Update successful:', responseData);

      // Update local state in submissions list
      setSubmissions(prev => prev.map(s => s.id === submissionId ? { ...s, risk_score: parseInt(newScore) } : s));

      // Also update the currently selected submission to refresh the UI
      if (selected && selected.id === submissionId) {
        setSelected(prev => ({ ...prev, risk_score: parseInt(newScore) }));
      }

      return true;
    } catch (error) {
      console.error("[RiskScore] Update failed:", error);
      alert(`Risk Score Update Failed: ${error.message}`);
      return false;
    }
  }

  // mode: "full" = ID + selfie + data  |  "data" = data only  |  "docs" = additional docs
  async function runAiAnalysis(sub, mode = "full") {
    const id      = sub.id;
    const loadKey = mode === "docs" ? `${id}_docs` : mode === "data" ? `${id}_data` : id;
    if (aiLoading[loadKey]) return;
    setAiLoading(p => ({ ...p, [loadKey]: true }));

    const JSON_SCHEMA = `Return ONLY a JSON object (no markdown, no preamble) with this structure:
{
  "overall": "PASS" | "REVIEW" | "FAIL",
  "confidence": 0-100,
  "id_authenticity": { "verdict": "GENUINE"|"SUSPECT"|"INCONCLUSIVE"|"NOT_PROVIDED", "score": 0-100, "findings": ["..."] },
  "face_match":      { "verdict": "MATCH"|"MISMATCH"|"INCONCLUSIVE"|"NOT_PROVIDED",   "score": 0-100, "findings": ["..."] },
  "data_consistency":{ "verdict": "CONSISTENT"|"INCONSISTENT"|"INCONCLUSIVE",          "score": 0-100, "findings": ["..."] },
  "red_flags": ["..."],
  "recommendations": ["..."],
  "summary": "2-3 sentence plain-English summary for the compliance reviewer"
}`;

    const applicantInfo = `Applicant info:
- Name: ${sub.first_name} ${sub.last_name}
- DOB: ${sub.dob || "not provided"}
- Nationality: ${sub.nationality || "not provided"}
- ID type declared: ${sub.id_type || "not provided"}
- Source of funds: ${sub.source_of_funds || "not provided"}
- PEP declaration: ${sub.pep_declaration || "not provided"}
- Sanctions declaration: ${sub.sanctions_declaration || "not provided"}
- Address: ${[sub.address_line1, sub.city, sub.country].filter(Boolean).join(", ") || "not provided"}
${sub.type === "business" ? `- Business: ${sub.business_name || ""} (${sub.business_type || ""}, ${sub.incorporation_country || ""})` : ""}`;

    const addImageBlocks = async (label, dataUrl) => {
      if (!dataUrl) {
        console.warn(`[KYC] ${label}: no data provided`);
        return [];
      }
      if (dataUrl.startsWith("data:image/svg")) {
        console.warn(`[KYC] ${label}: SVG placeholder detected`);
        return [];
      }

      // Compress large images before sending to AI (reduce payload size)
      if (dataUrl.startsWith("data:image/") && dataUrl.length > 1000000) {
        console.log(`[KYC] ${label}: compressing large image (${Math.round(dataUrl.length / 1024 / 1024)}MB)`);
        try {
          dataUrl = await compressImage(dataUrl, 512, 512, 0.6);
          console.log(`[KYC] ${label}: compressed to ${Math.round(dataUrl.length / 1024)}KB`);
        } catch (e) {
          console.warn(`[KYC] ${label}: compression failed, using original:`, e.message);
        }
      }

      // PDFs can't be sent as images — send as document block for Anthropic or text note for Gemini
      if (dataUrl.startsWith("data:application/pdf") || (dataUrl.startsWith("http") && dataUrl.includes(".pdf"))) {
        const b64 = dataUrl.startsWith("data:") ? dataUrl.split(",")[1] : null;
        if (b64) return [{ type: "document", source: { type: "base64", media_type: "application/pdf", data: b64 } }];
        return [{ type: "text", text: `[${label}: PDF document uploaded but cannot be displayed inline]` }];
      }

      // If it's a signed URL (https://) — fetch and convert to base64 for AI
      if (dataUrl.startsWith("http")) {
        try {
          const r = await fetch(dataUrl);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const blob = await r.blob();
          const mediaType = blob.type?.split(";")[0] || "image/jpeg";
          // Handle PDFs from storage
          if (mediaType === "application/pdf") {
            const b64 = await new Promise(resolve => { const reader = new FileReader(); reader.onloadend = () => resolve(reader.result.split(",")[1]); reader.readAsDataURL(blob); });
            return [{ type: "document", source: { type: "base64", media_type: "application/pdf", data: b64 } }];
          }
          if (!["image/jpeg","image/png","image/gif","image/webp"].includes(mediaType)) {
            return [{ type: "text", text: `[${label}: file type ${mediaType} cannot be displayed]` }];
          }
          const b64 = await new Promise(resolve => { const reader = new FileReader(); reader.onloadend = () => resolve(reader.result.split(",")[1]); reader.readAsDataURL(blob); });
          console.log(`[KYC] ${label}: fetched from URL, size=${b64?.length || 0}`);
          return [{ type: "text", text: `[${label}]` }, { type: "image", source: { type: "base64", media_type: mediaType, data: b64 } }];
        } catch (e) {
          console.warn("[KYC] Failed to fetch signed URL:", label, e.message);
          return [{ type: "text", text: `[${label}: document exists but could not be loaded — ${e.message}]` }];
        }
      }

      // Otherwise it's a data URL
      try {
        // Split on FIRST comma only to handle base64 that may contain commas
        const commaIdx = dataUrl.indexOf(",");
        if (commaIdx === -1) {
          console.warn(`[KYC] ${label}: invalid data URL (no comma found)`);
          return [];
        }
        const meta = dataUrl.substring(0, commaIdx);
        const b64 = dataUrl.substring(commaIdx + 1);

        const mimeMatch = meta.match(/data:([^;]+);/);
        const mediaType = mimeMatch ? mimeMatch[1] : "image/jpeg";
        if (!["image/jpeg","image/png","image/gif","image/webp"].includes(mediaType)) {
          console.warn(`[KYC] ${label}: unsupported media type ${mediaType}`);
          return [];
        }
        console.log(`[KYC] ${label}: data URL, size=${b64?.length || 0}, type=${mediaType}`);
        if (!b64 || b64.length === 0) {
          console.warn(`[KYC] ${label}: base64 data is empty!`);
          return [];
        }
        return [{ type: "text", text: `[${label}]` }, { type: "image", source: { type: "base64", media_type: mediaType, data: b64 } }];
      } catch(e) {
        console.error(`[KYC] ${label}: error processing data URL:`, e.message);
        return [];
      }
    };

    try {
      let raw = "";

      // ── Serpro verification (Brazilian government data) ─────────────────────
      let serproData = "";
      try {
        console.log("[KYC] Running Serpro verification...");

        // Always check individual CPF if available
        if (sub.tax_id) {
          console.log("[KYC] Serpro: Looking up CPF", sub.tax_id);
          const cpfRes = await fetch('/api/serpro', {
            method: 'POST',
            headers: getApiHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('amlguard_token')}` }),
            body: JSON.stringify({ action: 'cpf', data: { cpf: sub.tax_id } })
          });
          if (cpfRes.ok) {
            const cpfData = await cpfRes.json();
            serproData += `\n- Serpro CPF: ${cpfData.status || 'unknown status'} (${cpfData.name || 'name not found'})`;
            console.log("[KYC] Serpro CPF result:", { status: cpfData.status, name: cpfData.name });
          } else {
            console.warn("[KYC] Serpro CPF lookup failed:", cpfRes.status);
          }
        }

        // Always check business CNPJ if available
        if (sub.reg_number) {
          console.log("[KYC] Serpro: Looking up CNPJ", sub.reg_number);
          const cnpjRes = await fetch('/api/serpro', {
            method: 'POST',
            headers: getApiHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('amlguard_token')}` }),
            body: JSON.stringify({ action: 'cnpj', data: { cnpj: sub.reg_number } })
          });
          if (cnpjRes.ok) {
            const cnpjData = await cnpjRes.json();
            serproData += `\n- Serpro CNPJ: ${cnpjData.status || 'unknown status'} (${cnpjData.company_name || 'name not found'})`;
            console.log("[KYC] Serpro CNPJ result:", { status: cnpjData.status, name: cnpjData.company_name });
          } else {
            console.warn("[KYC] Serpro CNPJ lookup failed:", cnpjRes.status);
          }
        }

        if (serproData) {
          console.log("[KYC] Serpro verification complete");
        }
      } catch (e) {
        console.warn("[KYC] Serpro verification skipped:", e.message);
      }

      if (mode === "data") {
        // ── Data-only: no images, use full provider waterfall ──────────────
        const prompt = `You are a KYC compliance analyst. Assess the following applicant based on their submitted data only — no document images are available.

${applicantInfo}${serproData}

Evaluate data consistency, flag PEP/sanctions/high-risk indicators, and assess plausibility.
Set id_authenticity and face_match verdict to "NOT_PROVIDED" since no documents were submitted.

${JSON_SCHEMA}`;
        raw = await callTextWaterfall(prompt);

      } else if (mode === "docs") {
        let resolvedDocs = sub.additional_docs_resolved || [];

        // Build list of all paths needing signed URLs (skip data URIs)
        // additional_doc_paths is the primary source; additional_docs may also have path entries
        const allPaths = [
          ...ensureArray(sub.additional_doc_paths).filter(d => d.path && !d.path.startsWith('data:')),
          ...ensureArray(sub.additional_docs).filter(d => d.path && !d.path.startsWith('data:') && !ensureArray(sub.additional_doc_paths).some(p => p.path === d.path)),
        ];

        // Fetch signed URLs on-demand if not yet resolved
        if (resolvedDocs.length === 0 && allPaths.length > 0) {
          console.log("[KYC] Fetching signed URLs on-demand for", allPaths.length, "docs");
          resolvedDocs = await Promise.all(allPaths.map(async doc => {
            if (!doc.path || doc.path.startsWith('data:')) return null;
            const { data: urlData, error } = await supabase.from("kyc-documents").createSignedUrl(doc.path, 300);
            if (error) { console.warn("[KYC] Signed URL error for", doc.path, error.message); return null; }
            return { ...doc, signedUrl: urlData?.signedUrl };
          }));
          resolvedDocs = resolvedDocs.filter(Boolean);
          if (resolvedDocs.length > 0) setSelected(prev => ({ ...prev, additional_docs_resolved: resolvedDocs }));
        }

        // Legacy: base64 data stored directly (old submissions)
        const legacyDocs = ensureArray(sub.additional_docs).filter(d => d.data && !d.data.startsWith("data:image/svg"));
        const docs = resolvedDocs.length > 0 ? resolvedDocs : legacyDocs;

        // If no supporting documents, skip this analysis (it's optional)
        if (!docs.length) {
          console.log("[KYC] No supporting documents found — skipping document analysis");
          return; // Return early instead of throwing error
        }
        const blocks = [{ type: "text", text: `You are a KYC compliance analyst. Review the following supporting documents uploaded by the applicant.\n\n${applicantInfo}${serproData}` }];
        for (const doc of docs) { blocks.push(...await addImageBlocks(doc.label || doc.type, doc.signedUrl || doc.data)); }
        blocks.push({ type: "text", text: `Analyse each document for authenticity and relevance. id_authenticity covers these supporting docs. face_match should be NOT_PROVIDED unless a selfie is included.\n\n${JSON_SCHEMA}` });
        raw = await callAnthropicWithImages(blocks);

      } else {
        // ── Full: ID front/back + selfie + data ────────────────────────────
        const blocks = [{ type: "text", text: `You are a KYC compliance analyst. Analyse the following documents for a ${sub.type} applicant named ${sub.first_name} ${sub.last_name}.` }];

        // Log what we're working with
        console.log("[KYC Analysis] Image data status:", {
          hasIdFront: !!sub.id_front_data,
          idFrontLength: sub.id_front_data?.length || 0,
          hasIdBack: !!sub.id_back_data,
          idBackLength: sub.id_back_data?.length || 0,
          hasSelfie: !!sub.selfie_data,
          selfieLength: sub.selfie_data?.length || 0,
        });

        blocks.push(...await addImageBlocks("Government-issued ID (front)", sub.id_front_data));
        blocks.push(...await addImageBlocks("Government-issued ID (back)",  sub.id_back_data));
        blocks.push(...await addImageBlocks("Live selfie",                  sub.selfie_data));

        const hasImgs = blocks.some(b => b.type === "image");
        console.log("[KYC Analysis] Blocks prepared:", {
          totalBlocks: blocks.length,
          imageBlocks: blocks.filter(b => b.type === "image").length,
          textBlocks: blocks.filter(b => b.type === "text").length,
          blockTypes: blocks.map((b, i) => `[${i}]${b.type}${b.type === "image" ? `(${b.source?.media_type})` : ""}`),
        });

        blocks.push({ type: "text", text: `\n${applicantInfo}${serproData}\n\n${hasImgs ? "Analyse ALL provided images carefully." : "No document images — assess from data only."}\n\n${JSON_SCHEMA}` });
        if (hasImgs) raw = await callAnthropicWithImages(blocks);
        else         raw = await callTextWaterfall(blocks.filter(b => b.type === "text").map(b => b.text).join("\n\n"));
      }

      if (!raw) throw new Error("Empty response from AI");

      // Don't treat insufficient_quota as fatal — the waterfall may have tried other providers
      // Only throw error if it's explicitly a non-JSON error response
      const isJsonError = raw.includes('"error"') || raw.includes('"message"');
      if ((raw.includes("API Error") || raw.includes("error")) && !isJsonError && raw.length < 100) {
        throw new Error(`AI Error: ${raw.substring(0, 150)}`);
      }
      if (raw.length < 20) throw new Error(`Invalid AI response: ${raw.substring(0, 100)}`);

      let clean = raw.replace(/```json|```/g, "").trim();
      // Repair truncated JSON by finding last complete field
      if (!clean.endsWith("}")) {
        const lastBrace = clean.lastIndexOf("}");
        if (lastBrace > 0) clean = clean.slice(0, lastBrace + 1);
        // Close any open objects
        const opens = (clean.match(/{/g) || []).length;
        const closes = (clean.match(/}/g) || []).length;
        for (let i = 0; i < opens - closes; i++) clean += "}";
      }

      let result;
      try {
        result = JSON.parse(clean);
      } catch (parseErr) {
        console.error("[KYC] JSON parse error. Raw response:", raw.substring(0, 200));
        console.error("[KYC] Cleaned response:", clean.substring(0, 200));

        // Check if the error is due to insufficient credits on all providers
        if (raw.includes("insufficient_quota") || raw.includes("credit balance")) {
          throw new Error("All AI providers are temporarily unavailable (insufficient credits). Please try again later or configure additional API keys.");
        }
        throw new Error(`AI response not JSON: ${parseErr.message}`);
      }

      if (mode === "docs")      setDocsAnalysis(p => ({ ...p, [id]: result }));
      else if (mode === "data") setDataAnalysis(p => ({ ...p, [id]: result }));
      else                      setAiAnalysis(p => ({ ...p, [id]: result }));

      // Persist result to database so it survives page navigation
      if (!id?.startsWith("mock-")) {
        try {
          const colMap = { docs: "docs_analysis", data: "data_analysis" };
          const col = colMap[mode] || "ai_analysis";
          const token = localStorage.getItem('amlguard_token');

          // Log what we're saving
          console.log(`[KYC] Saving ${mode} analysis:`, {
            id,
            resultSize: JSON.stringify(result).length,
            resultKeys: typeof result === 'object' ? Object.keys(result) : 'not-object'
          });

          const updatePayload = {
            id: id,
            [col]: result,
            [`${col}_at`]: new Date().toISOString()
          };

          console.log(`[KYC] Sending PUT to /api/query?resource=kyc_submissions with payload size:`, JSON.stringify(updatePayload).length);

          const resp = await fetch('/api/query?resource=kyc_submissions', {
            method: 'PUT',
            headers: getApiHeaders({
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            }),
            body: JSON.stringify(updatePayload),
          });

          if (!resp.ok) {
            const errText = await resp.text();
            console.warn(`[KYC] Failed to save ${mode} analysis:`, resp.status, errText);
          } else {
            console.log(`[KYC] ${mode} analysis saved successfully`);
          }
        } catch(e) {
          console.warn("[KYC] Could not save analysis result:", e.message);
        }
      }

      if (mode !== "docs" && apiSettings.webhookUrl && result.overall === "PASS") {
        pushToCustomerApi(sub, result, apiSettings);
      }
    } catch (e) {
      console.error("[KYC] Analysis error:", e);
      const err = { error: e.message };
      if (mode === "docs")      setDocsAnalysis(p => ({ ...p, [id]: err }));
      else if (mode === "data") setDataAnalysis(p => ({ ...p, [id]: err }));
      else                      setAiAnalysis(p => ({ ...p, [id]: err }));
    } finally {
      setAiLoading(p => ({ ...p, [loadKey]: false }));
    }
  }

  async function pushToCustomerApi(sub, aiResult, settings) {
    if (!settings.webhookUrl) return;
    try {
      const payload = {
        event: "kyc_submission",
        submission_id: sub.id,
        submitted_at: sub.submitted_at,
        applicant: {
          type: sub.type,
          first_name: sub.first_name,
          last_name: sub.last_name,
          email: sub.email,
          phone: sub.phone,
          dob: sub.dob,
          nationality: sub.nationality,
          country: sub.country,
          ...(sub.type === "business" ? {
            business_name: sub.business_name,
            reg_number: sub.reg_number,
            business_type: sub.business_type,
            incorporation_country: sub.incorporation_country,
          } : {}),
        },
        compliance: {
          source_of_funds: sub.source_of_funds,
          pep_declaration: sub.pep_declaration,
          sanctions_declaration: sub.sanctions_declaration,
        },
        ai_analysis: aiResult,
        status: sub.status,
      };
      const headers = { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" };
      if (settings.apiKey) headers[settings.apiKeyHeader || "Authorization"] = settings.apiKeyPrefix ? `${settings.apiKeyPrefix} ${settings.apiKey}` : settings.apiKey;
      await fetch(settings.webhookUrl, { method: "POST", headers, body: JSON.stringify(payload) });
    } catch (e) {
      console.warn("[CRG] API push failed:", e.message);
    }
  }

  // Load team members for assign-to-analyst feature
  useEffect(() => {
    if (!company?.id) return;
    try {
      db?.from("company_members")?.select("id, email, role, full_name")
        ?.eq("company_id", company.id)
        ?.execute()
        .then(({ data }) => setTeamMembers(data || []))
        .catch(err => console.warn("[KYC] Team members fetch failed:", err));
    } catch (err) {
      console.warn("[KYC] Team members error:", err);
    }
  }, [company?.id]);

  async function handleAssign() {
    if (!selected || !assignTo || assigning) return;
    setAssigning(true);
    try {
      const analyst = teamMembers.find(m => m.id === assignTo);
      const now = new Date().toISOString();
      if (!selected.id?.startsWith("mock-")) {
        try { await db.from("kyc_submissions").update({ assigned_to: assignTo, assigned_at: now }).eq("id", selected.id).execute(); } catch (e) { console.warn("[KYC] Assignment update failed:", e); }
        const analystName = analyst?.full_name || analyst?.email || "Analyst";
        const subjectName = [selected.first_name, selected.last_name].filter(Boolean).join(" ") || selected.business_name || "Applicant";
        try { await db.from("assignments").insert({ company_id: company?.id, assigned_to: assignTo, kyc_id: selected.id, status: "open", assigned_by: currentUser?.id }); } catch (e) { console.warn("[KYC] Assignment creation failed:", e); }
        try { await auditLog({ action: "kyc_assigned", module: "kyc", entityId: selected.id, entityName: subjectName, newValue: { assigned_to: assignTo, analyst: analystName } }); } catch (e) { console.warn("[KYC] Audit log failed:", e); }
        try { await db.from("notifications").insert({ company_id: company?.id, user_id: assignTo, type: "kyc_assigned", title: `KYC Review assigned: ${subjectName}`, body: `You have been assigned a KYC submission to review for ${subjectName}.`, link_module: "onboarding", read: false }); } catch (e) { console.warn("[KYC] Notification creation failed:", e); }
        try { await db.from("kyc_audit_logs").insert({ company_id: company?.id, submission_id: selected.id, action: "assigned", note: `Assigned to ${analystName}`, reviewer_id: currentUser?.id }); } catch (e) { console.warn("[KYC] Audit logs creation failed:", e); }
        await loadAuditLogs(selected.id);
      }
      setSelected(prev => ({ ...prev, assigned_to: assignTo, assigned_at: now }));
      setSubmissions(prev => prev.map(s => s.id === selected.id ? { ...s, assigned_to: assignTo } : s));
      setAssignTo("");
    } catch (err) { console.error("[handleAssign]", err); }
    setAssigning(false);
  }

  useEffect(() => { loadSubmissions(); }, []);
  useEffect(() => { if (selected) loadAuditLogs(selected.id); }, [selected?.id]);

  // Helper: fetch signed URLs for a submission and return enriched sub object
  async function getSignedUrls(sub) {
    const paths = {
      id_front_data: sub.id_front_data,
      id_back_data:  sub.id_back_data,
      selfie_data:   sub.selfie_data,
    };
    const updates = {};
    for (const [key, path] of Object.entries(paths)) {
      // Skip data URIs and empty paths
      if (!path || path.startsWith('data:')) continue;
      const { data, error } = await supabase
        .from("kyc-documents")
        .createSignedUrl(path, 300);
      if (!error && data?.signedUrl) updates[key] = data.signedUrl;
    }
    const addDocs = ensureArray(sub.additional_doc_paths);
    if (addDocs.length > 0) {
      const resolvedDocs = await Promise.all(addDocs.map(async doc => {
        if (!doc.path || doc.path.startsWith('data:')) return doc; // Skip data URIs
        const { data, error } = await supabase.from("kyc-documents").createSignedUrl(doc.path, 300);
        return { ...doc, signedUrl: error ? null : data?.signedUrl };
      }));
      updates.additional_docs_resolved = resolvedDocs;
    }
    return { ...sub, ...updates };
  }

  // Auto-run all analyses for new pending submissions on load (background, sequential)
  useEffect(() => {
    if (!realSubmissions) return;
    const needsWork = realSubmissions.filter(s =>
      !s.id?.startsWith("mock-") &&
      s.status === "pending" &&
      (!s.ai_analysis || !s.data_analysis || !s.docs_analysis || !s.sanctions_result)
    ).slice(0, 8); // cap to avoid overloading
    if (needsWork.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const sub of needsWork) {
        if (cancelled) break;
        // 1. Data analysis — no images needed
        if (!sub.data_analysis) {
          await runAiAnalysis(sub, "data");
          if (cancelled) break;
          await new Promise(r => setTimeout(r, 600));
        }
        // 2. Sanctions screening — no images needed
        if (!sub.sanctions_result && dilisenseKey) {
          await runSanctionsCheck(sub);
          if (cancelled) break;
          await new Promise(r => setTimeout(r, 600));
        }
        // 3. Get signed URLs so document & full analyses can run
        const hasDocPaths = sub.id_front_data || sub.id_back_data || sub.selfie_data;
        if (hasDocPaths) {
          const enriched = await getSignedUrls(sub);
          if (cancelled) break;
          // 4. Full analysis — identity verification + selfie comparison
          if (!sub.ai_analysis) {
            await runAiAnalysis(enriched, "full");
            if (cancelled) break;
            await new Promise(r => setTimeout(r, 600));
          }
          // 5. Document analysis
          if (!sub.docs_analysis) {
            await runAiAnalysis(enriched, "docs");
            if (cancelled) break;
          }
        }
        await new Promise(r => setTimeout(r, 800));
      }
    })();
    return () => { cancelled = true; };
  }, [realSubmissions]);

  // Fetch signed URLs when a submission is opened, fill any gaps in analyses
  useEffect(() => {
    if (!selected || selected.id?.startsWith("mock-")) return;
    async function fetchAndAnalyze() {
      const enriched = await getSignedUrls(selected);
      const updates = {};
      if (enriched.id_front_data) updates.id_front_data = enriched.id_front_data;
      if (enriched.id_back_data)  updates.id_back_data  = enriched.id_back_data;
      if (enriched.selfie_data)   updates.selfie_data   = enriched.selfie_data;
      if (enriched.additional_docs_resolved) updates.additional_docs_resolved = enriched.additional_docs_resolved;
      if (Object.keys(updates).length > 0) setSelected(prev => ({ ...prev, ...updates }));

      const hasDocPaths = selected.id_front_data || selected.id_back_data || selected.selfie_data;
      // Fill any analysis that wasn't pre-computed yet
      // Temporarily disabled fire-and-forget calls - they're causing unhandled promise rejections
      // if (!selected.data_analysis)                    runAiAnalysis(selected, "data").catch(e => console.warn("[KYC] Data analysis failed:", e));
      // if (!selected.sanctions_result && dilisenseKey) runSanctionsCheck(selected).catch(e => console.warn("[KYC] Sanctions check failed:", e));
      // if (hasDocPaths) {
      //   if (!selected.ai_analysis)   runAiAnalysis(enriched, "full").catch(e => console.warn("[KYC] Full analysis failed:", e));
      //   if (!selected.docs_analysis) runAiAnalysis(enriched, "docs").catch(e => console.warn("[KYC] Docs analysis failed:", e));
      // }
    }
    fetchAndAnalyze();
  }, [selected?.id]);

  async function runSanctionsCheck(sub) {
    const id = sub.id;
    if (sanctionsLoading[id]) return;
    setSanctionsLoading(p => ({ ...p, [id]: true }));
    setSanctionsResults(p => ({ ...p, [id]: null }));
    try {
      const indivName = `${sub.first_name} ${sub.last_name}`;
      console.log(`[KYC] Running sanctions check for: ${indivName}`);
      const indivData = await dilisenseCheck(indivName, "Individual");
      console.log(`[KYC] Individual sanctions check result:`, indivData);
      const indivResults = parseSanctionsResults(indivData);
      let bizResults = null;
      if (sub.type === "business" && sub.business_name) {
        console.log(`[KYC] Running sanctions check for business: ${sub.business_name}`);
        const bizData = await dilisenseCheck(sub.business_name, "Business");
        console.log(`[KYC] Business sanctions check result:`, bizData);
        bizResults = parseSanctionsResults(bizData);
      }
      const combined = {
        individual: { name: indivName, ...indivResults },
        business:   bizResults ? { name: sub.business_name, ...bizResults } : null,
        overallClear: indivResults.totalHits === 0 && (!bizResults || bizResults.totalHits === 0),
        screenedAt: new Date().toISOString(),
      };
      setSanctionsResults(p => ({ ...p, [id]: combined }));
      if (company?.id && !id.startsWith("mock-")) {
        // Optional: save to database if tables exist
        try {
          const overallResult = combined.overallClear ? "clear" : indivResults.sanctions.length > 0 ? "confirmed_match" : "potential_match";

          // Serialize complex data to ensure it's JSON-compatible
          const matchDetail = {
            sanctions: (indivResults.sanctions || []).map(s => ({ ...s })),
            pep: (indivResults.pep || []).map(p => ({ ...p })),
            criminal: (indivResults.criminal || []).map(c => ({ ...c }))
          };
          const matchesData = [...(indivResults.allRecords||[]), ...(bizResults?.allRecords||[])]
            .map(m => ({
              name: m.name,
              source_type: m.source_type,
              list_type: m.source_type,
              confidence: m.confidence,
              country: m.country
            }));

          // Save individual and business as separate records using direct API call
          const saveScreeningRecord = async (name, screenType, results) => {
            console.log(`[KYC] Saving ${screenType} screening for: ${name}`, { totalHits: results.totalHits });

            const matchDetail = {
              sanctions: (results.sanctions || []).map(s => ({ name: s.name, country: s.country })),
              pep: (results.pep || []).map(p => ({ name: p.name, country: p.country })),
              criminal: (results.criminal || []).map(c => ({ name: c.name, country: c.country }))
            };

            const screeningPayload = {
              company_id: company.id,
              screen_type: screenType,
              subject_name: name,
              subject_nationality: screenType === "individual" ? (sub.nationality || sub.country || null) : null,
              result: results.totalHits === 0 ? "clear" : results.sanctions?.length > 0 ? "confirmed_match" : "potential_match",
              match_count: results.totalHits || 0,
              match_detail: matchDetail,
              sanctions: results.sanctions?.length > 0 ? "failed" : "passed",
              watchlist: results.pep?.length > 0 ? "failed" : "passed",
              criminal: results.criminal?.length > 0 ? "failed" : "passed",
              risk_score: results.totalHits === 0 ? 0 : results.sanctions?.length > 0 ? 90 : 50,
              provider: "Dilisense",
              notes: `Screened from KYC submission ${id.slice(0,8)}`,
              screened_at: new Date().toISOString(),
            };

            try {
              console.log(`[KYC] Inserting ${screenType} record for: ${name}`);
              const token = localStorage.getItem('amlguard_token');
              const response = await fetch('/api/query?resource=sanctions_screening', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(screeningPayload),
              });

              if (response.ok) {
                console.log(`[KYC] Successfully saved ${screenType} screening for: ${name}`);

                // Log to audit trail
                await auditLog({
                  action: 'sanctions_screening_completed',
                  module: 'kyc',
                  entityId: sub.id,
                  entityName: name,
                  newValue: {
                    screen_type: screenType,
                    result: screeningPayload.result,
                    match_count: results.totalHits,
                    sanctions: screeningPayload.sanctions,
                    watchlist: screeningPayload.watchlist,
                    criminal: screeningPayload.criminal,
                  }
                });
              } else {
                const errorText = await response.text();
                console.error(`[KYC] API error for ${name}:`, response.status, errorText);
              }
            } catch (err) {
              console.error(`[KYC] Sanctions screening save error for ${name}:`, err.message);
            }
          };

          // Save individual screening
          console.log("[KYC] Starting sanctions screening save process");
          await saveScreeningRecord(indivName, "individual", indivResults);

          // Save business screening if it's a business type
          if (sub.type === "business" && sub.business_name && bizResults) {
            await saveScreeningRecord(sub.business_name, "business", bizResults);
          }
          console.log("[KYC] Sanctions screening save process complete");

          // Stringify combined result for TEXT column
          await db.from("kyc_submissions").update({
            sanctions_result: JSON.stringify(combined)
          }).eq("id", id).execute().catch(() => {}); // Ignore if column doesn't exist
        } catch(dbErr) {
          console.warn("[KYC] Sanctions result save skipped:", dbErr.message);
        }
      }
    } catch(e) { setSanctionsResults(p => ({ ...p, [id]: { error: e.message } })); }
    setSanctionsLoading(p => ({ ...p, [id]: false }));
  }

  function runSerproVerification(sub) {
    // Open modal with applicant's data
    setSerproModal({
      open: true,
      cpf: sub.tax_id || "",
      name: sub.first_name && sub.last_name ? `${sub.first_name} ${sub.last_name}` : "",
      dob: sub.dob || "",
      submissionId: sub.id
    });
  }

  async function confirmSerproVerification() {
    const { cpf, name, dob, submissionId } = serproModal;
    if (!submissionId) return;

    setSerproLoading(p => ({ ...p, [submissionId]: true }));

    try {
      console.log("[KYC] Running Serpro verification for CPF:", cpf);

      // Call Serpro API with CPF and DOB
      const response = await fetch("/api/serpro", {
        method: "POST",
        headers: getApiHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          action: "cpf",
          data: { cpf, birth_date: dob }
        })
      });

      const result = await response.json();
      console.log("[KYC] Serpro result:", result);

      setSerproResults(p => ({ ...p, [submissionId]: result }));

      // Automatically include Serpro data in AI analysis for enrichment
      if (result.success) {
        setDataAnalysis(p => ({
          ...p,
          [submissionId]: {
            ...(p[submissionId] || {}),
            serpro_verification: {
              status: "verified",
              cpf: result.cpf,
              name: result.data?.nome,
              governmentStatus: result.data?.situacao?.descricao,
              verifiedAt: new Date().toISOString()
            }
          }
        }));
      }

      setSerproModal({ open: false, cpf: "", name: "", dob: "", submissionId: null });

      // Show success/error message
      if (result.error) {
        alert(`Serpro verification failed: ${result.error}\n${result.details?.erroDescricao || result.details?.message || ""}`);
      } else {
        alert(`✓ Serpro verification successful!\nName: ${result.data?.nome || name}\nStatus: ${result.data?.situacao?.descricao || "Unknown"}\n\nResult added to AI Analysis data.`);
      }
    } catch(e) {
      console.error("[KYC] Serpro error:", e);
      setSerproResults(p => ({ ...p, [submissionId]: { error: e.message } }));
      alert(`Serpro error: ${e.message}`);
    }
    setSerproLoading(p => ({ ...p, [submissionId]: false }));
  }

  async function runSocureVerification(sub) {
    const id = sub.id;
    if (socureLoading[id]) return;
    setSocureLoading(p => ({ ...p, [id]: true }));
    try {
      console.log("[KYC] Running Socure verification for submission:", id);
      const token = localStorage.getItem('amlguard_token');

      const response = await fetch('/api/socure/evaluate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          submission_id: id
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[KYC] Socure error:", response.status, errorText);
        setSocureResults(p => ({ ...p, [id]: { error: errorText } }));
        alert(`Socure verification failed: ${response.status}`);
        return;
      }

      const result = await response.json();
      console.log("[KYC] Socure verification result:", result);
      setSocureResults(p => ({ ...p, [id]: result }));

      // Store Socure results in AI analysis section
      if (result.success) {
        setAiAnalysis(p => ({
          ...p,
          [id]: {
            ...(p[id] || {}),
            socure_verification: {
              reference_id: result.reference_id,
              status: result.status,
              decision: result.decision,
              confidence_score: result.confidence_score,
              verified_at: new Date().toISOString()
            }
          }
        }));
      }
    } catch(e) {
      console.error("[KYC] Socure verification error:", e);
      setSocureResults(p => ({ ...p, [id]: { error: e.message } }));
      alert(`Socure error: ${e.message}`);
    }
    setSocureLoading(p => ({ ...p, [id]: false }));
  }

  // Generate random 10-digit account number
  function generateAccountNumber() {
    return Math.floor(Math.random() * 10000000000).toString().padStart(10, '0');
  }

  async function handleAction(action) {
    if (!selected || actioning) return;
    // For request_info, show the structured modal instead
    if (action === "request_info") { setShowInfoModal(true); return; }
    // E-014 / F-003: Gate approval on UBO attestation for business submissions
    if (action === "approve" && selected.type === "business") {
      const ubos = (() => { try { return Array.isArray(selected.ubos) ? selected.ubos : JSON.parse(selected.ubos || "[]"); } catch { return []; } })();
      if (ubos.length > 0) {
        const attested = ubos.every((_, i) => uboAttestation[i]);
        if (!attested) {
          alert("All beneficial owners must be individually attested before approval. Please check each UBO below.");
          return;
        }
      }
    }
    setActioning(true);
    const statusMap = { approve: "approved", reject: "rejected" };
    const newStatus = statusMap[action];
    const now = new Date().toISOString();

    const riskScoreMap = { Low: 25, Medium: 55, High: 80 };
    const riskPatch = action === "approve"
      ? {
          risk_rating: actionRisk,
          risk_score: riskScoreMap[actionRisk] ?? 55,
          account_number: accountNumber || generateAccountNumber()
        }
      : {};

    try {
      if (!selected.id?.startsWith("mock-")) {
        let basePayload = { status: newStatus, last_reviewed_by: currentUser?.id, last_reviewed_at: now };

        try {
          const result = await db.from("kyc_submissions").update({ ...basePayload, ...riskPatch }).eq("id", selected.id).execute();
          if (result.error) {
            // If columns don't exist, try fallback with just status
            const errorMsg = result.error.message || "";
            if (errorMsg.includes("last_reviewed_by") || errorMsg.includes("last_reviewed_at") ||
              errorMsg.includes("risk_rating") || errorMsg.includes("risk_score") || errorMsg.includes("review_note")) {
              console.warn("[KYC] Some columns not yet in database, using minimal fallback:", errorMsg.substring(0, 100));
              const fallbackResult = await db.from("kyc_submissions").update({ status: newStatus }).eq("id", selected.id).execute();
              if (fallbackResult.error) throw fallbackResult.error;
            } else {
              throw result.error;
            }
          }
        } catch (updateErr) {
          console.error("[KYC] Failed to update kyc_submissions:", updateErr);
          throw updateErr;
        }

        try {
          await db.from("kyc_audit_logs").insert({
            company_id: company?.id,
            submission_id: selected.id,
            action,
            details: { note: actionNote, risk_rating: actionRisk, account_number: accountNumber }
          }).execute();
        } catch (auditErr) {
          console.error("[KYC] Failed to insert audit log:", auditErr);
        }

        await loadAuditLogs(selected.id);
        const subjectName = [selected.first_name, selected.last_name].filter(Boolean).join(" ") || selected.business_name || "Applicant";
        await auditLog({
          action: action === "approve" ? "kyc_approved" : "kyc_rejected",
          module: "kyc",
          entityId: selected.id,
          entityName: subjectName,
          newValue: { status: newStatus, review_note: actionNote, account_number: accountNumber, terms_accepted_at: selected.terms_accepted_at },
        });

        // Mark any open assignment for this KYC as done
        if (newStatus === "approved" || newStatus === "rejected") {
          try {
            // Query the assignment API directly since we need to filter by kyc_id
            const assignmentRes = await fetch(`/api/query?resource=assignments&filter=${encodeURIComponent(JSON.stringify({ kyc_id: selected.id, status: 'open' }))}`, {
              method: 'GET',
              headers: { 'Authorization': `Bearer ${localStorage.getItem('amlguard_token')}` }
            });
            if (assignmentRes.ok) {
              const assignments = await assignmentRes.json();
              // Update each assignment to done
              for (const assignment of assignments) {
                await fetch(`/api/query?resource=assignments`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('amlguard_token')}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ id: assignment.id, status: 'done' })
                }).catch(() => {});
              }
            }
          } catch (err) {
            console.warn("[KYC] Failed to update assignments:", err);
          }
        }
      }

      setSubmissions(prev => prev.map(s => s.id === selected.id ? { ...s, status: newStatus, last_reviewed_at: now, last_reviewed_by: currentUser?.id, reviewed_at: now, reviewed_by: currentUser?.id, ...riskPatch } : s));
      setSelected(prev => ({ ...prev, status: newStatus, last_reviewed_at: now, last_reviewed_by: currentUser?.id, reviewed_at: now, reviewed_by: currentUser?.id, ...riskPatch }));

      // Auto-populate beneficial_owners table from UBOs when a business is approved
      if (action === "approve" && selected.type === "business" && !selected.id?.startsWith("mock-")) {
        const ubos = (() => { try { return Array.isArray(selected.ubos) ? selected.ubos : JSON.parse(selected.ubos || "[]"); } catch { return []; } })();
        if (ubos.length > 0 && company?.id) {
          // Insert each UBO separately since API expects single objects
          for (const u of ubos) {
            const boPayload = {
              kyc_submission_id:    selected.id,
              company_id:           company.id,
              full_name:            u.name || "",
              relationship:         u.role || "Director",
              ownership_percentage: parseFloat(u.ownership) || null,
              certified_at:         now,
            };
            await db.from("beneficial_owners").insert(boPayload).catch(() => {}); // Ignore if table op fails
          }
        }
      }

      if (apiSettings.webhookUrl && ((action === "approve" && apiSettings.pushOnApprove) || (action === "reject" && apiSettings.pushOnReject))) {
        pushToCustomerApi({ ...selected, status: newStatus }, aiAnalysis[selected.id], apiSettings);
      }

      setActionNote("");
      setActionRisk("Medium");
      setAccountNumber("");
    } catch (err) {
      console.error("[handleAction] error:", err);
    } finally {
      setActioning(false);
    }
  }

  async function submitInfoRequest() {
    if (!selected) return;
    setActioning(true);
    const now = new Date().toISOString();
    const requirements = {
      id_front:              infoRequest.id_front,
      id_back:               infoRequest.id_back,
      selfie:                infoRequest.selfie,
      data:                  infoRequest.data,
      pep_declaration:       infoRequest.pep_declaration,
      sanctions_declaration: infoRequest.sanctions_declaration,
      additional_docs:       infoRequest.additional_docs,
      custom_doc:            infoRequest.custom_doc,
      note:                  infoRequest.note || actionNote,
    };

    if (!selected.id?.startsWith("mock-")) {
      await db.from("kyc_submissions").update({
        status: "info_requested",
        reviewed_by: currentUser?.id,
        reviewed_at: now,
        review_note: requirements.note,
        info_requirements: requirements,
      }).eq("id", selected.id).execute();
      await db.from("kyc_audit_logs").insert({
        company_id: company?.id,
        submission_id: selected.id,
        action: "request_info",
        note: JSON.stringify(requirements),
        reviewer_id: currentUser?.id,
      });
      await loadAuditLogs(selected.id);
    }

    // Build the re-submission link with encoded requirements
    const reqParam = encodeURIComponent(btoa(JSON.stringify(requirements)));
    const link = `${window.location.origin}?kyc=1&company=${selected.company_id}&resubmit=${selected.id}&req=${reqParam}`;
    setInfoLink(link);

    setSubmissions(prev => prev.map(s => s.id === selected.id ? { ...s, status: "info_requested", reviewed_at: now, info_requirements: requirements } : s));
    setSelected(prev => ({ ...prev, status: "info_requested", reviewed_at: now, info_requirements: requirements }));
    setActioning(false);
  }

  const filtered = filter === "all" ? submissions : submissions.filter(s => s.status === filter);

  const statusColor = { pending: C.warn, approved: C.success, rejected: C.danger, info_requested: C.gold };
  const statusLabel = { pending: "PENDING", approved: "APPROVED", rejected: "REJECTED", info_requested: "INFO NEEDED" };

  function exportKYCPDF(s, ai) {
    const rows = (fields) => fields.filter(([,v])=>v).map(([k,v])=>`<tr><td><b>${k}</b></td><td>${String(v).replace(/</g,"&lt;")}</td></tr>`).join("");
    const personalRows = rows([["Name",`${s.first_name} ${s.last_name}`],["Email",s.email],["Phone",s.phone||"—"],["DOB",s.dob||"—"],["Nationality",s.nationality||"—"],["Tax ID",s.tax_id||"—"],["Address",`${s.address_line1||""} ${s.city||""} ${s.country||""}`.trim()]]);
    const complianceRows = rows([["Source of Funds",s.source_of_funds||"—"],["Annual Income",s.annual_income||"—"],["PEP",s.pep_declaration||"—"],["Sanctions",s.sanctions_declaration||"—"]]);
    const businessRows = s.type==="business" ? rows([["Business",s.business_name||"—"],["Reg No.",s.reg_number||"—"],["Type",s.business_type||"—"],["Country",s.incorporation_country||"—"],["Website",s.website||"—"]]) : "";
    const aiRows = ai ? rows([["Verdict",ai.overall_verdict||"—"],["Confidence",ai.confidence!=null?`${ai.confidence}%`:"—"],["ID Auth",ai.id_authenticity?`${ai.id_authenticity.verdict} (${ai.id_authenticity.score}%)`:"—"],["Face Match",ai.face_match?`${ai.face_match.verdict} (${ai.face_match.score}%)`:"—"],["Consistency",ai.data_consistency?`${ai.data_consistency.verdict} (${ai.data_consistency.score}%)`:"—"],["Summary",ai.summary||"—"]]) : "";
    const redFlags = ai?.red_flags?.length ? `<ul>${ai.red_flags.map(f=>`<li>${f}</li>`).join("")}</ul>` : "None identified";
    const w = window.open("","_blank");
    if(!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>KYC — ${s.first_name} ${s.last_name}</title><style>
    @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@700;800&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Mono',monospace;background:#fff;color:#0A2342;padding:12mm;font-size:11px}
    .header{display:flex;align-items:center;gap:10px;margin-bottom:6mm;padding-bottom:4mm;border-bottom:3px solid #00D4FF}
    .logo-hex{width:26px;height:26px;background:#00D4FF;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);flex-shrink:0}
    .logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:16px}
    .doc-title{font-family:'Syne',sans-serif;font-size:18px;font-weight:700;margin-bottom:1mm}
    .doc-meta{font-size:9px;color:#4A5568;margin-bottom:5mm}
    .section{margin-bottom:5mm}.section-title{font-family:'Syne',sans-serif;font-weight:700;font-size:11px;letter-spacing:0.1em;margin-bottom:2mm;padding-bottom:1mm;border-bottom:1px solid #E2E8F0}
    table{width:100%;border-collapse:collapse;font-size:10px}td{padding:4px 7px;border-bottom:1px solid #E2E8F0;vertical-align:top;width:30%}td:last-child{width:70%}tr:nth-child(even) td{background:#F7FAFC}
    ul{margin:4px 0 0 16px;font-size:10px;line-height:1.6}
    .footer{margin-top:6mm;padding-top:2mm;border-top:1px solid #E2E8F0;font-size:8px;color:#8A9BB5;display:flex;justify-content:space-between}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body>
    <div class="header"><div class="logo-hex"></div><span class="logo-text">CrossRegGuard</span></div>
    <div class="doc-title">KYC Report — ${s.first_name} ${s.last_name}</div>
    <div class="doc-meta">ID: ${s.id||"—"} · Status: ${(statusLabel[s.status]||s.status||"").toUpperCase()} · Generated: ${new Date().toLocaleString()}</div>
    <div class="section"><div class="section-title">PERSONAL INFORMATION</div><table><tbody>${personalRows}</tbody></table></div>
    ${businessRows?`<div class="section"><div class="section-title">BUSINESS DETAILS</div><table><tbody>${businessRows}</tbody></table></div>`:""}
    <div class="section"><div class="section-title">COMPLIANCE DECLARATIONS</div><table><tbody>${complianceRows}</tbody></table></div>
    ${aiRows?`<div class="section"><div class="section-title">AI ANALYSIS</div><table><tbody>${aiRows}</tbody></table><div style="margin-top:6px"><b>Red Flags:</b>${redFlags}</div></div>`:""}
    <div class="footer"><span>CrossRegGuard — Confidential</span><span>KYC ${(s.id||"").slice(0,8)} · ${new Date().toLocaleDateString()}</span></div>
    </body></html>`);
    w.document.close(); setTimeout(()=>w.print(),800);
  }

  if (selected && selected.id) {
    let score = 0, flags = [];
    try {
      const result = computeRiskScore(selected);
      score = result.score || 0;
      flags = result.flags || [];
    } catch (e) {
      console.error('[KYC] Risk score computation error:', e);
    }

    return (
      <>
      <div style={{ fontFamily: "'DM Mono', monospace" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
          <button onClick={() => setSelected(null)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, padding: "6px 13px", cursor: "pointer", fontSize: 12 }}>{t('onboarding.back')}</button>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h2 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 700, margin: 0 }}>
                {selected.first_name} {selected.last_name}
                {selected.business_name && <span style={{ color: C.textDim, fontSize: 14, marginLeft: 8 }}>· {selected.business_name}</span>}
              </h2>
              <span style={{ color: statusColor[selected.status] || C.textDim, fontSize: 10, background: `${statusColor[selected.status]}18`, border: `1px solid ${statusColor[selected.status]}30`, padding: "2px 8px", borderRadius: 4 }}>
                {statusLabel[selected.status] || selected.status?.toUpperCase()}
              </span>
            </div>
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{selected.email} · Submitted {new Date(selected.submitted_at).toLocaleString()}</div>
          </div>
          <button onClick={() => exportKYCPDF(selected, aiAnalysis[selected.id])} style={{ background: "transparent", border: `1px solid ${C.accent}`, borderRadius: 6, color: C.accent, padding: "6px 14px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>⬇ {t('onboarding.dashboard.exportPDF')}</button>
        </div>

        {/* E-015 / F-009: Internal watchlist warning */}
        {watchlistHits && Array.isArray(watchlistHits) && watchlistHits.length > 0 && (
          <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}44`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, color: C.danger, fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              🚫 INTERNAL WATCHLIST MATCH — {watchlistHits.length} hit{watchlistHits.length > 1 ? "s" : ""}
            </div>
            {watchlistHits.map((h, i) => (
              <div key={i} style={{ fontSize: 11, marginBottom: 3 }}>
                · <strong>{h.full_name}</strong> — {h.list_type?.toUpperCase()} — {h.reason}
              </div>
            ))}
            <div style={{ fontSize: 11, color: C.textMid, marginTop: 6 }}>
              This subject is on the internal watchlist. Approval is not recommended without escalation.
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 16 }}>
          {/* Left: Details */}
          <div>
            {/* Risk + Flags */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 }}>
              <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 12 }}>RISK ASSESSMENT</div>
              <RiskMeter score={score} onScoreChange={(newScore) => updateRiskScore(selected.id, newScore)} isEditable={true} />
              {flags.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 12 }}>
                  {flags.map((f, i) => (
                    <span key={i} style={{ color: f.color, fontSize: 10, background: `${f.color}18`, border: `1px solid ${f.color}30`, padding: "3px 9px", borderRadius: 4 }}>⚑ {f.label}</span>
                  ))}
                </div>
              )}
            </div>

            {/* ID / Selfie comparison — always shown */}
            {(() => {
              const isMock = selected.id?.startsWith("mock-");
              const initials = `${selected.first_name?.[0] || "?"}${selected.last_name?.[0] || ""}`;
              const hue = ((selected.first_name?.charCodeAt(0) || 65) * 7 + (selected.last_name?.charCodeAt(0) || 65) * 13) % 360;

              // SVG placeholder selfie — avatar with initials
              const selfieSvg = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="400" height="320" viewBox="0 0 400 320">
                <rect width="400" height="320" fill="#0A0E1A"/>
                <rect x="1" y="1" width="398" height="318" fill="none" stroke="#1E2D45" stroke-width="1"/>
                <!-- Background grid -->
                <pattern id="g" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M 24 0 L 0 0 0 24" fill="none" stroke="#1E2D4520" stroke-width="0.5"/></pattern>
                <rect width="400" height="320" fill="url(#g)"/>
                <!-- Oval guide -->
                <ellipse cx="200" cy="148" rx="80" ry="100" fill="hsl(${hue},50%,18%)" stroke="hsl(${hue},70%,55%)" stroke-width="2"/>
                <!-- Head -->
                <ellipse cx="200" cy="130" rx="52" ry="60" fill="hsl(${hue},40%,28%)"/>
                <!-- Shoulders -->
                <ellipse cx="200" cy="270" rx="85" ry="55" fill="hsl(${hue},40%,22%)"/>
                <!-- Initials -->
                <text x="200" y="148" text-anchor="middle" dominant-baseline="middle" font-family="monospace" font-size="38" font-weight="700" fill="hsl(${hue},80%,80%)">${initials}</text>
                <!-- Scan line effect -->
                <rect x="0" y="0" width="400" height="320" fill="url(#scan)" opacity="0.06"/>
                <pattern id="scan" width="400" height="4" patternUnits="userSpaceOnUse"><rect width="400" height="2" fill="white"/></pattern>
                <!-- Label -->
                <rect x="0" y="290" width="400" height="30" fill="#0A0E1Aaa"/>
                <text x="200" y="310" text-anchor="middle" font-family="monospace" font-size="10" fill="#4A5870">LIVE CAPTURE · DEMO PREVIEW</text>
              </svg>`)}`;

              // SVG placeholder ID card
              const idSvg = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="400" height="260" viewBox="0 0 400 260">
                <defs>
                  <linearGradient id="card" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="hsl(${hue},45%,14%)"/>
                    <stop offset="100%" stop-color="hsl(${(hue+40)%360},35%,10%)"/>
                  </linearGradient>
                </defs>
                <rect width="400" height="260" rx="12" fill="url(#card)"/>
                <rect x="1" y="1" width="398" height="258" rx="11" fill="none" stroke="hsl(${hue},60%,40%)" stroke-width="1.5"/>
                <!-- Decorative stripe -->
                <rect width="400" height="8" rx="0" fill="hsl(${hue},70%,45%)" opacity="0.7"/>
                <!-- Photo box -->
                <rect x="20" y="30" width="80" height="100" rx="6" fill="hsl(${hue},35%,20%)" stroke="hsl(${hue},50%,35%)" stroke-width="1"/>
                <ellipse cx="60" cy="68" rx="22" ry="26" fill="hsl(${hue},40%,32%)"/>
                <ellipse cx="60" cy="112" rx="30" ry="20" fill="hsl(${hue},40%,26%)"/>
                <text x="60" y="73" text-anchor="middle" dominant-baseline="middle" font-family="monospace" font-size="16" font-weight="700" fill="hsl(${hue},70%,75%)">${initials}</text>
                <!-- Info lines -->
                <text x="120" y="52" font-family="monospace" font-size="8" fill="hsl(${hue},40%,55%)">FULL NAME</text>
                <text x="120" y="66" font-family="monospace" font-size="13" font-weight="700" fill="#E8EDF5">${selected.first_name} ${selected.last_name}</text>
                <text x="120" y="90" font-family="monospace" font-size="8" fill="hsl(${hue},40%,55%)">DATE OF BIRTH</text>
                <text x="120" y="104" font-family="monospace" font-size="12" fill="#C0C8D8">${selected.dob || "—"}</text>
                <text x="120" y="122" font-family="monospace" font-size="8" fill="hsl(${hue},40%,55%)">NATIONALITY</text>
                <text x="120" y="136" font-family="monospace" font-size="12" fill="#C0C8D8">${selected.nationality || "—"}</text>
                <!-- ID Type badge -->
                <rect x="270" y="30" width="110" height="22" rx="4" fill="hsl(${hue},60%,22%)" stroke="hsl(${hue},60%,40%)" stroke-width="1"/>
                <text x="325" y="45" text-anchor="middle" font-family="monospace" font-size="9" font-weight="700" fill="hsl(${hue},80%,70%)">${selected.id_type || "PASSPORT"}</text>
                <!-- MRZ zone -->
                <rect x="0" y="178" width="400" height="62" rx="0" fill="#05080F"/>
                <rect x="0" y="178" width="400" height="1" fill="hsl(${hue},50%,30%)"/>
                <text x="20" y="196" font-family="monospace" font-size="9" fill="#2A3A50">P&lt;GBR${selected.last_name?.toUpperCase() || "SMITH"}${`&lt;${selected.first_name?.toUpperCase() || "JANE"}&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;`.slice(0,25)}</text>
                <text x="20" y="212" font-family="monospace" font-size="9" fill="#2A3A50">AB${Math.floor(Math.random()*9000000+1000000)}3GBR${(selected.dob||"850101").replace(/-/g,"").slice(2)}&lt;9${(selected.dob||"250101").replace(/-/g,"").slice(2,6)}&lt;&lt;&lt;&lt;</text>
                <text x="20" y="228" font-family="monospace" font-size="9" fill="#2A3A50">DEMO DOCUMENT · NOT VALID FOR TRAVEL · CROSSREGGUARD PREVIEW</text>
                <!-- Corner chip -->
                <rect x="340" y="155" width="44" height="34" rx="4" fill="hsl(45,70%,45%)" opacity="0.8"/>
                <rect x="346" y="161" width="32" height="22" rx="2" fill="none" stroke="hsl(45,50%,30%)" stroke-width="1.5"/>
                <line x1="362" y1="161" x2="362" y2="183" stroke="hsl(45,50%,30%)" stroke-width="1"/>
                <line x1="346" y1="172" x2="378" y2="172" stroke="hsl(45,50%,30%)" stroke-width="1"/>
              </svg>`)}`;

              const selfieData  = selected.selfie_data   || (isMock ? selfieSvg : null);
              const idFrontData = selected.id_front_data || (isMock ? idSvg    : null);
              const idBackData  = selected.id_back_data  || null;

              return (
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      {["compare","selfie","id"].map(tabKey => (
                        <button key={tabKey} onClick={() => setSelfieTab(tabKey)} style={{ padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 11, background: selfieTab === tabKey ? C.accentGlow : "transparent", border: `1px solid ${selfieTab === tabKey ? C.accent : C.border}`, color: selfieTab === tabKey ? C.accent : C.textMid }}>
                          {tabKey === "compare" ? t('onboarding.dashboard.tabSideBySide') : tabKey === "selfie" ? t('onboarding.dashboard.tabSelfie') : t('onboarding.dashboard.tabIdDocument')}
                        </button>
                      ))}
                    </div>
                    {isMock && (
                      <span style={{ color: C.textDim, fontSize: 10, background: C.surfaceAlt, border: `1px solid ${C.border}`, padding: "2px 8px", borderRadius: 4 }}>
                        ⚠ {t('onboarding.dashboard.demoPreview')}
                      </span>
                    )}
                  </div>

                  {selfieTab === "compare" && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      {[
                        { label: "ID DOCUMENT",  src: idFrontData,  sub: selected.id_type || "Government ID" },
                        { label: "LIVE SELFIE",  src: selfieData,   sub: "Liveness capture" },
                      ].map(({ label, src, sub }) => (
                        <div key={label}>
                          <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 6 }}>{label}</div>
                          {src
                            ? <img src={src} alt={label} style={{ width: "100%", borderRadius: 8, objectFit: "cover", maxHeight: 220, border: `1px solid ${C.border}`, display: "block" }} />
                            : <div style={{ height: 180, background: C.bg, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, fontSize: 12, border: `1px dashed ${C.border}` }}>{t('onboarding.dashboard.notUploaded')}</div>
                          }
                          <div style={{ color: C.textDim, fontSize: 10, marginTop: 5 }}>{sub}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {selfieTab === "selfie" && (
                    <div>
                      {selfieData
                        ? <img src={selfieData} alt="Selfie" style={{ width: "100%", maxHeight: 360, objectFit: "contain", borderRadius: 8, border: `1px solid ${C.border}` }} />
                        : <div style={{ height: 220, background: C.bg, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, fontSize: 13, border: `1px dashed ${C.border}` }}>{t('onboarding.dashboard.noSelfieUploaded')}</div>
                      }
                    </div>
                  )}

                  {selfieTab === "id" && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      {[["FRONT", idFrontData], ["BACK", idBackData]].map(([label, src]) => (
                        <div key={label}>
                          <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 6 }}>{label}</div>
                          {src
                            ? <img src={src} alt={label} style={{ width: "100%", borderRadius: 8, border: `1px solid ${C.border}`, display: "block" }} />
                            : <div style={{ height: 140, background: C.bg, borderRadius: 8, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: C.textDim, fontSize: 12, border: `1px dashed ${C.border}`, gap: 6 }}>
                                <span style={{ fontSize: 20 }}>📄</span>
                                {label === "BACK" ? t('onboarding.dashboard.notRequiredPassport') : t('onboarding.dashboard.notUploaded')}
                              </div>
                          }
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── AI Analysis ── */}
            {(() => {
              if (!selected || !selected.id) return null;
              const isMockSub    = selected.id?.startsWith("mock-");
              const analysis     = aiAnalysis[selected.id];
              const dataAnal     = dataAnalysis[selected.id];
              const docsAnal     = docsAnalysis[selected.id];
              const loading      = aiLoading[selected.id];
              const loadingData  = aiLoading[`${selected.id}_data`];
              const loadingDocs  = aiLoading[`${selected.id}_docs`];
              const hasAdditionalImageDocs = (
                ensureArray(selected.additional_docs).some(d => (d.data && !d.data.startsWith("data:image/svg")) || d.path) ||
                ensureArray(selected.additional_docs_resolved).some(d => d.signedUrl) ||
                ensureArray(selected.additional_doc_paths).length > 0
              );

              const verdictColor = v => ({ GENUINE: C.success, PASS: C.success, MATCH: C.success, CONSISTENT: C.success, SUSPECT: C.danger, FAIL: C.danger, MISMATCH: C.danger, INCONSISTENT: C.warn, INCONCLUSIVE: C.textMid, REVIEW: C.warn, NOT_PROVIDED: C.textDim }[v] || C.textMid);

              const Spinner = () => <span style={{ display: "inline-block", width: 12, height: 12, border: `2px solid ${C.border}`, borderTopColor: C.accent, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />;

              const AnalysisResult = ({ result, label }) => {
                if (!result || result.error) return null;
                const oc = verdictColor(result.overall);
                return (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.12em", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 4, height: 4, borderRadius: "50%", background: oc, display: "inline-block" }} />
                      {label}
                    </div>
                    <div style={{ background: `${oc}12`, border: `1px solid ${oc}40`, borderRadius: 9, padding: "12px 14px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ color: oc, fontSize: 16, fontWeight: 800, fontFamily: "'Syne', sans-serif" }}>
                          {result.overall === "PASS" ? "✓ PASS" : result.overall === "FAIL" ? "✗ FAIL" : "⚠ NEEDS REVIEW"}
                        </div>
                        <div style={{ color: C.textMid, fontSize: 11, marginTop: 3, lineHeight: 1.5 }}>{result.summary}</div>
                      </div>
                      <div style={{ textAlign: "center", flexShrink: 0, marginLeft: 14 }}>
                        <div style={{ color: oc, fontSize: 22, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{result.confidence}%</div>
                        <div style={{ color: C.textDim, fontSize: 10 }}>confidence</div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                      {[
                        { label: "ID AUTHENTICITY", data: result.id_authenticity },
                        { label: "FACE MATCH",       data: result.face_match },
                        { label: "DATA CONSISTENCY", data: result.data_consistency },
                      ].map(({ label: lbl, data: d }) => d ? (
                        <div key={lbl} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
                          <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.1em", marginBottom: 5 }}>{lbl}</div>
                          <div style={{ color: verdictColor(d.verdict), fontSize: 12, fontWeight: 700, marginBottom: 5 }}>{d.verdict}</div>
                          <div style={{ height: 3, background: C.border, borderRadius: 2, marginBottom: 7, overflow: "hidden" }}>
                            <div style={{ width: `${d.score}%`, height: "100%", background: verdictColor(d.verdict), borderRadius: 2 }} />
                          </div>
                          {(d.findings || []).map((f, fi) => (
                            <div key={fi} style={{ color: C.textDim, fontSize: 10, lineHeight: 1.5, paddingLeft: 7, borderLeft: `2px solid ${C.border}`, marginBottom: 3 }}>{f}</div>
                          ))}
                        </div>
                      ) : null)}
                    </div>
                    {result.red_flags?.length > 0 && (
                      <div style={{ background: `${C.danger}08`, border: `1px solid ${C.danger}25`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
                        <div style={{ color: C.danger, fontSize: 9, letterSpacing: "0.1em", marginBottom: 6 }}>RED FLAGS</div>
                        {result.red_flags.map((f, fi) => <div key={fi} style={{ color: C.textMid, fontSize: 11, marginBottom: 3, display: "flex", gap: 6 }}><span style={{ color: C.danger }}>⚑</span>{f}</div>)}
                      </div>
                    )}
                    {result.recommendations?.length > 0 && (
                      <div style={{ background: `${C.gold}08`, border: `1px solid ${C.gold}25`, borderRadius: 8, padding: 10 }}>
                        <div style={{ color: C.gold, fontSize: 9, letterSpacing: "0.1em", marginBottom: 6 }}>RECOMMENDED ACTIONS</div>
                        {result.recommendations.map((r, ri) => <div key={ri} style={{ color: C.textMid, fontSize: 11, marginBottom: 3, display: "flex", gap: 6 }}><span style={{ color: C.gold }}>→</span>{r}</div>)}
                      </div>
                    )}
                  </div>
                );
              };

              const btnStyle = (isLoading, disabled) => ({
                padding: "7px 13px", borderRadius: 7, cursor: disabled ? "not-allowed" : "pointer",
                fontSize: 11, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap",
                display: "flex", alignItems: "center", gap: 6,
                background: disabled ? C.border : C.accentGlow,
                border: `1px solid ${disabled ? C.border : C.borderLight}`,
                color: disabled ? C.textDim : C.accent,
              });

              return (
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 }}>
                  {/* Header + buttons */}
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 8 }}>AI ANALYSIS</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>

                      {/* ── Full analysis (ID + selfie) ── */}
                      <button onClick={() => runAiAnalysis(selected, "full")} disabled={loading || isMockSub} style={btnStyle(loading, loading || isMockSub)}>
                        {loading ? <><Spinner /> Analysing…</> : isMockSub ? "🖼 Demo only" : analysis ? "🖼 Re-run Full" : "🖼 ID & Selfie"}
                      </button>

                      {/* ── Data-only analysis ── */}
                      <button onClick={() => runAiAnalysis(selected, "data")} disabled={loadingData || isMockSub} style={btnStyle(loadingData, loadingData || isMockSub)}>
                        {loadingData ? <><Spinner /> Analysing…</> : isMockSub ? "📋 Demo only" : dataAnal ? "📋 Re-run Data" : "📋 Data Only"}
                      </button>

                      {/* ── Additional docs analysis (only shown if docs exist) ── */}
                      {hasAdditionalImageDocs && (
                        <button onClick={() => runAiAnalysis(selected, "docs")} disabled={loadingDocs || isMockSub} style={btnStyle(loadingDocs, loadingDocs || isMockSub)}>
                          {loadingDocs ? <><Spinner /> Analysing…</> : docsAnal ? "📎 Re-run Docs" : "📎 Supporting Docs"}
                        </button>
                      )}
                      {/* ── Sanctions screening ── */}
                      <button
                        onClick={() => runSanctionsCheck(selected)}
                        disabled={!!sanctionsLoading[selected.id] || isMockSub}
                        style={btnStyle(!!sanctionsLoading[selected.id], !!sanctionsLoading[selected.id] || isMockSub)}>
                        {sanctionsLoading[selected.id] ? <><Spinner /> Screening…</> : sanctionsResults[selected.id] ? "🛡 Re-run Sanctions" : "🛡 Sanctions Check"}
                      </button>

                      {/* ── Serpro verification ── */}
                      <button
                        onClick={() => runSerproVerification(selected)}
                        disabled={!!serproLoading[selected.id] || isMockSub}
                        style={btnStyle(!!serproLoading[selected.id], !!serproLoading[selected.id] || isMockSub)}>
                        {serproLoading[selected.id] ? <><Spinner /> Verifying…</> : serproResults[selected.id] ? "🔐 Re-run Serpro" : "🔐 Serpro"}
                      </button>

                      {/* ── Socure Document Verification ── */}
                      <button
                        onClick={() => runSocureVerification(selected)}
                        disabled={!!socureLoading[selected.id] || isMockSub}
                        style={btnStyle(!!socureLoading[selected.id], !!socureLoading[selected.id] || isMockSub)}>
                        {socureLoading[selected.id] ? <><Spinner /> Verifying…</> : socureResults[selected.id] ? "📸 Re-run Socure" : "📸 Socure DocV"}
                      </button>
                    </div>

                    <div style={{ color: C.textDim, fontSize: 10, marginTop: 6 }}>
                      🖼 Gemini Flash → Anthropic (vision waterfall) · 📋 Anthropic → Gemini → Groq (text waterfall) · 📎 Gemini Flash → Anthropic (vision waterfall) · 🛡 Dilisense (120+ lists) · 🔐 Serpro (Brazilian government) · 📸 Socure (ID & Selfie verification)
                    </div>

                    {/* ── Serpro Verification Modal ── */}
                    {serproModal.open && (
                      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
                        <div style={{ background: C.surface, borderRadius: 12, padding: 24, maxWidth: 400, width: "90%", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
                          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 20, color: C.text }}>Verify Serpro Information</div>

                          <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", fontSize: 12, color: C.textDim, marginBottom: 6, letterSpacing: "0.05em", textTransform: "uppercase" }}>CPF</label>
                            <input
                              type="text"
                              value={serproModal.cpf}
                              onChange={(e) => setSerproModal(p => ({ ...p, cpf: e.target.value }))}
                              style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surfaceAlt, color: C.text, fontSize: 14 }}
                              placeholder="CPF (e.g., 12345678901)"
                            />
                          </div>

                          <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", fontSize: 12, color: C.textDim, marginBottom: 6, letterSpacing: "0.05em", textTransform: "uppercase" }}>Name</label>
                            <input
                              type="text"
                              value={serproModal.name}
                              onChange={(e) => setSerproModal(p => ({ ...p, name: e.target.value }))}
                              style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surfaceAlt, color: C.text, fontSize: 14 }}
                              placeholder="Full name"
                            />
                          </div>

                          <div style={{ marginBottom: 20 }}>
                            <label style={{ display: "block", fontSize: 12, color: C.textDim, marginBottom: 6, letterSpacing: "0.05em", textTransform: "uppercase" }}>Date of Birth</label>
                            <input
                              type="date"
                              value={serproModal.dob}
                              onChange={(e) => setSerproModal(p => ({ ...p, dob: e.target.value }))}
                              style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surfaceAlt, color: C.text, fontSize: 14 }}
                            />
                          </div>

                          <div style={{ display: "flex", gap: 12 }}>
                            <button
                              onClick={() => setSerproModal({ open: false, cpf: "", name: "", dob: "", submissionId: null })}
                              style={{ flex: 1, padding: "10px 16px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surfaceAlt, color: C.text, cursor: "pointer", fontSize: 14, fontWeight: 500 }}>
                              Cancel
                            </button>
                            <button
                              onClick={confirmSerproVerification}
                              disabled={!serproModal.cpf || !serproModal.dob}
                              style={{ flex: 1, padding: "10px 16px", border: "none", borderRadius: 6, background: C.primary, color: "white", cursor: !serproModal.cpf || !serproModal.dob ? "not-allowed" : "pointer", fontSize: 14, fontWeight: 500, opacity: !serproModal.cpf || !serproModal.dob ? 0.5 : 1 }}>
                              Verify with Serpro
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {isMockSub && (
                    <div style={{ color: C.textDim, fontSize: 11, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7, padding: "10px 12px", marginTop: 10 }}>
                      {t('onboarding.dashboard.aiRealOnly')}
                    </div>
                  )}

                  {/* Error states */}
                  {analysis?.error    && <div style={{ color: C.danger, fontSize: 12, background: `${C.danger}10`, border: `1px solid ${C.danger}30`, borderRadius: 7, padding: "10px 12px", marginTop: 10 }}>✗ ID & Selfie: {analysis.error}</div>}
                  {dataAnal?.error    && <div style={{ color: C.danger, fontSize: 12, background: `${C.danger}10`, border: `1px solid ${C.danger}30`, borderRadius: 7, padding: "10px 12px", marginTop: 10 }}>✗ Data Only: {dataAnal.error}</div>}
                  {docsAnal?.error    && <div style={{ color: C.danger, fontSize: 12, background: `${C.danger}10`, border: `1px solid ${C.danger}30`, borderRadius: 7, padding: "10px 12px", marginTop: 10 }}>✗ Supporting Docs: {docsAnal.error}</div>}

                  {/* Results — each renders independently */}
                  <AnalysisResult result={analysis}  label="ID & SELFIE ANALYSIS" />
                  <AnalysisResult result={dataAnal}  label="DATA-ONLY ANALYSIS" />
                  <AnalysisResult result={docsAnal}  label="SUPPORTING DOCUMENTS ANALYSIS" />

                  {/* ── External Research / OSINT ── */}
                  <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
                    <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 10 }}>EXTERNAL RESEARCH & OSINT</div>
                    <ExternalResearch
                      subjectName={selected.business_name
                        ? `${selected.business_name}`
                        : `${selected.first_name || ""} ${selected.last_name || ""}`.trim()}
                      subjectType={selected.type === "business" ? "Business" : "Individual"}
                      subjectCountry={selected.country || selected.incorporation_country || ""}
                    />

                    {/* ── Email Breach Checking (HIBP) ── */}
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                        <button
                          onClick={() => checkHibpEmail(selected.id, selected.email)}
                          disabled={hibpLoading[selected.id]}
                          style={{
                            padding: "6px 12px",
                            fontSize: 11,
                            borderRadius: 6,
                            border: `1px solid ${C.accent}`,
                            background: hibpLoading[selected.id] ? `${C.accent}40` : "transparent",
                            color: C.accent,
                            cursor: hibpLoading[selected.id] ? "not-allowed" : "pointer",
                            opacity: hibpLoading[selected.id] ? 0.6 : 1
                          }}
                        >
                          {hibpLoading[selected.id] ? "Checking..." : "Check Email Breaches (HIBP)"}
                        </button>
                      </div>
                      {selected.id && hibpResult[selected.id] && (
                        <div style={{
                          background: hibpResult[selected.id]?.error ? `${C.danger}0A` : hibpResult[selected.id]?.status === "clean" ? `${C.success}0A` : `${C.danger}0A`,
                          border: `1px solid ${hibpResult[selected.id]?.error ? `${C.danger}30` : hibpResult[selected.id]?.status === "clean" ? `${C.success}30` : `${C.danger}30`}`,
                          borderRadius: 6,
                          padding: "10px 12px",
                          fontSize: 11
                        }}>
                          {hibpResult[selected.id]?.error ? (
                            <div style={{ color: C.danger }}>✗ Error: {hibpResult[selected.id]?.error}</div>
                          ) : hibpResult[selected.id]?.status === "clean" ? (
                            <div style={{ color: C.success }}>✓ Email not found in breaches</div>
                          ) : (
                            <>
                              <div style={{ color: C.danger, fontWeight: 700, marginBottom: 6 }}>⚠ Found in {hibpResult[selected.id]?.foundCount} breach(es)</div>
                              {hibpResult[selected.id]?.breaches?.slice(0, 3).map((b, i) => (
                                <div key={i} style={{ color: C.text, marginBottom: 4, fontSize: 10 }}>
                                  · {b.name} ({b.date}) — Risk: {hibpResult[selected.id]?.riskLevel}
                                </div>
                              ))}
                              {hibpResult[selected.id]?.foundCount > 3 && (
                                <div style={{ color: C.textDim, fontSize: 10, marginTop: 4 }}>+{hibpResult[selected.id]?.foundCount - 3} more breaches</div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Phone Validation & Scam Detection ── */}
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                        <button
                          onClick={() => checkPhone(selected.id, selected.phone, selected.nationality || "US")}
                          disabled={phoneLoading[selected.id]}
                          style={{
                            padding: "6px 12px",
                            fontSize: 11,
                            borderRadius: 6,
                            border: `1px solid ${C.accent}`,
                            background: phoneLoading[selected.id] ? `${C.accent}40` : "transparent",
                            color: C.accent,
                            cursor: phoneLoading[selected.id] ? "not-allowed" : "pointer",
                            opacity: phoneLoading[selected.id] ? 0.6 : 1
                          }}
                        >
                          {phoneLoading[selected.id] ? "Validating..." : "Validate Phone"}
                        </button>
                      </div>
                      {selected.id && phoneResult[selected.id] && (
                        <div style={{
                          background: phoneResult[selected.id]?.error ? `${C.danger}0A` : phoneResult[selected.id]?.status === "invalid" ? `${C.danger}0A` : phoneResult[selected.id]?.isPossiblyFake ? `${C.warn}0A` : `${C.success}0A`,
                          border: `1px solid ${phoneResult[selected.id]?.error ? `${C.danger}30` : phoneResult[selected.id]?.status === "invalid" ? `${C.danger}30` : phoneResult[selected.id]?.isPossiblyFake ? `${C.warn}30` : `${C.success}30`}`,
                          borderRadius: 6,
                          padding: "10px 12px",
                          fontSize: 11
                        }}>
                          {phoneResult[selected.id]?.error ? (
                            <div style={{ color: C.danger }}>✗ Error: {phoneResult[selected.id]?.error}</div>
                          ) : phoneResult[selected.id]?.status === "invalid" ? (
                            <div style={{ color: C.danger }}>✗ Invalid phone number format</div>
                          ) : (
                            <>
                              <div style={{ color: phoneResult[selected.id]?.isPossiblyFake ? C.warn : C.success, fontWeight: 700, marginBottom: 6 }}>
                                {phoneResult[selected.id]?.isPossiblyFake ? "⚠ Suspicious" : "✓ Valid"} · {phoneResult[selected.id]?.formatted}
                              </div>
                              <div style={{ color: C.textDim, fontSize: 10, marginBottom: 4 }}>· Type: {phoneResult[selected.id]?.type || "Unknown"} ({phoneResult[selected.id]?.isMobile ? "Mobile" : "Landline"})</div>
                              {phoneResult[selected.id]?.carrier && (
                                <div style={{ color: C.textDim, fontSize: 10, marginBottom: 4 }}>· Carrier: {phoneResult[selected.id]?.carrier}</div>
                              )}
                              {phoneResult[selected.id]?.isPossiblyFake && (
                                <div style={{ color: C.warn, fontSize: 10, marginTop: 4 }}>⚠ Reason: {phoneResult[selected.id]?.suspiciousReason}</div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── IP Address Checking ── */}
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                        <button
                          onClick={() => checkIp(selected.id, selected.ip_address)}
                          disabled={ipLoading[selected.id]}
                          style={{
                            padding: "6px 12px",
                            fontSize: 11,
                            borderRadius: 6,
                            border: `1px solid ${C.accent}`,
                            background: ipLoading[selected.id] ? `${C.accent}40` : "transparent",
                            color: C.accent,
                            cursor: ipLoading[selected.id] ? "not-allowed" : "pointer",
                            opacity: ipLoading[selected.id] ? 0.6 : 1
                          }}
                        >
                          {ipLoading[selected.id] ? "Checking..." : "Check IP Address"}
                        </button>
                      </div>
                      {selected.id && ipResult[selected.id] && (
                        <div style={{
                          background: ipResult[selected.id]?.error ? `${C.danger}0A` : ipResult[selected.id]?.riskLevel === "CRITICAL" ? `${C.danger}0A` : ipResult[selected.id]?.riskLevel === "HIGH" ? `${C.warn}0A` : ipResult[selected.id]?.riskLevel === "MEDIUM" ? `${C.warn}0A` : `${C.success}0A`,
                          border: `1px solid ${ipResult[selected.id]?.error ? `${C.danger}30` : ipResult[selected.id]?.riskLevel === "CRITICAL" ? `${C.danger}30` : ipResult[selected.id]?.riskLevel === "HIGH" ? `${C.warn}30` : ipResult[selected.id]?.riskLevel === "MEDIUM" ? `${C.warn}30` : `${C.success}30`}`,
                          borderRadius: 6,
                          padding: "10px 12px",
                          fontSize: 11
                        }}>
                          {ipResult[selected.id]?.error ? (
                            <div style={{ color: C.danger }}>✗ Error: {ipResult[selected.id]?.error}</div>
                          ) : ipResult[selected.id]?.status === "invalid" ? (
                            <div style={{ color: C.danger }}>✗ Invalid IP address</div>
                          ) : (
                            <>
                              <div style={{ color: ipResult[selected.id]?.riskLevel === "CRITICAL" ? C.danger : ipResult[selected.id]?.riskLevel === "HIGH" ? C.warn : C.success, fontWeight: 700, marginBottom: 6 }}>
                                {ipResult[selected.id]?.riskLevel === "CRITICAL" ? "🔴" : ipResult[selected.id]?.riskLevel === "HIGH" ? "⚠️" : ipResult[selected.id]?.riskLevel === "MEDIUM" ? "🟡" : "✓"} Risk: {ipResult[selected.id]?.riskLevel || "LOW"}
                              </div>
                              <div style={{ color: C.textDim, fontSize: 10, marginBottom: 4 }}>
                                · {ipResult[selected.id]?.city}, {ipResult[selected.id]?.country} ({ipResult[selected.id]?.countryCode})
                              </div>
                              {ipResult[selected.id]?.isp && (
                                <div style={{ color: C.textDim, fontSize: 10, marginBottom: 4 }}>· ISP: {ipResult[selected.id]?.isp}</div>
                              )}
                              {ipResult[selected.id]?.concerns?.length > 0 && (
                                <div style={{ color: C.danger, fontSize: 10, marginTop: 4 }}>
                                  {ipResult[selected.id]?.concerns?.map((c, i) => (
                                    <div key={i}>⚠ {c}</div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Sanctions results ── */}
                  {(() => {
                    const sr = sanctionsResults[selected.id];
                    if (!sr) return null;
                    if (sr.error) return <div style={{ color: C.danger, fontSize: 12, background: `${C.danger}10`, border: `1px solid ${C.danger}30`, borderRadius: 7, padding: "10px 12px", marginTop: 10 }}>✗ Sanctions: {sr.error}</div>;
                    const overallColor = sr.overallClear ? C.success : C.danger;
                    const SanctionBadge = ({ items, label }) => items?.length > 0 ? (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ color: C.danger, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>⚠ {label} ({items.length} match{items.length !== 1 ? "es" : ""})</div>
                        {items.slice(0,3).map((m,i) => (
                          <div key={i} style={{ background: `${C.danger}0A`, border: `1px solid ${C.danger}22`, borderRadius: 5, padding: "6px 10px", marginBottom: 4, fontSize: 11 }}>
                            <span style={{ color: C.text, fontWeight: 600 }}>{m.name || m.full_name || "Unknown"}</span>
                            {m.country && <span style={{ color: C.textDim, marginLeft: 6 }}>· {m.country}</span>}
                            {m.score != null && <span style={{ color: C.warn, marginLeft: 6 }}>· {m.score}% match</span>}
                          </div>
                        ))}
                        {items.length > 3 && <div style={{ color: C.textDim, fontSize: 10 }}>+{items.length-3} more on Sanctions page</div>}
                      </div>
                    ) : null;
                    return (
                      <div style={{ background: `${overallColor}0A`, border: `1px solid ${overallColor}33`, borderRadius: 10, padding: "14px 16px", marginTop: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700 }}>🛡 SANCTIONS SCREENING</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ color: overallColor, fontSize: 13, fontWeight: 800 }}>{sr.overallClear ? "✓ CLEAR" : "⚠ MATCH FOUND"}</span>
                            <span style={{ color: C.textDim, fontSize: 10 }}>{sr.screenedAt ? new Date(sr.screenedAt).toLocaleString() : ""}</span>
                          </div>
                        </div>
                        <div style={{ marginBottom: sr.business ? 10 : 0 }}>
                          <div style={{ color: C.textMid, fontSize: 10, marginBottom: 4 }}>INDIVIDUAL: {sr.individual?.name}</div>
                          {sr.individual?.totalHits === 0
                            ? <div style={{ color: C.success, fontSize: 12 }}>✓ No matches found across 120+ lists</div>
                            : <><SanctionBadge items={sr.individual?.sanctions} label="Sanctions" /><SanctionBadge items={sr.individual?.pep} label="PEP" /><SanctionBadge items={sr.individual?.criminal} label="Criminal" /></>
                          }
                        </div>
                        {sr.business && (
                          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                            <div style={{ color: C.textMid, fontSize: 10, marginBottom: 4 }}>BUSINESS: {sr.business?.name}</div>
                            {sr.business?.totalHits === 0
                              ? <div style={{ color: C.success, fontSize: 12 }}>✓ No matches found</div>
                              : <><SanctionBadge items={sr.business?.sanctions} label="Sanctions" /><SanctionBadge items={sr.business?.pep} label="PEP" /><SanctionBadge items={sr.business?.criminal} label="Criminal" /></>
                            }
                          </div>
                        )}
                        <div style={{ color: C.textDim, fontSize: 10, marginTop: 10 }}>Results saved to Sanctions Screening page · Powered by Dilisense</div>
                      </div>
                    );
                  })()}
                </div>
              );
            })()}

            {/* Socure Document Verification Results */}
            {(() => {
              const sr = socureResults[selected.id];
              if (!sr) return null;

              // Get full Socure result from database if available
              const fullResult = selected.socure_result ? (typeof selected.socure_result === 'string' ? JSON.parse(selected.socure_result) : selected.socure_result) : null;

              const isVerified = sr.decision === "ACCEPT" || sr.status === "completed";
              const decisionColor = sr.decision === "ACCEPT" ? C.success : sr.decision === "REJECT" ? C.danger : C.warn;
              return (
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700 }}>📸 SOCURE DOCUMENT VERIFICATION</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: decisionColor, fontSize: 13, fontWeight: 800 }}>
                        {sr.error ? "✗ ERROR" : sr.decision === "ACCEPT" ? "✓ VERIFIED" : sr.decision === "REJECT" ? "✗ REJECTED" : "⏳ REVIEW"}
                      </span>
                      <span style={{ color: C.textDim, fontSize: 10 }}>{sr.verified_at ? new Date(sr.verified_at).toLocaleString() : ""}</span>
                    </div>
                  </div>
                  {sr.error ? (
                    <div style={{ color: C.danger, fontSize: 12 }}>Error: {sr.error}</div>
                  ) : (
                    <>
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ color: C.textMid, fontSize: 11, marginBottom: 4 }}>Reference ID: <span style={{ color: C.text, fontFamily: "'DM Mono', monospace" }}>{sr.reference_id || "—"}</span></div>
                        <div style={{ color: C.textMid, fontSize: 11, marginBottom: 4 }}>Status: <span style={{ color: C.text, fontWeight: 600 }}>{sr.status || "—"}</span></div>
                        {sr.confidence_score !== null && (
                          <div style={{ color: C.textMid, fontSize: 11 }}>Confidence Score: <span style={{ color: decisionColor, fontWeight: 700 }}>{Math.round((sr.confidence_score || 0) * 100)}%</span></div>
                        )}
                      </div>
                      {fullResult?.tags && fullResult.tags.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ color: C.textMid, fontSize: 10, marginBottom: 6 }}>RISK TAGS</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {fullResult.tags.map(tag => (
                              <span key={tag} style={{ background: `${C.danger}20`, color: C.danger, fontSize: 10, padding: "3px 8px", borderRadius: 3, fontWeight: 600 }}>
                                ⚠ {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {fullResult?.sub_status && (
                        <div style={{ color: C.textMid, fontSize: 11, marginBottom: 8 }}>
                          Details: <span style={{ color: C.text }}>{fullResult.sub_status}</span>
                        </div>
                      )}
                      <div style={{ color: C.textDim, fontSize: 10, marginTop: 10 }}>Verification powered by Socure DocV</div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* IP Geolocation mismatch warning */}
            {selected.ip_address && selected.ip_country_code && selected.country && selected.ip_country_code !== (selected.country || "").slice(0, 2).toUpperCase() && (() => {
              // Build a simple country→code map for declared country comparison
              const declaredCode = selected.ip_country_code; // reuse for check below
              const ipCode = selected.ip_country_code;
              // Compare declared country name against ip_country
              const mismatch = selected.ip_country && selected.country &&
                selected.ip_country.toLowerCase() !== selected.country.toLowerCase();
              if (!mismatch) return null;
              return (
                <div style={{ background: `${C.warn}18`, border: `1px solid ${C.warn}50`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 16 }}>⚠</span>
                  <div>
                    <div style={{ color: C.warn, fontSize: 12, fontWeight: 700 }}>IP Country Mismatch</div>
                    <div style={{ color: C.textMid, fontSize: 11, marginTop: 2 }}>
                      Declared country: <strong>{selected.country}</strong> · IP detected: <strong>{selected.ip_country} ({selected.ip_city || "unknown city"})</strong>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Info tables */}
            {[
              { title: "PERSONAL INFORMATION", rows: [["Full Name", `${selected.first_name} ${selected.last_name}`], ["Date of Birth", selected.dob], ["Nationality", selected.nationality], ["Email", selected.email], ["Phone", selected.phone], ["Tax ID", selected.tax_id || "—"], ["Job Title", selected.job_title || "—"], ["Employer", selected.employer_name || "—"], ["Purpose of Account", selected.purpose_of_account || "—"], ...(selected.wallet_id ? [["Wallet ID", selected.wallet_id]] : [])] },
              { title: "ADDRESS", rows: [["Line 1", selected.address_line1], ["Line 2", selected.address_line2 || "—"], ["City", selected.city], ["Post Code", selected.post_code || "—"], ["Country", selected.country]] },
              ...(selected.type === "business" ? [{ title: "BUSINESS", rows: [["Business Name", selected.business_name], ["Reg Number", selected.reg_number], ["Type", selected.business_type], ["Country", selected.incorporation_country], ["Website", selected.website || "—"], ["Directors/UBOs", (selected.ubos || selected.directors || []).length ? (selected.ubos || []).map(u => `${u.name} (${u.role}${u.ownership ? ` · ${u.ownership}%` : ""})`).join(", ") || (selected.directors || []).join(", ") : "—"]] }] : []),
              { title: "COMPLIANCE", rows: [["Source of Funds", selected.source_of_funds], ["Annual Income", selected.annual_income || "—"], ["PEP Declaration", selected.pep_declaration], ["PEP Details", selected.pep_details || "—"], ["Sanctions", selected.sanctions_declaration]] },
              ...(selected.ip_address ? [{ title: "IP & GEOLOCATION", rows: [["IP Address", selected.ip_address], ["Detected City", selected.ip_city || "—"], ["Detected Country", selected.ip_country || "—"], ["Country Code", selected.ip_country_code || "—"]] }] : []),
            ].map(section => (
              <div key={section.title} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 }}>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 12 }}>{section.title}</div>
                {section.rows.map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
                    <span style={{ color: C.textDim }}>{k}</span>
                    <span style={{ color: C.text, fontFamily: "'DM Mono', monospace", maxWidth: "60%", textAlign: "right" }}>{v || "—"}</span>
                  </div>
                ))}
              </div>
            ))}

            {/* E-014 / F-003: Beneficial Ownership Verification Attestation */}
            {selected.type === "business" && (() => {
              const ubos = (() => { try { return Array.isArray(selected.ubos) ? selected.ubos : JSON.parse(selected.ubos || "[]"); } catch { return []; } })();
              if (ubos.length === 0) return null;
              const allAttested = ubos.every((_, i) => uboAttestation[i]);
              return (
                <div style={{ background: C.surface, border: `1px solid ${allAttested ? "rgba(0,230,118,0.35)" : C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em" }}>BENEFICIAL OWNERS — VERIFICATION ATTESTATION</div>
                    {allAttested && <span style={{ fontSize: 10, color: C.success, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>✓ ALL VERIFIED</span>}
                  </div>
                  <div style={{ color: C.textDim, fontSize: 11, marginBottom: 12, lineHeight: 1.6 }}>
                    Tick each beneficial owner once their identity and ownership percentage have been independently verified (FATF R.10 / 4AMLD Art.13). Approval is gated until all UBOs are attested.
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {ubos.map((u, i) => {
                      const checked = !!uboAttestation[i];
                      const isHighOwnership = parseFloat(u.ownership) >= 25;
                      return (
                        <label key={i} onClick={() => setUboAttestation(p => ({ ...p, [i]: !p[i] }))} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: checked ? "rgba(0,230,118,0.06)" : C.bg, border: `1px solid ${checked ? "rgba(0,230,118,0.3)" : C.border}`, borderRadius: 8, cursor: "pointer", transition: "all 0.12s" }}>
                          <div style={{ width: 18, height: 18, borderRadius: 4, background: checked ? C.success : "transparent", border: `2px solid ${checked ? C.success : C.textDim}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.12s" }}>
                            {checked && <span style={{ color: C.bg, fontSize: 11, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>{u.name}</span>
                              <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: u.role?.includes("UBO") ? "rgba(255,193,7,0.12)" : C.surfaceAlt, color: u.role?.includes("UBO") ? C.gold : C.textMid, border: `1px solid ${u.role?.includes("UBO") ? "rgba(255,193,7,0.3)" : C.border}` }}>
                                {u.role}
                              </span>
                              {isHighOwnership && <span style={{ fontSize: 10, color: C.gold }}>⚑ UBO ≥25%</span>}
                            </div>
                            <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
                              {u.ownership ? `${u.ownership}% ownership` : "Ownership not specified"} · Identity verification required
                            </div>
                          </div>
                          {checked && <span style={{ color: C.success, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>Attested</span>}
                        </label>
                      );
                    })}
                  </div>
                  {!allAttested && selected.status === "pending" && (
                    <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(255,140,0,0.08)", border: "1px solid rgba(255,140,0,0.25)", borderRadius: 7, color: C.warn, fontSize: 11 }}>
                      ⚠ {ubos.filter((_, i) => !uboAttestation[i]).length} beneficial owner{ubos.filter((_, i) => !uboAttestation[i]).length !== 1 ? "s" : ""} pending attestation — approval is blocked until all are verified.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Additional documents */}
            {(ensureArray(selected.additional_docs).length > 0) && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em" }}>SUPPORTING DOCUMENTS ({ensureArray(selected.additional_docs).length})</div>
                  {(ensureArray(selected.additional_docs).some(d => d.data && !d.data.startsWith("data:image/svg")) || ensureArray(selected.additional_doc_paths).length > 0 || ensureArray(selected.additional_docs_resolved).length > 0) && (
                    <button
                      onClick={() => runAiAnalysis(selected, "docs")}
                      disabled={!!aiLoading[`${selected.id}_docs`] || selected.id?.startsWith("mock-")}
                      style={{ padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", background: C.accentGlow, border: `1px solid ${C.borderLight}`, color: C.accent, display: "flex", alignItems: "center", gap: 5 }}>
                      {aiLoading[`${selected.id}_docs`]
                        ? <><span style={{ display: "inline-block", width: 10, height: 10, border: `2px solid ${C.border}`, borderTopColor: C.accent, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Analysing…</>
                        : "📎 Analyse Docs"}
                    </button>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {ensureArray(selected.additional_docs_resolved || selected.additional_docs).map((doc, i) => {
                    const docDef = ADDITIONAL_DOC_TYPES?.find(d => d.value === doc.type);
                    const src = doc.signedUrl || doc.data || null;
                    const isImg = src && !doc.name?.endsWith(".pdf");
                    const canOpen = !!src;
                    return (
                      <div key={i} onClick={() => {
                        if (!canOpen) return;
                        const w = window.open("", "_blank");
                        if (w) {
                          if (src.startsWith("data:") || src.includes("supabase")) {
                            w.document.write(`<html><body style="margin:0;background:#000"><img src="${src}" style="max-width:100%;display:block;margin:auto" /><br/><a href="${src}" download style="color:white">Download</a></body></html>`);
                          } else {
                            w.location.href = src;
                          }
                        }
                      }}
                        style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", cursor: canOpen ? "pointer" : "default" }}>
                        {src && isImg && (
                          <img src={src} alt={doc.label} style={{ width: "100%", maxHeight: 140, objectFit: "cover", display: "block" }} />
                        )}
                        {src && !isImg && (
                          <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: 28 }}>📄</span>
                            <span style={{ color: C.accent, fontSize: 10 }}>Click to open</span>
                          </div>
                        )}
                        {!src && (
                          <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, border: `1px dashed ${C.border}` }}>
                            <span style={{ color: C.textDim, fontSize: 12 }}>{t('onboarding.dashboard.notUploaded')}</span>
                          </div>
                        )}
                        <div style={{ padding: "10px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 14 }}>{docDef?.icon || "📎"}</span>
                            <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{doc.label}</span>
                          </div>
                          {doc.name && <div style={{ color: C.textDim, fontSize: 10, marginTop: 3 }}>{doc.name}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Audit trail */}
            {auditLogs && Array.isArray(auditLogs) && auditLogs.length > 0 && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 12 }}>AUDIT TRAIL</div>
                {auditLogs.map((log, i) => (
                  <div key={log.id || i} style={{ padding: "10px 0", borderBottom: i < auditLogs.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ color: log.action === "approve" ? C.success : log.action === "reject" ? C.danger : C.gold, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
                        {log.action === "approve" ? "✓ Approved" : log.action === "reject" ? "✕ Rejected" : "✎ Info Requested"}
                      </span>
                      <span style={{ color: C.textDim, fontSize: 11 }}>{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                    {log.reviewer && <div style={{ color: C.textDim, fontSize: 11 }}>by {log.reviewer.first_name} {log.reviewer.last_name}</div>}
                    {log.note && <div style={{ color: C.textMid, fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>{log.note}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Actions */}
          <div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, position: "sticky", top: 20 }}>
              <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 14 }}>{t('onboarding.dashboard.reviewDecision')}</div>

              {selected.status === "pending" ? (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>{t('onboarding.dashboard.notesOptional')}</div>
                    <textarea value={actionNote} onChange={e => setActionNote(e.target.value)} placeholder={t('onboarding.dashboard.notesPlaceholder')}
                      rows={4} style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 12, padding: "9px 11px", fontFamily: "'DM Mono', monospace", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }} />
                  </div>
                  {/* Risk Rating selector — saved on approval */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>{t('onboarding.dashboard.riskRating')}</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {["Low", "Medium", "High"].map(r => {
                        const color = r === "High" ? C.danger : r === "Medium" ? C.warn : C.success;
                        const active = actionRisk === r;
                        return (
                          <button key={r} onClick={() => setActionRisk(r)} style={{
                            flex: 1, padding: "7px 0", borderRadius: 7, fontSize: 11, fontWeight: 700,
                            fontFamily: "'DM Mono', monospace", cursor: "pointer",
                            background: active ? color + "22" : "transparent",
                            border: `1px solid ${active ? color : C.border}`,
                            color: active ? color : C.textDim,
                            transition: "all 0.12s",
                          }}>
                            {r === "Low" ? t('common.low') : r === "Medium" ? t('common.medium') : t('common.high')}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Account Number — auto-generated on approval */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>ACCOUNT NUMBER (Auto-Generated)</div>
                    <div style={{
                      width: "100%",
                      background: `${C.accent}10`,
                      border: `1px solid ${C.accent}40`,
                      borderRadius: 7,
                      color: C.accent,
                      fontSize: 14,
                      fontWeight: 600,
                      padding: "10px 11px",
                      fontFamily: "'DM Mono', monospace",
                      boxSizing: "border-box"
                    }}>
                      {accountNumber || "Will generate: XXXXXXXXXX"}
                    </div>
                    {!accountNumber && (
                      <div style={{ color: C.textDim, fontSize: 10, marginTop: 6 }}>
                        A random 10-digit account number will be assigned upon approval
                      </div>
                    )}
                  </div>
                  {/* Assign to Analyst */}
                  {teamMembers.filter(m => m.role === "analyst" || m.role === "supervisor").length > 0 && (
                    <div style={{ marginBottom: 14, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}>
                      <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.08em", marginBottom: 7 }}>ASSIGN TO ANALYST</div>
                      {selected.assigned_to ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ color: C.success, fontSize: 12 }}>✓ Assigned to {(() => { const m = teamMembers.find(x => x.id === selected.assigned_to); return m ? (m.full_name || m.email) : "Analyst"; })()}</span>
                          <button onClick={() => setSelected(p => ({ ...p, assigned_to: null }))} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 11, padding: 0 }}>reassign</button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 6 }}>
                          <select value={assignTo} onChange={e => setAssignTo(e.target.value)}
                            style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 9px", fontFamily: "'DM Mono', monospace", outline: "none" }}>
                            <option value="">— Select analyst —</option>
                            {teamMembers.filter(m => m.role === "analyst" || m.role === "supervisor").map(m => (
                              <option key={m.id} value={m.id}>{m.full_name || m.email} ({m.role})</option>
                            ))}
                          </select>
                          <button onClick={handleAssign} disabled={!assignTo || assigning}
                            style={{ padding: "7px 13px", background: assignTo ? C.accentGlow : "transparent", border: `1px solid ${assignTo ? C.accent : C.border}`, borderRadius: 6, color: assignTo ? C.accent : C.textDim, cursor: assignTo ? "pointer" : "default", fontSize: 12, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>
                            {assigning ? "…" : "Assign"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {/* E-014: UBO attestation gate warning */}
                  {selected.type === "business" && (() => {
                    const ubos = (() => { try { return Array.isArray(selected.ubos) ? selected.ubos : JSON.parse(selected.ubos || "[]"); } catch { return []; } })();
                    if (ubos.length === 0) return null;
                    const unattested = ubos.filter((_, i) => !uboAttestation[i]).length;
                    if (unattested === 0) return null;
                    return (
                      <div style={{ padding: "8px 12px", background: "rgba(255,140,0,0.08)", border: "1px solid rgba(255,140,0,0.25)", borderRadius: 7, color: C.warn, fontSize: 11, marginBottom: 4 }}>
                        ⚠ Verify {unattested} UBO{unattested !== 1 ? "s" : ""} before approving
                      </div>
                    );
                  })()}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <button onClick={() => handleAction("approve")} disabled={actioning} style={{ padding: "11px", background: `${C.success}18`, border: `1px solid ${C.success}50`, borderRadius: 8, color: C.success, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                      ✓ {t('onboarding.dashboard.approve')}
                    </button>
                    <button onClick={() => { setInfoLink(null); setShowInfoModal(true); }} disabled={actioning} style={{ padding: "11px", background: `${C.gold}18`, border: `1px solid ${C.gold}50`, borderRadius: 8, color: C.gold, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                      ✎ {t('onboarding.dashboard.requestMoreInfo')}
                    </button>
                    <button onClick={() => handleAction("reject")} disabled={actioning} style={{ padding: "11px", background: `${C.danger}10`, border: `1px solid ${C.danger}40`, borderRadius: 8, color: C.danger, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                      ✕ {t('onboarding.dashboard.reject')}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ padding: "14px 0" }}>
                  <div style={{ color: statusColor[selected.status] || C.textDim, fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                    {statusLabel[selected.status]}
                  </div>
                  {(selected.review_note || selected.review_notes) && <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.6 }}>{selected.review_note || selected.review_notes}</div>}
                  {(selected.last_reviewed_at || selected.reviewed_at) && <div style={{ color: C.textDim, fontSize: 11, marginTop: 8 }}>{new Date(selected.last_reviewed_at || selected.reviewed_at).toLocaleString()}</div>}
                  <button onClick={async () => {
                    const payload = { status: "pending", last_reviewed_by: null, last_reviewed_at: null, review_note: null };
                    try {
                      const result = await db.from("kyc_submissions").update(payload).eq("id", selected.id).execute();
                      if (result.error?.message?.includes("last_reviewed")) {
                        await db.from("kyc_submissions").update({ status: "pending" }).eq("id", selected.id).execute();
                      }
                      setSelected(p => ({ ...p, status: "pending" }));
                      setSubmissions(prev => prev.map(s => s.id === selected.id ? { ...s, status: "pending" } : s));
                    } catch (e) { console.error("[KYC] Reset error:", e); }
                  }}
                    style={{ marginTop: 14, width: "100%", padding: "9px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
                    ↺ {t('onboarding.dashboard.reOpenForReview')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* ── Info Request Modal ── */}
      {showInfoModal && (
        <div onClick={e => { if (e.target === e.currentTarget) { setShowInfoModal(false); setInfoLink(null); } }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }}>
            {!infoLink ? (
              <>
                <h3 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700, margin: "0 0 6px" }}>{t('onboarding.infoModal.title')}</h3>
                <p style={{ color: C.textMid, fontSize: 12, margin: "0 0 20px" }}>{t('onboarding.infoModal.desc')}</p>

                {/* Checkboxes */}
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                  {[
                    { key: "id_front", label: `🪪 ${t('onboarding.infoModal.updatedIdFront')}`, desc: t('onboarding.infoModal.updatedIdFrontDesc') },
                    { key: "id_back",  label: `🪪 ${t('onboarding.infoModal.updatedIdBack')}`,  desc: t('onboarding.infoModal.updatedIdBackDesc') },
                    { key: "selfie",   label: `🤳 ${t('onboarding.infoModal.newSelfie')}`,          desc: t('onboarding.infoModal.newSelfieDesc') },
                    { key: "data",     label: `📋 ${t('onboarding.infoModal.updatedPersonalData')}`, desc: t('onboarding.infoModal.updatedPersonalDataDesc') },
                    { key: "pep_declaration", label: "⚠️ PEP Declaration", desc: "Confirm PEP status" },
                    { key: "sanctions_declaration", label: "🛡️ Sanctions Declaration", desc: "Confirm sanctions status" },
                  ].map(({ key, label, desc }) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: infoRequest[key] ? C.accentGlow : C.bg, border: `1px solid ${infoRequest[key] ? C.accent : C.border}`, borderRadius: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={!!infoRequest[key]} onChange={e => setInfoRequest(p => ({ ...p, [key]: e.target.checked }))}
                        style={{ width: 16, height: 16, accentColor: C.accent }} />
                      <div>
                        <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{label}</div>
                        <div style={{ color: C.textDim, fontSize: 11 }}>{desc}</div>
                      </div>
                    </label>
                  ))}

                  {/* Supporting docs — multi-select */}
                  <div style={{ padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>📎 {t('onboarding.infoModal.supportingDocs')}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {["Bank Statement", "Proof of Address", "Source of Funds Letter", "Business Registration", "Tax Certificate", "Company Accounts", "Director ID", "UBO Declaration", "Other"].map(docType => {
                        const checked = infoRequest.additional_docs.includes(docType);
                        return (
                          <label key={docType} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                            <input type="checkbox" checked={checked}
                              onChange={e => setInfoRequest(p => ({ ...p, additional_docs: e.target.checked ? [...p.additional_docs, docType] : p.additional_docs.filter(d => d !== docType) }))}
                              style={{ accentColor: C.accent }} />
                            <span style={{ color: checked ? C.accent : C.textMid, fontSize: 12 }}>{docType}</span>
                          </label>
                        );
                      })}
                    </div>
                    {infoRequest.additional_docs.includes("Other") && (
                      <input value={infoRequest.custom_doc} onChange={e => setInfoRequest(p => ({ ...p, custom_doc: e.target.value }))}
                        placeholder="Describe the document needed…"
                        style={{ marginTop: 8, width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "8px 10px", fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
                    )}
                  </div>
                </div>

                {/* Note to applicant */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>{t('onboarding.infoModal.noteLabel')}</label>
                  <textarea value={infoRequest.note} onChange={e => setInfoRequest(p => ({ ...p, note: e.target.value }))}
                    placeholder={t('onboarding.infoModal.notePlaceholder')}
                    rows={3}
                    style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 12, padding: "10px 12px", fontFamily: "'DM Mono', monospace", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => setShowInfoModal(false)}
                    style={{ flex: 1, padding: "11px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                    {t('common.cancel')}
                  </button>
                  <button onClick={submitInfoRequest} disabled={actioning || (!infoRequest.id_front && !infoRequest.id_back && !infoRequest.selfie && !infoRequest.data && !infoRequest.pep_declaration && !infoRequest.sanctions_declaration && infoRequest.additional_docs.length === 0)}
                    style={{ flex: 2, padding: "11px", background: C.gold, border: "none", borderRadius: 8, color: "#000", fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13, opacity: actioning ? 0.7 : 1 }}>
                    {actioning ? t('onboarding.infoModal.saving') : `✎ ${t('onboarding.infoModal.sendRequest')}`}
                  </button>
                </div>
              </>
            ) : (
              /* Link generated view */
              <>
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                  <h3 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700, margin: "0 0 6px" }}>{t('onboarding.infoModal.requestSent')}</h3>
                  <p style={{ color: C.textMid, fontSize: 12 }}>{t('onboarding.infoModal.requestSentDesc')}</p>
                </div>

                {/* What was requested */}
                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
                  <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.08em", marginBottom: 8 }}>{t('onboarding.infoModal.requestedFromApplicant')}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {infoRequest.id_front              && <span style={{ background: C.accentGlow, color: C.accent, fontSize: 10, padding: "2px 8px", borderRadius: 4 }}>{t('onboarding.front')} ID</span>}
                    {infoRequest.id_back               && <span style={{ background: C.accentGlow, color: C.accent, fontSize: 10, padding: "2px 8px", borderRadius: 4 }}>{t('onboarding.back_label')} ID</span>}
                    {infoRequest.selfie                && <span style={{ background: C.accentGlow, color: C.accent, fontSize: 10, padding: "2px 8px", borderRadius: 4 }}>{t('onboarding.selfieLabel')}</span>}
                    {infoRequest.data                  && <span style={{ background: C.accentGlow, color: C.accent, fontSize: 10, padding: "2px 8px", borderRadius: 4 }}>{t('onboarding.personalInformation')}</span>}
                    {infoRequest.pep_declaration       && <span style={{ background: C.accentGlow, color: C.accent, fontSize: 10, padding: "2px 8px", borderRadius: 4 }}>⚠️ PEP Declaration</span>}
                    {infoRequest.sanctions_declaration && <span style={{ background: C.accentGlow, color: C.accent, fontSize: 10, padding: "2px 8px", borderRadius: 4 }}>🛡️ Sanctions Declaration</span>}
                    {infoRequest.additional_docs.map(d => <span key={d} style={{ background: C.accentGlow, color: C.accent, fontSize: 10, padding: "2px 8px", borderRadius: 4 }}>{d}</span>)}
                  </div>
                  {infoRequest.note && <div style={{ color: C.textMid, fontSize: 11, marginTop: 8, fontStyle: "italic" }}>"{infoRequest.note}"</div>}
                </div>

                {/* Copyable link */}
                <div style={{ background: C.bg, border: `1px solid ${C.accent}44`, borderRadius: 8, padding: "10px 12px", marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ color: C.textMid, fontSize: 10, flex: 1, wordBreak: "break-all", fontFamily: "monospace" }}>{infoLink}</span>
                  <button onClick={() => navigator.clipboard.writeText(infoLink)}
                    style={{ padding: "6px 12px", background: C.accent, border: "none", borderRadius: 6, color: C.bg, fontWeight: 700, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>
                    {t('onboarding.infoModal.copyLink')}
                  </button>
                </div>

                <button onClick={() => { setShowInfoModal(false); setInfoLink(null); setInfoRequest({ id_front: false, id_back: false, selfie: false, data: false, additional_docs: [], custom_doc: "", note: "" }); }}
                  style={{ width: "100%", padding: "11px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                  {t('common.close')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      </>
    );
  }

  // ── Submission list ──
  const counts = {
    all:            submissions.length,
    pending:        submissions.filter(s => s.status === "pending").length,
    approved:       submissions.filter(s => s.status === "approved").length,
    rejected:       submissions.filter(s => s.status === "rejected").length,
    info_requested: submissions.filter(s => s.status === "info_requested").length,
  };

  return (
    <div style={{ fontFamily: "'DM Mono', monospace" }}>
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&display=swap" rel="stylesheet" />

      {/* ── Inline form modal ── */}
      {showForm && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.80)", zIndex: 1000, overflowY: "auto", padding: "24px 16px" }}>
          <div style={{ maxWidth: 660, margin: "0 auto", position: "relative" }}>
            <button onClick={() => setShowForm(false)} style={{ position: "sticky", top: 0, float: "right", zIndex: 10, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, padding: "6px 14px", cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace", marginBottom: 10 }}>
              ✕ Close
            </button>
            <div style={{ clear: "both" }}>
              <KYCOnboardingPortalInline
                onComplete={() => { setShowForm(false); loadSubmissions(); }}
                companyId={company?.id}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Settings modal ── */}
      {showSettings && (
        <div onClick={e => { if (e.target === e.currentTarget) setShowSettings(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.80)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <h2 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 700, margin: 0 }}>{t('onboarding.settings.title')}</h2>
                <p style={{ color: C.textMid, fontSize: 12, margin: "4px 0 0" }}>{t('onboarding.settings.desc')}</p>
              </div>
              <button onClick={() => setShowSettings(false)} style={{ background: "transparent", border: "none", color: C.textMid, cursor: "pointer", fontSize: 18, padding: "4px 8px" }}>✕</button>
            </div>

            {/* Webhook / API endpoint */}
            <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 16 }}>
              <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.1em", marginBottom: 12 }}>{t('onboarding.settings.webhookLabel')}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", display: "block", marginBottom: 5 }}>ENDPOINT URL</label>
                  <input value={apiSettings.webhookUrl || ""} onChange={e => saveApiSettings({ ...apiSettings, webhookUrl: e.target.value })}
                    placeholder="https://api.yourapp.com/webhooks/kyc"
                    style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 12, padding: "10px 12px", fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
                  <div style={{ color: C.textDim, fontSize: 11, marginTop: 4 }}>Receives a POST with applicant data + AI analysis on each submission</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", display: "block", marginBottom: 5 }}>AUTH HEADER NAME</label>
                    <input value={apiSettings.apiKeyHeader || ""} onChange={e => saveApiSettings({ ...apiSettings, apiKeyHeader: e.target.value })}
                      placeholder="Authorization"
                      style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 12, padding: "9px 12px", fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", display: "block", marginBottom: 5 }}>TOKEN PREFIX</label>
                    <input value={apiSettings.apiKeyPrefix || ""} onChange={e => saveApiSettings({ ...apiSettings, apiKeyPrefix: e.target.value })}
                      placeholder="Bearer"
                      style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 12, padding: "9px 12px", fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
                  </div>
                </div>
                <div>
                  <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", display: "block", marginBottom: 5 }}>API KEY / TOKEN</label>
                  <input type="password" value={apiSettings.apiKey || ""} onChange={e => saveApiSettings({ ...apiSettings, apiKey: e.target.value })}
                    placeholder="sk-••••••••••••••••"
                    style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 12, padding: "9px 12px", fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
                </div>
              </div>
            </div>

            {/* Push triggers */}
            <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 16 }}>
              <div style={{ color: C.accent, fontSize: 10, letterSpacing: "0.1em", marginBottom: 12 }}>{t('onboarding.settings.pushTriggersLabel')}</div>
              {[
                ["pushOnSubmit",   "On new submission",          "Immediately when a form is submitted"],
                ["pushOnApprove",  "On approval",                "When a reviewer approves an application"],
                ["pushOnReject",   "On rejection",               "When a reviewer rejects an application"],
                ["pushAiResult",   "Include AI analysis",        "Attach full AI document analysis to payload"],
              ].map(([key, label, hint]) => (
                <label key={key} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!apiSettings[key]} onChange={e => saveApiSettings({ ...apiSettings, [key]: e.target.checked })}
                    style={{ marginTop: 2, accentColor: C.accent, flexShrink: 0 }} />
                  <div>
                    <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{label}</div>
                    <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{hint}</div>
                  </div>
                </label>
              ))}
            </div>

            {/* Test button */}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={async () => {
                if (!apiSettings.webhookUrl) { alert(t('onboarding.settings.enterEndpointFirst')); return; }
                try {
                  const headers = { "Content-Type": "application/json" };
                  if (apiSettings.apiKey) headers[apiSettings.apiKeyHeader || "Authorization"] = apiSettings.apiKeyPrefix ? `${apiSettings.apiKeyPrefix} ${apiSettings.apiKey}` : apiSettings.apiKey;
                  const r = await fetch(apiSettings.webhookUrl, { method: "POST", headers, body: JSON.stringify({ event: "test", source: "CrossRegGuard", timestamp: new Date().toISOString() }) });
                  alert(r.ok ? `✓ Success (${r.status})` : `✗ Error: ${r.status} ${r.statusText}`);
                } catch(e) { alert("✗ " + e.message); }
              }} style={{ flex: 1, padding: "10px", background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 8, color: C.accent, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                ▶ {t('onboarding.settings.testEndpoint')}
              </button>
              <button onClick={() => setShowSettings(false)} style={{ flex: 1, padding: "10px", background: C.accent, border: "none", borderRadius: 8, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                {t('onboarding.settings.saveAndClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, margin: 0 }}>{t('onboarding.title')}</h1>
          <p style={{ color: C.textMid, margin: "4px 0 0", fontSize: 14 }}>{t('onboarding.dashboard.subtitle', { pending: counts.pending, approved: counts.approved, rejected: counts.rejected })}</p>
        </div>
        <div style={{ display: "flex", gap: 9 }}>
          <button onClick={() => setShowSettings(true)} style={{ padding: "8px 14px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>⚙ {t('onboarding.dashboard.settings')}</button>
          <a href={company?.id ? `/app?kyc=1&company=${company.id}` : `/app?kyc=1`} target="_blank" rel="noopener noreferrer"
            style={{ padding: "8px 16px", background: C.accent, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
            + {t('onboarding.dashboard.newApplication')} ↗
          </a>
          <button onClick={loadSubmissions} style={{ padding: "8px 14px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontSize: 12 }}>↻</button>
          <button
            onClick={() => { if (window.confirm("Clear all submissions from this view? (Real Supabase records are not deleted — only mock/local data is removed.)")) { setSubmissions(MOCK_SUBMISSIONS.length ? [] : []); setRealSubmissions([]); } }}
            style={{ padding: "8px 14px", background: "transparent", border: `1px solid ${C.danger}40`, borderRadius: 7, color: C.danger, cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
            🗑 {t('onboarding.dashboard.clearAll')}
          </button>
        </div>
      </div>

      {/* ── Onboarding link banner ── */}
      <div style={{ background: `${C.accent}08`, border: `1px solid ${C.accent}25`, borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ color: C.accent, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 4 }}>📋 {t('onboarding.dashboard.clientOnboardingLink')}</div>
            <div style={{ color: C.textDim, fontSize: 12, lineHeight: 1.5 }}>
              {t('onboarding.dashboard.clientOnboardingDesc')}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px" }}>
            <span style={{ color: C.textMid, fontSize: 12, fontFamily: "'DM Mono', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>
              {portalUrl}
            </span>
            <button onClick={copyLink} style={{ background: copied ? `${C.success}18` : C.accentGlow, border: `1px solid ${copied ? C.success : C.borderLight}`, borderRadius: 6, color: copied ? C.success : C.accent, padding: "5px 12px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.2s" }}>
              {copied ? `✓ ${t('common.copied')}` : t('common.copy')}
            </button>
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, padding: "5px 10px", cursor: "pointer", fontSize: 11, textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0 }}>
              Open ↗
            </a>
          </div>
        </div>
        <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
          <button onClick={() => setShowForm(true)} style={{ background: "transparent", border: "none", color: C.accentDim, fontSize: 12, cursor: "pointer", fontFamily: "'DM Mono', monospace", padding: 0, textDecoration: "underline" }}>
            {t('onboarding.dashboard.fillOnBehalf')}
          </button>
        </div>
      </div>

      {/* ── DB error banner ── */}
      {dbError && (
        <div style={{ background: `${C.danger}12`, border: `1px solid ${C.danger}40`, borderRadius: 9, padding: "12px 16px", marginBottom: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 3 }}>{t('onboarding.dashboard.couldNotLoad')}</div>
            <div style={{ color: C.textMid, fontSize: 11, fontFamily: "'DM Mono', monospace", wordBreak: "break-all" }}>{dbError}</div>
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 6 }}>
              Check that: (1) <code>setup_kyc.sql</code> has been run, (2) RLS allows authenticated SELECT, (3) your Supabase URL/key in the app are correct.
            </div>
          </div>
          <button onClick={loadSubmissions} style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}40`, borderRadius: 6, color: C.danger, padding: "5px 10px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>{t('common.retry')}</button>
        </div>
      )}

      {/* ── Real data indicator ── */}
      {realSubmissions !== null && !dbError && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.success }} />
          <span style={{ color: C.textDim, fontSize: 11 }}>
            {t('onboarding.dashboard.connected')} · {realSubmissions.length} {t('onboarding.dashboard.realSubmissions', { count: realSubmissions.length })}
            {MOCK_SUBMISSIONS.length > 0 && <span style={{ color: C.textDim }}> + {MOCK_SUBMISSIONS.length} {t('onboarding.dashboard.sampleRecords')}</span>}
          </span>
        </div>
      )}

      {/* ── Filter tabs ── */}
      <div style={{ display: "flex", gap: 7, marginBottom: 18 }}>
        {[["all", t('onboarding.dashboard.filterAll')], ["pending", t('onboarding.dashboard.filterPending')], ["approved", t('onboarding.dashboard.filterApproved')], ["rejected", t('onboarding.dashboard.filterRejected')], ["info_requested", t('onboarding.dashboard.filterInfoNeeded')]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{ padding: "5px 13px", borderRadius: 7, cursor: "pointer", fontSize: 11, background: filter === v ? C.accentGlow : "transparent", border: `1px solid ${filter === v ? C.accent : C.border}`, color: filter === v ? C.accent : C.textMid, fontFamily: "'DM Mono', monospace" }}>
            {l}{counts[v] > 0 ? <span style={{ marginLeft: 5, background: filter === v ? C.accent : C.borderLight, color: filter === v ? C.bg : C.textDim, padding: "1px 5px", borderRadius: 8, fontSize: 10 }}>{counts[v]}</span> : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: "48px", textAlign: "center", color: C.textDim, fontSize: 13 }}>{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: "48px", textAlign: "center", color: C.textDim, fontSize: 13 }}>{t('onboarding.dashboard.noSubmissions')}</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {[t('onboarding.dashboard.colName'), t('onboarding.dashboard.colType'), t('onboarding.dashboard.colEmail'), t('onboarding.dashboard.colCountry'), t('onboarding.dashboard.colRisk'), "AI", t('onboarding.dashboard.colStatus'), t('onboarding.dashboard.colSubmitted'), ""].map(h => (
                <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: C.textDim, fontSize: 10, letterSpacing: "0.1em", fontFamily: "'DM Mono', monospace" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const { score, flags } = computeRiskScore(s);
              const riskColor = score >= 70 ? C.danger : score >= 40 ? C.warn : C.success;
              const aiResult = aiAnalysis[s.id] || s.ai_analysis || dataAnalysis[s.id] || s.data_analysis;
              const isAnalyzing = aiLoading[s.id] || aiLoading[`${s.id}_data`] || aiLoading[`${s.id}_docs`] || sanctionsLoading[s.id];
              const total = dilisenseKey ? 4 : 3;
              const doneCount = [
                s.ai_analysis    || aiAnalysis[s.id],
                s.data_analysis  || dataAnalysis[s.id],
                s.docs_analysis  || docsAnalysis[s.id],
                dilisenseKey ? (s.sanctions_result || sanctionsResults[s.id]) : true,
              ].filter(Boolean).length - (dilisenseKey ? 0 : 1);
              const aiVerdict = typeof aiResult === "object" ? aiResult?.overall : null;
              const aiVerdictColor = aiVerdict === "PASS" ? C.success : aiVerdict === "FAIL" ? C.danger : aiVerdict === "REVIEW" ? C.warn : null;
              return (
                <tr key={s.id} style={{ borderBottom: `1px solid ${C.border}`, transition: "background 0.1s", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = C.surfaceAlt}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  onClick={() => selectSubmission(s)}>
                  <td style={{ padding: "12px 12px" }}>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 500 }}>{s.first_name} {s.last_name}</div>
                    {s.business_name && <div style={{ color: C.textDim, fontSize: 11 }}>{s.business_name}</div>}
                  </td>
                  <td style={{ padding: "12px 12px" }}><span style={{ color: C.textMid, fontSize: 11, textTransform: "capitalize" }}>{s.type}</span></td>
                  <td style={{ padding: "12px 12px", color: C.textDim, fontSize: 12 }}>{s.email}</td>
                  <td style={{ padding: "12px 12px", color: C.textMid, fontSize: 12 }}>{s.country || s.incorporation_country || "—"}</td>
                  <td style={{ padding: "12px 12px" }}>
                    <span style={{ color: riskColor, fontSize: 11, fontWeight: 700 }}>{score}</span>
                    {flags.length > 0 && <span style={{ color: C.textDim, fontSize: 10, marginLeft: 5 }}>⚑{flags.length}</span>}
                  </td>
                  <td style={{ padding: "12px 12px" }}>
                    {isAnalyzing ? (
                      <span style={{ color: C.accent, fontSize: 10, fontFamily: "'DM Mono',monospace" }}>⟳ {doneCount}/{total}</span>
                    ) : aiVerdictColor ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: `${aiVerdictColor}18`, color: aiVerdictColor, fontFamily: "'DM Mono',monospace" }}>{aiVerdict}</span>
                        {doneCount < total && <span style={{ color: C.textDim, fontSize: 9 }}>{doneCount}/{total}</span>}
                      </div>
                    ) : (
                      <span style={{ color: C.textDim, fontSize: 10 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "12px 12px" }}>
                    <span style={{ color: statusColor[s.status] || C.textDim, fontSize: 10, background: `${statusColor[s.status]}18`, padding: "2px 8px", borderRadius: 4 }}>
                      {statusLabel[s.status] || s.status}
                    </span>
                  </td>
                  <td style={{ padding: "12px 12px", color: C.textDim, fontSize: 11 }}>{new Date(s.submitted_at).toLocaleDateString()}</td>
                  <td style={{ padding: "12px 12px" }}>
                    <span style={{ color: C.accent, fontSize: 11 }}>{t('onboarding.dashboard.review')} →</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}